import { withClassPass } from "./payment-pricing";
import {
  TEAM_ORDER,
  TEAM_NAMES,
  compareMembers,
  memberGroup,
} from "./team-config";
import {
  Archive,
  ArrowLeft,
  BellRing,
  CalendarDays,
  CalendarPlus,
  Check,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Copy,
  Download,
  ExternalLink,
  FileVideo,
  Filter,
  Home,
  ListMusic,
  LogOut,
  MapPin,
  Megaphone,
  Menu,
  Mic,
  MessageSquare,
  Moon,
  Music2,
  Pause,
  Palette,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Scissors,
  Search,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Sun,
  Trash2,
  Upload,
  UserCircle,
  Users,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  calculateHstCents,
  googleClassUrl,
  googleEventUrl,
  HST_RATE_BPS,
  parseQuickEventText,
} from "./domain";
import {
  cacheableAudioUrl,
  clearClientMediaCaches,
  ensureClientMediaCacheOwner,
  fetchAndCacheAudio,
  getCachedWaveform,
  saveCachedWaveform,
  waveformCacheKey,
} from "./media-cache";
import {
  PaymentInstructionsEditor,
  MemberPaymentInstructions,
} from "./payment-ui";
import { paymentQuote, paymentTotalForMethod } from "./payment-pricing";
import type {
  Announcement,
  AttendanceMark,
  AttendanceStatus,
  BootstrapData,
  CastMember,
  EventRecord,
  EventRoutine,
  FeedbackItem,
  CalendarPreferences,
  MemberCapability,
  MediaAsset,
  Member,
  MerchProduct,
  PaymentCategory,
  PaymentMethod,
  PaymentRecord,
  PaymentReceipt,
  PersonalTrack,
  PracticeSection,
  Routine,
  TeamId,
  VideoPlaylist,
  WeeklyClass,
} from "./types";

type Page =
  | "home"
  | "schedule"
  | "routines"
  | "people"
  | "payments"
  | "announcements"
  | "music"
  | "merch"
  | "videos"
  | "feedback"
  | "profile";

interface AuthConfig {
  google: boolean;
  development: boolean;
}

interface DevMember {
  id: string;
  name: string;
  role: "teacher" | "student";
  teamIds: TeamId[];
}

const api = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers:
      init?.body && !(init.body instanceof FormData)
        ? { "content-type": "application/json", ...init.headers }
        : init?.headers,
  });
  const payload = await response.text();
  const value = (payload ? JSON.parse(payload) : undefined) as
    (T & { error?: string }) | undefined;
  if (!response.ok)
    throw new Error(value?.error ?? `Request failed (${response.status})`);
  return value as T;
};

const signOut = async () => {
  try {
    await api("/api/auth/logout", { method: "POST", body: "{}" });
  } finally {
    await clearClientMediaCaches();
  }
  localStorage.setItem("avante-page-v2", "home");
  location.replace("/");
};

const cx = (...values: Array<string | false | undefined>) =>
  values.filter(Boolean).join(" ");
const teamName = (id?: TeamId) => (id ? TEAM_NAMES[id] : "All teams");
const teamLabel = (teamIds: TeamId[]) =>
  teamIds.length
    ? TEAM_ORDER.filter((id) => teamIds.includes(id))
        .map(teamName)
        .join(" + ")
    : "All teams";
const formatDate = (date?: string, full = false) =>
  date
    ? new Intl.DateTimeFormat("en-CA", {
        weekday: full ? "long" : undefined,
        month: "short",
        day: "numeric",
        year: full ? "numeric" : undefined,
        timeZone: "UTC",
      }).format(new Date(`${date}T12:00:00Z`))
    : "Date TBD";
const birthdayMonths = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;
const formatBirthday = (birthday?: string) => {
  const match = birthday?.match(/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/);
  if (!match) return undefined;
  return `${birthdayMonths[Number(match[1]) - 1]} ${Number(match[2])}`;
};
const formatTimestampDate = (timestamp: string) =>
  new Intl.DateTimeFormat("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/Toronto",
  }).format(new Date(timestamp));

function BirthdaySelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  const [month = "", day = ""] = value.split("-");
  const update = (nextMonth: string, nextDay: string) =>
    onChange(
      nextMonth && nextDay
        ? `${nextMonth}-${String(Number(nextDay)).padStart(2, "0")}`
        : "",
    );
  return (
    <div className="birthday-select" role="group" aria-label="Birthday">
      <label htmlFor={`${id}-month`}>
        <span>Month</span>
        <select
          id={`${id}-month`}
          value={month}
          onChange={(event) => update(event.target.value, day || "1")}
        >
          <option value="">Not set</option>
          {birthdayMonths.map((label, index) => (
            <option value={String(index + 1).padStart(2, "0")} key={label}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor={`${id}-day`}>
        <span>Day</span>
        <select
          id={`${id}-day`}
          value={day ? String(Number(day)) : ""}
          disabled={!month}
          onChange={(event) => update(month, event.target.value)}
        >
          <option value="">—</option>
          {Array.from({ length: 31 }, (_, index) => String(index + 1)).map(
            (label) => (
              <option value={label} key={label}>
                {label}
              </option>
            ),
          )}
        </select>
      </label>
    </div>
  );
}
const isoDate = (date: Date) => date.toISOString().slice(0, 10);
const businessDate = (date = new Date()) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Toronto",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
const dateFromIso = (date?: string) =>
  new Date(`${date ?? isoDate(new Date())}T12:00:00Z`);
const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};
const startOfWeek = (date: Date) => addDays(date, -date.getUTCDay());
const startOfMonth = (date: Date) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 12));
const monthLabel = (date: Date) =>
  new Intl.DateTimeFormat("en-CA", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
const money = (amount?: number) =>
  amount == null
    ? "Amount not entered"
    : new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "CAD",
      }).format(amount);
const weekdays = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
type ThemeMode = "light" | "dark";
type ThemePalette = "studio" | "avante";
type InstallPlatform = "ios" | "android" | "desktop";
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}
interface ChecklistItem {
  id: string;
  label: string;
  detail?: string;
}
type CalendarViewMode = "list" | "day" | "week" | "month";
type TeamCalendarTeamFilter = "all" | TeamId;
declare global {
  interface Navigator {
    standalone?: boolean;
  }
}
const themeStorageKey = "avante-theme-mode-v1";
const themePaletteStorageKey = "avante-theme-palette-v1";
const installHintDismissedKey = "avante-install-hint-dismissed-v1";
const eventChecklistStoragePrefix = "avante-event-checklist-v1";
const preferredTheme = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(themeStorageKey);
    if (stored === "light" || stored === "dark") return stored;
    return "light";
  } catch {
    return "light";
  }
};
const preferredThemePalette = (): ThemePalette => {
  try {
    const stored = localStorage.getItem(themePaletteStorageKey);
    if (stored === "studio" || stored === "avante") return stored;
    return "studio";
  } catch {
    return "studio";
  }
};
const browserThemeColor = (palette: ThemePalette, mode: ThemeMode) => {
  if (palette === "avante") return mode === "dark" ? "#09182c" : "#dbeaff";
  return mode === "dark" ? "#111014" : "#f8f6f5";
};
const standaloneDisplay = () =>
  matchMedia("(display-mode: standalone)").matches ||
  Boolean(navigator.standalone);
const installPlatform = (): InstallPlatform => {
  const userAgent = navigator.userAgent;
  const touchMac =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  if (/iPad|iPhone|iPod/.test(userAgent) || touchMac) return "ios";
  if (/Android/i.test(userAgent)) return "android";
  return "desktop";
};

function BrandLockup({ context }: { context: string }) {
  return (
    <div className="brand-lockup">
      <img src="/brand/avante-wordmark.webp" alt="Avante Dance Company" />
      <span>{context}</span>
    </div>
  );
}

function Pill({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: string;
}) {
  return <span className={`pill pill-${tone}`}>{children}</span>;
}

function Avatar({ member }: { member: Member }) {
  return (
    <span className="avatar" style={{ background: member.color }}>
      {member.photoMediaId ? (
        <img src={`/api/media/${member.photoMediaId}`} alt="" loading="lazy" />
      ) : (
        member.initials
      )}
    </span>
  );
}

function Modal({
  title,
  eyebrow: _eyebrow,
  onClose,
  children,
  wide,
  className,
}: {
  title: string;
  eyebrow?: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
  className?: string;
}) {
  useEffect(() => {
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    addEventListener("keydown", close);
    return () => removeEventListener("keydown", close);
  }, [onClose]);
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        className={cx("modal", wide && "modal-wide", className)}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="modal-head">
          <div>
            <h2>{title}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close">
            <X />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function Login({ config }: { config: AuthConfig }) {
  const [members, setMembers] = useState<DevMember[]>([]);
  const [busy, setBusy] = useState("");
  const authError = new URLSearchParams(location.search).get("auth");
  useEffect(() => {
    if (config.development)
      void api<DevMember[]>("/api/auth/dev-members").then(setMembers);
  }, [config.development]);
  const enter = async (memberId: string) => {
    setBusy(memberId);
    await clearClientMediaCaches();
    await api("/api/auth/development", {
      method: "POST",
      body: JSON.stringify({ memberId }),
    });
    localStorage.setItem("avante-page-v2", "home");
    location.replace("/");
  };
  const teacher =
    members.find((member) => member.id === "member-arshan") ??
    members.find((member) => member.role === "teacher");
  const student = members.find((member) => member.role === "student");
  return (
    <main className="login-page">
      <section className="login-card">
        <BrandLockup
          context={config.development ? "Development hub" : "Private team hub"}
        />
        <h1 className="sr-only">Avante Team Hub</h1>
        <p>Avante's private home for coaches and members.</p>
        {authError && (
          <div className="auth-message">
            {authError === "not-approved"
              ? "This Google account has not been approved by an Avante coach."
              : "Google sign-in could not be completed. Please try again."}
          </div>
        )}
        {config.google && (
          <a className="google-button" href="/auth/google">
            <span>G</span> Continue with Google <ChevronRight />
          </a>
        )}
        {config.development && (
          <div className="dev-entry">
            <span>Development views</span>
            <div>
              {teacher && (
                <button
                  onClick={() => enter(teacher.id)}
                  disabled={Boolean(busy)}
                >
                  <ShieldCheck />{" "}
                  {busy === teacher.id ? "Opening…" : "Coach view"}
                </button>
              )}
              {student && (
                <button
                  onClick={() => enter(student.id)}
                  disabled={Boolean(busy)}
                >
                  <Sparkles />{" "}
                  {busy === student.id
                    ? "Opening…"
                    : `Member · ${student.name}`}
                </button>
              )}
            </div>
          </div>
        )}
        <small>Google verifies identity. Avante controls membership.</small>
      </section>
    </main>
  );
}

const teacherNav: Array<{ id: Page; label: string; icon: typeof Home }> = [
  { id: "home", label: "Today", icon: Home },
  { id: "schedule", label: "Schedule", icon: CalendarDays },
  { id: "routines", label: "Music", icon: ListMusic },
  { id: "videos", label: "Videos", icon: FileVideo },
  { id: "people", label: "Team", icon: Users },
  { id: "payments", label: "Payments", icon: CircleDollarSign },
  { id: "merch", label: "Merch", icon: Sparkles },
  { id: "feedback", label: "Feedback", icon: BellRing },
  { id: "announcements", label: "Announcements", icon: Megaphone },
  { id: "profile", label: "My profile", icon: UserCircle },
];
const studentNav: Array<{ id: Page; label: string; icon: typeof Home }> = [
  { id: "home", label: "For you", icon: Home },
  { id: "schedule", label: "Schedule", icon: CalendarDays },
  { id: "routines", label: "Music", icon: ListMusic },
  { id: "music", label: "My music", icon: Music2 },
  { id: "videos", label: "Videos", icon: FileVideo },
  { id: "people", label: "Team", icon: Users },
  { id: "payments", label: "Payments", icon: CircleDollarSign },
  { id: "merch", label: "Merch", icon: Sparkles },
  { id: "feedback", label: "Feedback", icon: BellRing },
  { id: "profile", label: "My profile", icon: UserCircle },
];

function Sidebar({
  data,
  page,
  setPage,
  open,
  close,
}: {
  data: BootstrapData;
  page: Page;
  setPage: (page: Page) => void;
  open: boolean;
  close: () => void;
}) {
  const nav = data.user.role === "teacher" ? teacherNav : studentNav;
  return (
    <aside className={cx("sidebar", open && "open")}>
      <button
        className="mobile-close icon-button"
        onClick={close}
        aria-label="Close navigation"
      >
        <X />
      </button>
      <BrandLockup
        context={
          data.user.role === "teacher"
            ? "Coach studio"
            : `${teamLabel(data.user.teamIds)} team hub`
        }
      />
      <nav>
        <span>
          {data.user.role === "teacher" ? "Run the team" : "Your season"}
        </span>
        {nav.map(({ id, label, icon: Icon }) => (
          <button
            className={page === id ? "active" : ""}
            key={id}
            onClick={() => {
              setPage(id);
              close();
            }}
          >
            <Icon /> {label}
          </button>
        ))}
      </nav>
      <div className="sidebar-profile">
        <span className="avatar">
          {data.user.name.slice(0, 2).toUpperCase()}
        </span>
        <span>
          <b>{data.user.name}</b>
          <small>
            {data.user.role === "teacher"
              ? "Coach"
              : teamLabel(data.user.teamIds)}
          </small>
        </span>
        <button onClick={() => void signOut()} aria-label="Sign out">
          <LogOut />
        </button>
      </div>
    </aside>
  );
}

function PageHeader({
  eyebrow: _eyebrow,
  title,
  copy,
  action,
}: {
  eyebrow?: string;
  title: string;
  copy: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        <p>{copy}</p>
      </div>
      {action && <div className="page-actions">{action}</div>}
    </header>
  );
}

function ThemePicker({
  mode,
  setMode,
  palette,
  setPalette,
}: {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  palette: ThemePalette;
  setPalette: (palette: ThemePalette) => void;
}) {
  const [open, setOpen] = useState(false);
  const picker = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!picker.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);
  return (
    <div className="theme-picker" ref={picker}>
      <button
        className="theme-picker-trigger icon-button"
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label="Choose app theme"
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Choose app theme"
      >
        <Palette />
      </button>
      {open && (
        <section
          className="theme-picker-popover"
          role="dialog"
          aria-label="Theme settings"
        >
          <header>
            <div>
              <b>Theme</b>
              <small>Choose a palette and appearance.</small>
            </div>
            <button
              type="button"
              className="theme-picker-close"
              onClick={() => setOpen(false)}
              aria-label="Close theme settings"
            >
              <X />
            </button>
          </header>
          <div
            className="theme-palette-options"
            role="radiogroup"
            aria-label="Color palette"
          >
            <button
              type="button"
              role="radio"
              aria-checked={palette === "studio"}
              className={palette === "studio" ? "selected" : ""}
              onClick={() => setPalette("studio")}
            >
              <span
                className="theme-swatches theme-swatches-studio"
                aria-hidden="true"
              >
                <i />
                <i />
                <i />
              </span>
              <span>
                <b>Studio</b>
                <small>Original rose and violet</small>
              </span>
              {palette === "studio" && <Check />}
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={palette === "avante"}
              className={palette === "avante" ? "selected" : ""}
              onClick={() => setPalette("avante")}
            >
              <span
                className="theme-swatches theme-swatches-avante"
                aria-hidden="true"
              >
                <i />
                <i />
                <i />
              </span>
              <span>
                <b>Avante Dance</b>
                <small>Website blue, red, and yellow</small>
              </span>
              {palette === "avante" && <Check />}
            </button>
          </div>
          <div
            className="theme-mode-options"
            role="radiogroup"
            aria-label="Appearance"
          >
            <button
              type="button"
              role="radio"
              aria-checked={mode === "light"}
              className={mode === "light" ? "selected" : ""}
              onClick={() => setMode("light")}
            >
              <Sun />
              Light
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={mode === "dark"}
              className={mode === "dark" ? "selected" : ""}
              onClick={() => setMode("dark")}
            >
              <Moon />
              Dark
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function InstallHint() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent>();
  const [standalone, setStandalone] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(installHintDismissedKey) === "true";
    } catch {
      return false;
    }
  });
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState<InstallPlatform>("desktop");

  useEffect(() => {
    setStandalone(standaloneDisplay());
    setPlatform(installPlatform());
    const media = matchMedia("(display-mode: standalone)");
    const legacyMedia = media as MediaQueryList & {
      addListener?: (listener: () => void) => void;
      removeListener?: (listener: () => void) => void;
    };
    const onDisplayModeChange = () => setStandalone(standaloneDisplay());
    if (typeof media.addEventListener === "function")
      media.addEventListener("change", onDisplayModeChange);
    else legacyMedia.addListener?.(onDisplayModeChange);
    return () => {
      if (typeof media.removeEventListener === "function")
        media.removeEventListener("change", onDisplayModeChange);
      else legacyMedia.removeListener?.(onDisplayModeChange);
    };
  }, []);

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    const onAppInstalled = () => {
      setStandalone(true);
      setDeferredPrompt(undefined);
    };
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  if (standalone || dismissed || (platform === "desktop" && !deferredPrompt))
    return null;

  const copy =
    platform === "ios"
      ? "In Safari, tap Share, then Add to Home Screen."
      : platform === "android"
        ? deferredPrompt
          ? "Install Avante so it opens full screen from your home screen."
          : "Open the browser menu, then tap Add to Home screen or Install app."
        : "Install Avante so it opens in its own app window.";

  const dismiss = () => {
    setDismissed(true);
    setOpen(false);
    try {
      localStorage.setItem(installHintDismissedKey, "true");
    } catch {
      // Ignore storage failures; the hint can reappear next session.
    }
  };

  return (
    <div className="install-hint">
      <button
        className="install-hint-trigger icon-button"
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label="Install Avante app"
        title="Install Avante app"
      >
        <Smartphone />
      </button>
      {open && (
        <div className="install-hint-popover" role="dialog">
          <div>
            <b>Add Avante to your phone</b>
            <p>{copy}</p>
          </div>
          <div className="install-hint-actions">
            {deferredPrompt && (
              <button
                className="primary-button"
                type="button"
                onClick={async () => {
                  await deferredPrompt.prompt();
                  await deferredPrompt.userChoice.catch(() => undefined);
                  setDeferredPrompt(undefined);
                  dismiss();
                }}
              >
                <Download /> Install
              </button>
            )}
            <button
              className="secondary-button"
              type="button"
              onClick={dismiss}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Empty({
  title,
  copy,
  action,
}: {
  title: string;
  copy: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="empty">
      <Sparkles />
      <b>{title}</b>
      <span>{copy}</span>
      {action && <div className="empty-action">{action}</div>}
    </div>
  );
}

function MetricCard({
  label,
  value,
  detail,
  tone = "neutral",
}: {
  label: string;
  value: string | number;
  detail?: string;
  tone?: "neutral" | "primary" | "sage" | "gold";
}) {
  return (
    <article className={`metric-card metric-card-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </article>
  );
}

function SpeechInputAction({
  onTranscript,
}: {
  onTranscript: (transcript: string) => void;
}) {
  const Speech = (
    window as unknown as {
      webkitSpeechRecognition?: new () => {
        lang: string;
        start(): void;
        stop(): void;
        onstart?: () => void;
        onend?: () => void;
        onerror?: () => void;
        onresult?: (event: {
          results: ArrayLike<{ 0: { transcript: string } }>;
        }) => void;
      };
    }
  ).webkitSpeechRecognition;
  const statusId = useId();
  const [listening, setListening] = useState(false);
  const [message, setMessage] = useState("");
  return (
    <span className="speech-action">
      <button
        className="secondary-button"
        type="button"
        disabled={!Speech || listening}
        onClick={() => {
          if (!Speech) return;
          setMessage("");
          const recognition = new Speech();
          recognition.lang = "en-CA";
          recognition.onstart = () => setListening(true);
          recognition.onend = () => setListening(false);
          recognition.onerror = () => {
            setListening(false);
            setMessage(
              "Voice input could not start. Type the details instead.",
            );
          };
          recognition.onresult = (event) => {
            onTranscript(event.results[0]?.[0].transcript ?? "");
            setMessage("Voice input added. Review the text before saving.");
          };
          recognition.start();
        }}
        aria-describedby={message ? statusId : undefined}
        title={Speech ? "Dictate text" : "Voice input is not supported here"}
      >
        <Mic /> {listening ? "Listening…" : "Voice to text"}
      </button>
      {!Speech && <small>Voice input is unavailable in this browser.</small>}
      {message && <small id={statusId}>{message}</small>}
    </span>
  );
}

function ConfirmDialog({
  error,
  title,
  copy,
  confirmLabel,
  destructive = false,
  busy,
  onClose,
  onConfirm,
}: {
  error?: string;
  title: string;
  copy: string;
  confirmLabel: string;
  destructive?: boolean;
  busy?: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
}) {
  return (
    <Modal title={title} onClose={() => !busy && onClose()}>
      <div className="confirm-dialog">
        <p>{copy}</p>
        {error && (
          <p role="alert" className="auth-message">
            {error}
          </p>
        )}
        <div className="modal-actions">
          <button
            className="secondary-button"
            type="button"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className={destructive ? "destructive-button" : "primary-button"}
            type="button"
            disabled={busy}
            onClick={() => void onConfirm()}
          >
            {destructive ? <Trash2 /> : <Check />}{" "}
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function renderGroupedMembers(
  members: Member[],
  render: (member: Member) => React.ReactNode,
) {
  const groups = new Map<string, Member[]>();
  for (const member of [...members].sort(compareMembers)) {
    const group = memberGroup(member);
    groups.set(group, [...(groups.get(group) ?? []), member]);
  }
  return [...groups].map(([name, people]) => (
    <React.Fragment key={name}>
      <h3 className="member-group-heading">{name}</h3>
      {people.map(render)}
    </React.Fragment>
  ));
}

function TeamBadge({ teamId }: { teamId?: TeamId }) {
  return (
    <Pill
      tone={
        teamId === "fuerza"
          ? "rose"
          : teamId === "impulso"
            ? "violet"
            : teamId === "maxima"
              ? "sage"
              : "neutral"
      }
    >
      {teamName(teamId)}
    </Pill>
  );
}

function TeamBadges({ teamIds }: { teamIds: TeamId[] }) {
  if (teamIds.length === 0) return <Pill tone="neutral">All teams</Pill>;
  return (
    <span className="team-badges" aria-label={teamLabel(teamIds)}>
      {teamIds.map((teamId) => (
        <TeamBadge teamId={teamId} key={teamId} />
      ))}
    </span>
  );
}

function CalendarActionButton({
  href,
  label,
}: {
  href?: string;
  label: string;
}) {
  if (!href) return null;
  return (
    <a
      className="icon-button calendar-card-action"
      href={href}
      target="_blank"
      rel="noreferrer"
      title={label}
      aria-label={label}
    >
      <CalendarPlus />
    </a>
  );
}

const checklistStorageKey = (memberId: string, eventId: string) =>
  `${eventChecklistStoragePrefix}:${memberId}:${eventId}`;

const routineDisplayName = (entry: EventRoutine, routines: Routine[]) =>
  routines.find((routine) => routine.id === entry.routineId)?.name ??
  entry.routineName ??
  "Routine";

const checklistId = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

function performanceChecklistItems(
  entries: EventRoutine[],
  routines: Routine[],
): ChecklistItem[] {
  const routineItems = entries.map((entry) => {
    const name = routineDisplayName(entry, routines);
    return {
      id: `costume-${entry.routineId}-${checklistId(entry.id)}`,
      label: `Costume for ${name}`,
      detail: entry.label || "Costume, accessories, and any routine notes",
    };
  });

  return [
    {
      id: "dance-shoes",
      label: "Dance shoes",
      detail: "Pack the pair you perform in",
    },
    {
      id: "water",
      label: "Water bottle",
      detail: "Enough for call time through show end",
    },
    {
      id: "phone",
      label: "Charged phone",
      detail: "Keep calendar, music, and team messages handy",
    },
    ...routineItems,
  ];
}

function MemberPerformanceChecklist({
  event,
  entries,
  routines,
  memberId,
}: {
  event: EventRecord;
  entries: EventRoutine[];
  routines: Routine[];
  memberId: string;
}) {
  const items = useMemo(
    () => performanceChecklistItems(entries, routines),
    [entries, routines],
  );
  const storageKey = checklistStorageKey(memberId, event.id);
  const [checked, setChecked] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) ?? "{}") as Record<
        string,
        boolean
      >;
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      const next = Object.fromEntries(
        items.filter((item) => checked[item.id]).map((item) => [item.id, true]),
      );
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // Packing progress is personal convenience state; storage failure is safe.
    }
  }, [checked, items, storageKey]);

  const packed = items.filter((item) => checked[item.id]).length;
  const progress = items.length ? (packed / items.length) * 100 : 0;

  return (
    <section className="student-checklist" aria-label="Performance pack list">
      <header>
        <span>
          <Check /> Pack list
        </span>
        <b>
          {packed}/{items.length}
        </b>
      </header>
      <div className="student-checklist-progress" aria-hidden="true">
        <span style={{ width: `${progress}%` }} />
      </div>
      <div className="student-checklist-items">
        {items.map((item) => (
          <button
            className={cx(
              "student-checklist-item",
              checked[item.id] && "checked",
            )}
            type="button"
            aria-pressed={Boolean(checked[item.id])}
            onClick={() =>
              setChecked((current) => ({
                ...current,
                [item.id]: !current[item.id],
              }))
            }
            key={item.id}
          >
            <span>
              <Check />
            </span>
            <span>
              <b>{item.label}</b>
              {item.detail && <small>{item.detail}</small>}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

function EventCard({
  event,
  routines,
  members,
  onEdit,
}: {
  event: EventRecord;
  routines: Routine[];
  members: Member[];
  onEdit?: () => void;
}) {
  const performers = new Set(
    event.routines.flatMap((entry) =>
      entry.performers.map((person) => person.memberId),
    ),
  );
  return (
    <article
      className={cx("event-card", event.type === "practice" && "is-practice")}
    >
      <div className="date-tile">
        <span>{event.date ? formatDate(event.date).split(" ")[0] : "TBD"}</span>
        <b>
          {event.date ? new Date(`${event.date}T12:00:00Z`).getUTCDate() : "—"}
        </b>
      </div>
      <div className="event-main">
        <div>
          <Pill tone={event.type === "competition" ? "gold" : "rose"}>
            {event.type}
          </Pill>
          <Pill tone={event.status === "published" ? "sage" : "neutral"}>
            {event.status}
          </Pill>
        </div>
        <h3>{event.title}</h3>
        <p>
          <Clock3 />{" "}
          {event.type === "practice"
            ? `${event.showTime || event.callTime || "TBD"} – ${event.endTime || "TBD"}`
            : `Call ${event.callTime || "TBD"} · Show ${event.showTime || "TBD"}`}
        </p>
        <p>
          <MapPin /> {event.venue || "Venue TBD"}
        </p>
        {event.type === "practice" && (
          <>
            <TeamBadges teamIds={event.teamIds ?? []} />
            <p>{event.address}</p>
          </>
        )}
        <CoachNote
          note={event.notes}
          title={event.type === "practice" ? "Practice notes" : "Event notes"}
        />
        <div className="event-routines">
          {event.routines.map((entry) => {
            const routine = routines.find(
              (item) => item.id === entry.routineId,
            );
            return (
              <span key={entry.id}>
                <b>{routine?.name ?? entry.routineName ?? "Routine"}</b>
                {entry.label && <small>{entry.label}</small>}
              </span>
            );
          })}
        </div>
        <div
          className="avatar-stack"
          title={`${performers.size} selected performers`}
        >
          {[...performers].slice(0, 7).map((id) => {
            const member = members.find((person) => person.id === id);
            return member ? <Avatar member={member} key={id} /> : null;
          })}
          <small>{performers.size} selected</small>
        </div>
        <div className="event-roster">
          <div className="event-roster-head">
            <b>Run of show</b>
            <small>{performers.size} performers</small>
          </div>
          {event.routines.map((entry, index) => {
            const routine = routines.find(
              (item) => item.id === entry.routineId,
            );
            const routineTags = routine?.tags ?? entry.routineTags ?? [];
            return (
              <section className="event-roster-routine" key={entry.id}>
                <header>
                  <span>{index + 1}</span>
                  <div>
                    <span className="event-roster-title">
                      <b>{routine?.name ?? entry.routineName ?? "Routine"}</b>
                      <TeamBadge
                        teamId={routine?.teamId ?? entry.routineTeamId}
                      />
                    </span>
                    <small>
                      {entry.label || routineTags.join(" / ") || "Cast list"}
                    </small>
                  </div>
                </header>
                <div
                  className="event-roster-cast"
                  aria-label={`Cast for ${routine?.name ?? entry.routineName ?? "routine"}`}
                >
                  {entry.performers.length ? (
                    entry.performers.map((performer) => {
                      const member = members.find(
                        (person) => person.id === performer.memberId,
                      );
                      const slot = performer.position || "Slot TBD";
                      return (
                        <span
                          className="event-performer-chip"
                          key={performer.memberId}
                          title={`${member?.name ?? "Performer"} · ${slot}`}
                        >
                          <small>{slot}</small>
                          {member ? <Avatar member={member} /> : null}
                          <b>{member?.name ?? "Performer"}</b>
                        </span>
                      );
                    })
                  ) : (
                    <span className="event-roster-empty">Cast TBD</span>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>
      <div className="event-card-actions">
        <CalendarActionButton
          href={googleEventUrl(event, routines)}
          label={`Add ${event.title} to Google Calendar`}
        />
        {onEdit && (
          <button
            className="icon-button"
            onClick={onEdit}
            aria-label={`Edit ${event.title}`}
          >
            <Pencil />
          </button>
        )}
      </div>
    </article>
  );
}

const eventRoutineTeam = (entry: EventRoutine, routines: Routine[]) =>
  entry.routineTeamId ??
  routines.find((routine) => routine.id === entry.routineId)?.teamId;

const teamCalendarEventRoutines = (
  event: EventRecord,
  routines: Routine[],
  teamFilter: TeamCalendarTeamFilter,
  selectedMemberIds: Set<string>,
) =>
  event.routines.filter((entry) => {
    const matchesTeam =
      teamFilter === "all" || eventRoutineTeam(entry, routines) === teamFilter;
    const matchesPeople =
      selectedMemberIds.size === 0 ||
      entry.performers.some((performer) =>
        selectedMemberIds.has(performer.memberId),
      );
    return matchesTeam && matchesPeople;
  });

const eventPerformerIds = (event: EventRecord) =>
  new Set(
    event.routines.flatMap((entry) =>
      entry.performers.map((performer) => performer.memberId),
    ),
  );

const memberPerformsInRoutines = (routines: EventRoutine[], memberId: string) =>
  routines.some((entry) =>
    entry.performers.some((performer) => performer.memberId === memberId),
  );

const memberPerformsInEvent = (event: EventRecord, memberId: string) =>
  event.type !== "practice" &&
  memberPerformsInRoutines(event.routines, memberId);

const eventMatchesFilters = (
  event: EventRecord,
  routines: Routine[],
  teamFilter: TeamCalendarTeamFilter,
  selectedMemberIds: Set<string>,
) => {
  if (event.type === "practice" || event.routines.length === 0)
    return (
      (teamFilter === "all" || event.teamIds?.includes(teamFilter)) &&
      selectedMemberIds.size === 0
    );
  return (
    teamCalendarEventRoutines(event, routines, teamFilter, selectedMemberIds)
      .length > 0
  );
};

function CoachNote({
  note,
  title = "Note from your Coach",
}: {
  note?: string;
  title?: string;
}) {
  if (!note?.trim()) return null;
  return (
    <section className="coach-note" aria-label={title}>
      <div className="coach-note-heading">
        <MessageSquare aria-hidden="true" />
        <b>{title}</b>
      </div>
      <div className="coach-note-body">{note}</div>
    </section>
  );
}

function TeamCalendarEventCard({
  event,
  routines,
  members,
  viewerMemberId,
  teamFilter,
  selectedMemberIds,
  onEdit,
}: {
  event: EventRecord;
  routines: Routine[];
  members: Member[];
  viewerMemberId: string;
  teamFilter: TeamCalendarTeamFilter;
  selectedMemberIds: Set<string>;
  onEdit?: () => void;
}) {
  const entries = teamCalendarEventRoutines(
    event,
    routines,
    teamFilter,
    selectedMemberIds,
  );
  const performerIds = new Set(
    entries.flatMap((entry) =>
      entry.performers.map((performer) => performer.memberId),
    ),
  );
  const viewerPerforms =
    event.type !== "practice" &&
    memberPerformsInRoutines(entries, viewerMemberId);

  return (
    <article
      className={cx(
        "team-event-card",
        event.type === "practice" && "is-practice",
      )}
    >
      <div className="date-tile">
        <span>{event.date ? formatDate(event.date).split(" ")[0] : "TBD"}</span>
        <b>
          {event.date ? new Date(`${event.date}T12:00:00Z`).getUTCDate() : "-"}
        </b>
      </div>
      <div className="team-event-main">
        <header>
          <div>
            <Pill tone={event.type === "competition" ? "gold" : "rose"}>
              {event.type}
            </Pill>
            <Pill tone={event.status === "published" ? "sage" : "neutral"}>
              {event.status}
            </Pill>
            {viewerPerforms && (
              <Pill tone="personal">
                <Sparkles /> You're performing
              </Pill>
            )}
          </div>
          <CalendarActionButton
            href={googleEventUrl(event, routines)}
            label={`Add ${event.title} to Google Calendar`}
          />
          {onEdit && (
            <button
              className="icon-button"
              type="button"
              onClick={onEdit}
              aria-label={`Edit ${event.title}`}
            >
              <Pencil />
            </button>
          )}
        </header>
        <h3>{event.title}</h3>
        <p>
          <Clock3 />{" "}
          {event.type === "practice"
            ? `${event.showTime || event.callTime || "TBD"} – ${event.endTime || "TBD"}`
            : `Call ${event.callTime || "TBD"} · Show ${event.showTime || "TBD"}`}
        </p>
        <p>
          <MapPin /> {event.venue || "Venue TBD"}
        </p>
        {event.type === "practice" && (
          <>
            <TeamBadges teamIds={event.teamIds ?? []} />
            <p>{event.address}</p>
          </>
        )}
        <CoachNote
          note={event.notes}
          title={event.type === "practice" ? "Practice notes" : "Event notes"}
        />
        <div className="avatar-stack" title={`${performerIds.size} performers`}>
          {[...performerIds].slice(0, 6).map((id) => {
            const member = members.find((person) => person.id === id);
            return member ? <Avatar member={member} key={id} /> : null;
          })}
          <small>{performerIds.size} shown</small>
        </div>
        <div className="event-roster team-event-roster">
          {entries.length ? (
            entries.map((entry, index) => {
              const routine = routines.find(
                (item) => item.id === entry.routineId,
              );
              const routineTags = routine?.tags ?? entry.routineTags ?? [];
              return (
                <section className="event-roster-routine" key={entry.id}>
                  <header>
                    <span>{index + 1}</span>
                    <div>
                      <span className="event-roster-title">
                        <b>{routineDisplayName(entry, routines)}</b>
                        <TeamBadge
                          teamId={routine?.teamId ?? entry.routineTeamId}
                        />
                      </span>
                      <small>
                        {entry.label || routineTags.join(" / ") || "Cast list"}
                      </small>
                    </div>
                  </header>
                  <div
                    className="event-roster-cast"
                    aria-label={`Cast for ${routineDisplayName(entry, routines)}`}
                  >
                    {entry.performers.length ? (
                      entry.performers.map((performer) => {
                        const member = members.find(
                          (person) => person.id === performer.memberId,
                        );
                        const slot = performer.position || "Slot TBD";
                        const isCurrentMember =
                          performer.memberId === viewerMemberId;
                        return (
                          <span
                            className={cx(
                              "event-performer-chip",
                              selectedMemberIds.has(performer.memberId) &&
                                "highlighted",
                              isCurrentMember && "is-current-member",
                            )}
                            key={performer.memberId}
                            title={`${member?.name ?? "Performer"} · ${slot}${isCurrentMember ? " · You" : ""}`}
                            aria-label={`${member?.name ?? "Performer"}, ${slot}${isCurrentMember ? ", you" : ""}`}
                          >
                            <small>{slot}</small>
                            {member ? <Avatar member={member} /> : null}
                            <b>{member?.name ?? "Performer"}</b>
                            {isCurrentMember && (
                              <span
                                className="event-performer-self-mark"
                                aria-hidden="true"
                              >
                                <Sparkles />
                              </span>
                            )}
                          </span>
                        );
                      })
                    ) : (
                      <span className="event-roster-empty">Cast TBD</span>
                    )}
                  </div>
                </section>
              );
            })
          ) : (
            <span className="event-roster-empty">No matching cast</span>
          )}
        </div>
      </div>
    </article>
  );
}

function PracticeCalendarCard({
  practice,
  date,
  onEdit,
}: {
  practice: WeeklyClass;
  date?: Date;
  onEdit?: () => void;
}) {
  return (
    <article className="team-event-card practice-calendar-card">
      <div className="date-tile">
        <span>{date ? formatDate(isoDate(date)).split(" ")[0] : "Every"}</span>
        <b>
          {date
            ? date.getUTCDate()
            : (weekdays[practice.weekday] ?? "Day").slice(0, 3)}
        </b>
      </div>
      <div className="team-event-main">
        <header>
          <div>
            <Pill tone="sage">Weekly practice</Pill>
            <TeamBadge teamId={practice.teamId} />
          </div>
          <CalendarActionButton
            href={googleClassUrl(practice)}
            label={`Add ${teamName(practice.teamId)} practice to Google Calendar`}
          />
          {onEdit && (
            <button
              className="icon-button"
              type="button"
              onClick={onEdit}
              aria-label={`Edit ${teamName(practice.teamId)} weekly practice`}
            >
              <Pencil />
            </button>
          )}
        </header>
        <h3>{teamName(practice.teamId)} Weekly Practice</h3>
        <p>
          <Clock3 /> {weekdays[practice.weekday]} · {practice.startTime}–
          {practice.endTime}
        </p>
        <p>
          <MapPin /> {practice.venue || "Venue TBD"}
        </p>
      </div>
    </article>
  );
}

function TeamCalendarPage({
  data,
  embedded = false,
  practices = data.classes,
  includeUnpublished = false,
  onEditEvent,
  onEditPractice,
  scheduleQuery,
  onScheduleQueryChange,
}: {
  data: BootstrapData;
  embedded?: boolean;
  practices?: WeeklyClass[];
  includeUnpublished?: boolean;
  onEditEvent?: (event: EventRecord) => void;
  onEditPractice?: (practice: WeeklyClass) => void;
  scheduleQuery?: string;
  onScheduleQueryChange?: (query: string) => void;
}) {
  const calendarEvents = [
    ...(data.teamEvents.length ? data.teamEvents : data.events),
  ]
    .filter((event) => includeUnpublished || event.status === "published")
    .sort((a, b) => (a.date ?? "9999").localeCompare(b.date ?? "9999"));
  const firstDate =
    calendarEvents.find((event) => event.date)?.date ?? isoDate(new Date());
  const [view, setView] = useState<CalendarViewMode>("list");
  const [anchor, setAnchor] = useState(() => dateFromIso(firstDate));
  const [teamFilter, setTeamFilter] = useState<TeamCalendarTeamFilter>("all");
  const [personQuery, setPersonQuery] = useState("");
  const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
  const [myPerformancesOnly, setMyPerformancesOnly] = useState(
    () => data.user.role === "student",
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const selectedPeopleSet = useMemo(
    () => new Set(selectedPeople),
    [selectedPeople],
  );
  const performerIds = useMemo(() => {
    const ids = new Set<string>();
    for (const event of calendarEvents)
      for (const id of eventPerformerIds(event)) ids.add(id);
    return ids;
  }, [calendarEvents]);
  const people = useMemo(
    () =>
      data.members
        .filter(
          (member) =>
            member.status === "approved" &&
            performerIds.has(member.id) &&
            member.name.toLowerCase().includes(personQuery.toLowerCase()),
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [data.members, performerIds, personQuery],
  );
  const filteredEvents = useMemo(
    () =>
      calendarEvents.filter(
        (event) =>
          (!myPerformancesOnly ||
            event.type === "practice" ||
            memberPerformsInEvent(event, data.user.id)) &&
          eventMatchesFilters(
            event,
            data.routines,
            teamFilter,
            selectedPeopleSet,
          ),
      ),
    [
      calendarEvents,
      data.routines,
      data.user.id,
      myPerformancesOnly,
      teamFilter,
      selectedPeopleSet,
    ],
  );
  const filteredPractices = useMemo(
    () =>
      practices.filter(
        (practice) =>
          practice.active &&
          !myPerformancesOnly &&
          (teamFilter === "all" || practice.teamId === teamFilter) &&
          selectedPeopleSet.size === 0,
      ),
    [myPerformancesOnly, practices, teamFilter, selectedPeopleSet],
  );
  const visibleEvents = (date: Date) =>
    filteredEvents.filter((event) => event.date === isoDate(date));
  const visiblePractices = (date: Date) =>
    filteredPractices.filter(
      (practice) => practice.weekday === date.getUTCDay(),
    );
  const dayEvents = visibleEvents(anchor);
  const weekStart = startOfWeek(anchor);
  const weekDays = Array.from({ length: 7 }, (_, index) =>
    addDays(weekStart, index),
  );
  const weekEnd = addDays(weekStart, 6);
  const monthStartDate = startOfMonth(anchor);
  const monthGridStart = startOfWeek(monthStartDate);
  const monthDays = Array.from({ length: 42 }, (_, index) =>
    addDays(monthGridStart, index),
  );
  const rangeLabel =
    view === "day"
      ? formatDate(isoDate(anchor), true)
      : view === "week"
        ? `${formatDate(isoDate(weekStart))} - ${formatDate(isoDate(weekEnd))}`
        : view === "month"
          ? monthLabel(anchor)
          : "All scheduled items";
  const move = (direction: number) => {
    if (view === "day") setAnchor((current) => addDays(current, direction));
    else if (view === "week")
      setAnchor((current) => addDays(current, direction * 7));
    else
      setAnchor(
        (current) =>
          new Date(
            Date.UTC(
              current.getUTCFullYear(),
              current.getUTCMonth() + direction,
              1,
              12,
            ),
          ),
      );
  };
  const togglePerson = (memberId: string) =>
    setSelectedPeople((current) =>
      current.includes(memberId)
        ? current.filter((id) => id !== memberId)
        : [...current, memberId],
    );
  const eventCount =
    view === "list"
      ? filteredEvents.length + filteredPractices.length
      : view === "day"
        ? dayEvents.length + visiblePractices(anchor).length
        : view === "week"
          ? weekDays.flatMap(visibleEvents).length +
            weekDays.flatMap(visiblePractices).length
          : monthDays
              .filter((date) => date.getUTCMonth() === anchor.getUTCMonth())
              .flatMap((date) => [
                ...visibleEvents(date),
                ...visiblePractices(date),
              ]).length;
  const hasActiveFilters =
    myPerformancesOnly ||
    teamFilter !== "all" ||
    selectedPeople.length > 0 ||
    Boolean(personQuery.trim());

  return (
    <>
      {!embedded && (
        <PageHeader
          title="Schedule"
          copy="See every visible practice, performance, competition, and team event."
        />
      )}
      <section className="team-calendar-shell">
        <header className="team-calendar-toolbar">
          <div className="segmented" aria-label="Calendar view">
            {(
              [
                ["list", "All"],
                ["day", "Day"],
                ["week", "Week"],
                ["month", "Month"],
              ] as const
            ).map(([mode, label]) => (
              <button
                className={view === mode ? "active" : ""}
                onClick={() => setView(mode)}
                key={mode}
              >
                {label}
              </button>
            ))}
          </div>
          {view !== "list" && (
            <div className="team-calendar-datebar">
              <button
                className="icon-button"
                onClick={() => move(-1)}
                aria-label="Previous range"
              >
                <ChevronLeft />
              </button>
              <button
                className="secondary-button"
                onClick={() => setAnchor(dateFromIso(isoDate(new Date())))}
              >
                Today
              </button>
              <button
                className="icon-button"
                onClick={() => move(1)}
                aria-label="Next range"
              >
                <ChevronRight />
              </button>
            </div>
          )}
          <button
            className="secondary-button calendar-my-performances"
            type="button"
            aria-pressed={myPerformancesOnly}
            onClick={() => setMyPerformancesOnly((current) => !current)}
          >
            <UserCircle /> My performances
          </button>
          <button
            className={cx(
              "secondary-button calendar-filter-button",
              hasActiveFilters && "active",
            )}
            onClick={() => setFiltersOpen((current) => !current)}
            aria-expanded={filtersOpen}
          >
            <Filter /> Filters
            {(hasActiveFilters || scheduleQuery) && (
              <span className="filter-dot" />
            )}
          </button>
        </header>
        {filtersOpen && (
          <section className="team-calendar-filters">
            {onScheduleQueryChange && (
              <label className="people-search team-calendar-search-wide">
                <Search />
                <span className="sr-only">Search schedule</span>
                <input
                  type="search"
                  placeholder="Search title, venue, Coach, or team"
                  value={scheduleQuery ?? ""}
                  onChange={(event) =>
                    onScheduleQueryChange(event.target.value)
                  }
                />
              </label>
            )}
            <div className="team-calendar-team-filter" aria-label="Team filter">
              {(["all", "maxima", "fuerza", "impulso"] as const).map((team) => (
                <button
                  className={teamFilter === team ? "active" : ""}
                  onClick={() => setTeamFilter(team)}
                  key={team}
                >
                  {team === "all" ? "All teams" : teamName(team)}
                </button>
              ))}
            </div>
            <label className="people-search team-calendar-search">
              <Search />
              <span className="sr-only">Filter people</span>
              <input
                type="search"
                placeholder="Filter people"
                value={personQuery}
                onChange={(event) => setPersonQuery(event.target.value)}
              />
            </label>
            <div className="team-calendar-people" aria-label="People filter">
              {renderGroupedMembers(people, (member) => (
                <button
                  className={selectedPeople.includes(member.id) ? "active" : ""}
                  onClick={() => togglePerson(member.id)}
                  key={member.id}
                >
                  <Avatar member={member} />
                  <span>{member.name}</span>
                </button>
              ))}
            </div>
            {hasActiveFilters && (
              <button
                className="text-button team-calendar-clear"
                onClick={() => {
                  setTeamFilter("all");
                  setSelectedPeople([]);
                  setPersonQuery("");
                  setMyPerformancesOnly(false);
                }}
              >
                Clear filters
              </button>
            )}
          </section>
        )}
        <div className="team-calendar-summary">
          <span>{rangeLabel}</span>
          <b>
            {eventCount} {eventCount === 1 ? "event" : "events"}
          </b>
        </div>
        {view === "list" && (
          <div className="team-calendar-list">
            {filteredPractices.map((practice) => (
              <PracticeCalendarCard
                practice={practice}
                onEdit={
                  onEditPractice ? () => onEditPractice(practice) : undefined
                }
                key={practice.id}
              />
            ))}
            {filteredEvents.map((event) => (
              <TeamCalendarEventCard
                event={event}
                routines={data.routines}
                members={data.members}
                viewerMemberId={data.user.id}
                teamFilter={teamFilter}
                selectedMemberIds={selectedPeopleSet}
                onEdit={onEditEvent ? () => onEditEvent(event) : undefined}
                key={event.id}
              />
            ))}
            {!filteredPractices.length && !filteredEvents.length && (
              <Empty
                title="No matching schedule items"
                copy="Clear a filter to widen the schedule."
              />
            )}
          </div>
        )}
        {view === "day" && (
          <div className="team-calendar-list">
            {visiblePractices(anchor).map((practice) => (
              <PracticeCalendarCard
                practice={practice}
                date={anchor}
                onEdit={
                  onEditPractice ? () => onEditPractice(practice) : undefined
                }
                key={`${practice.id}-${isoDate(anchor)}`}
              />
            ))}
            {dayEvents.map((event) => (
              <TeamCalendarEventCard
                event={event}
                routines={data.routines}
                members={data.members}
                viewerMemberId={data.user.id}
                teamFilter={teamFilter}
                selectedMemberIds={selectedPeopleSet}
                onEdit={onEditEvent ? () => onEditEvent(event) : undefined}
                key={event.id}
              />
            ))}
            {!visiblePractices(anchor).length && !dayEvents.length && (
              <Empty
                title="No schedule items this day"
                copy="Move to another day or clear filters."
              />
            )}
          </div>
        )}
        {view === "week" && (
          <div className="team-calendar-week">
            {weekDays.map((date) => {
              const events = visibleEvents(date);
              const dayPractices = visiblePractices(date);
              return (
                <section
                  className="team-calendar-day-column"
                  key={isoDate(date)}
                >
                  <header>
                    <span>{formatDate(isoDate(date)).split(" ")[0]}</span>
                    <b>{date.getUTCDate()}</b>
                  </header>
                  {dayPractices.map((practice) => (
                    <PracticeCalendarCard
                      practice={practice}
                      date={date}
                      onEdit={
                        onEditPractice
                          ? () => onEditPractice(practice)
                          : undefined
                      }
                      key={`${practice.id}-${isoDate(date)}`}
                    />
                  ))}
                  {events.map((event) => (
                    <TeamCalendarEventCard
                      event={event}
                      routines={data.routines}
                      members={data.members}
                      viewerMemberId={data.user.id}
                      teamFilter={teamFilter}
                      selectedMemberIds={selectedPeopleSet}
                      onEdit={
                        onEditEvent ? () => onEditEvent(event) : undefined
                      }
                      key={event.id}
                    />
                  ))}
                </section>
              );
            })}
          </div>
        )}
        {view === "month" && (
          <div className="team-calendar-month">
            {monthDays.map((date) => {
              const events = visibleEvents(date);
              const dayPractices = visiblePractices(date);
              const labels = [
                ...dayPractices.map((practice) => ({
                  label: `${teamName(practice.teamId)} Practice`,
                  personal: false,
                })),
                ...events.map((event) => ({
                  label: event.title,
                  personal: memberPerformsInRoutines(
                    teamCalendarEventRoutines(
                      event,
                      data.routines,
                      teamFilter,
                      selectedPeopleSet,
                    ),
                    data.user.id,
                  ),
                })),
              ].sort((a, b) => Number(b.personal) - Number(a.personal));
              const inMonth = date.getUTCMonth() === anchor.getUTCMonth();
              return (
                <button
                  className={cx(
                    "team-calendar-month-day",
                    !inMonth && "muted",
                    (events.length > 0 || dayPractices.length > 0) &&
                      "has-events",
                  )}
                  onClick={() => {
                    setAnchor(date);
                    setView("day");
                  }}
                  key={isoDate(date)}
                >
                  <span>{date.getUTCDate()}</span>
                  {labels.slice(0, 2).map((item, index) => (
                    <b
                      className={item.personal ? "personal-performance" : ""}
                      aria-label={
                        item.personal
                          ? `${item.label}, you're performing`
                          : undefined
                      }
                      key={`${item.label}-${index}`}
                    >
                      {item.label}
                    </b>
                  ))}
                  {labels.length > 2 && <small>+{labels.length - 2}</small>}
                </button>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
}

function CoachHome({
  data,
  setPage,
}: {
  data: BootstrapData;
  setPage: (page: Page) => void;
}) {
  const upcoming = data.events
    .filter(
      (event) =>
        event.status === "published" &&
        event.date &&
        event.date >= businessDate(),
    )
    .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));
  const due = data.payments.filter(
    (payment) => payment.status === "due" && paymentAmountCents(payment) > 0,
  );
  const birthdayOrder = [...data.members]
    .filter((member) => member.birthday)
    .sort((a, b) => (a.birthday ?? "").localeCompare(b.birthday ?? ""));
  return (
    <>
      <PageHeader
        eyebrow="Coach studio"
        title={`Welcome back, ${data.user.name.split(" ")[0]}.`}
        copy="Everything here is real team data: practices, casts, routines, payments, and announcements."
        action={
          <button
            className="primary-button"
            onClick={() => setPage("schedule")}
          >
            <Plus /> Schedule an event
          </button>
        }
      />
      <section className="fact-grid">
        <button onClick={() => setPage("people")}>
          <Users />{" "}
          <strong>
            {
              data.members.filter((member) => member.status === "approved")
                .length
            }
          </strong>
          <span>people</span>
        </button>
        <button onClick={() => setPage("routines")}>
          <ListMusic /> <strong>{data.routines.length}</strong>
          <span>active routines</span>
        </button>
        <button onClick={() => setPage("schedule")}>
          <CalendarDays /> <strong>{upcoming.length}</strong>
          <span>upcoming events</span>
        </button>
        <button onClick={() => setPage("payments")}>
          <CircleDollarSign /> <strong>{due.length}</strong>
          <span>payments due</span>
        </button>
      </section>
      <div className="two-column">
        <section className="panel">
          <header className="panel-head">
            <div>
              <span className="section-label">Performance calendar</span>
              <h2>Coming up</h2>
            </div>
            <button className="text-button" onClick={() => setPage("schedule")}>
              Full schedule <ChevronRight />
            </button>
          </header>
          <div className="compact-list">
            {upcoming.slice(0, 4).map((event) => (
              <button onClick={() => setPage("schedule")} key={event.id}>
                <span className="compact-date">{formatDate(event.date)}</span>
                <span>
                  <b>{event.title}</b>
                  <small>
                    {event.callTime ? `${event.callTime} call` : "Time TBD"}
                  </small>
                </span>
                <ChevronRight />
              </button>
            ))}
          </div>
        </section>
        <section className="panel">
          <header className="panel-head">
            <div>
              <span className="section-label">People</span>
              <h2>Birthdays</h2>
            </div>
          </header>
          <div className="birthday-list">
            {birthdayOrder.slice(0, 8).map((member) => (
              <div key={member.id}>
                <Avatar member={member} />
                <span>
                  <b>{member.name}</b>
                  <small>{formatBirthday(member.birthday)}</small>
                </span>
                <TeamBadges teamIds={member.teamIds} />
              </div>
            ))}
          </div>
        </section>
      </div>
      <section className="panel announcement-panel">
        <header className="panel-head">
          <div>
            <span className="section-label">Team updates</span>
            <h2>Latest announcements</h2>
          </div>
          <button
            className="text-button"
            onClick={() => setPage("announcements")}
          >
            Manage <ChevronRight />
          </button>
        </header>
        <div className="announcement-grid">
          {data.announcements.slice(0, 3).map((item) => (
            <article key={item.id}>
              <Megaphone />
              <div>
                <b>{item.title}</b>
                <p>{item.body}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

function ClassForm({
  item,
  data,
  onSave,
  onClose,
}: {
  item?: WeeklyClass;
  data: BootstrapData;
  onSave: (value: Omit<WeeklyClass, "updatedAt">) => Promise<void>;
  onClose: () => void;
}) {
  const [value, setValue] = useState<Omit<WeeklyClass, "updatedAt">>(
    item
      ? {
          ...item,
          coachMemberIds: item.coachMemberIds?.length
            ? item.coachMemberIds
            : [data.user.id],
          visibility: item.visibility ?? "all-members",
          visibleMemberIds: item.visibleMemberIds ?? [],
        }
      : {
          id: "",
          teamId: "fuerza",
          weekday: 2,
          startTime: "19:00",
          endTime: "20:30",
          venue: "Avante Dance Company",
          address: "Toronto, ON",
          active: true,
          coachMemberIds: [data.user.id],
          visibility: "all-members",
          visibleMemberIds: [],
        },
  );
  const coaches = data.members.filter(
    (member) => member.role === "teacher" && member.status === "approved",
  );
  const members = data.members.filter(
    (member) => member.role === "student" && member.status === "approved",
  );
  const selectedMemberIds = value.visibleMemberIds ?? [];
  return (
    <Modal
      title={item ? "Edit weekly practice" : "Add weekly practice"}
      onClose={onClose}
      className="member-modal practice-modal"
    >
      <form
        className="form-stack practice-form"
        onSubmit={async (event) => {
          event.preventDefault();
          await onSave(value);
          onClose();
        }}
      >
        <div className="practice-form-summary">
          <span className="practice-form-summary-icon">
            <CalendarDays />
          </span>
          <span>
            <small>Weekly practice</small>
            <b>
              {teamName(value.teamId)} · {weekdays[value.weekday]}
            </b>
            <span>
              {value.startTime}–{value.endTime}
              {value.venue ? ` · ${value.venue}` : ""}
            </span>
          </span>
          <Pill tone={value.active ? "sage" : "neutral"}>
            {value.active ? "Active" : "Inactive"}
          </Pill>
        </div>

        <fieldset className="member-form-section practice-form-section">
          <legend>
            <span>01</span>
            <span>
              <b>Practice assignment</b>
              <small>Choose the team and coach responsible each week.</small>
            </span>
          </legend>
          <div
            className="team-choice-grid practice-team-grid"
            role="radiogroup"
            aria-label="Practice team"
          >
            {(["maxima", "fuerza", "impulso"] as const).map((teamId) => (
              <label
                className={cx(
                  "team-choice",
                  `team-choice-${teamId}`,
                  value.teamId === teamId && "selected",
                )}
                key={teamId}
              >
                <input
                  type="radio"
                  name="practice-team"
                  checked={value.teamId === teamId}
                  onChange={() => setValue({ ...value, teamId })}
                />
                <span className="team-choice-mark">
                  <Check />
                </span>
                <span>
                  <b>{teamName(teamId)}</b>
                  <small>{teamName(teamId)} performance team</small>
                </span>
              </label>
            ))}
          </div>
          <label className="practice-coach-field">
            <span>Coach</span>
            <select
              value={value.coachMemberIds?.[0] ?? data.user.id}
              onChange={(event) =>
                setValue({ ...value, coachMemberIds: [event.target.value] })
              }
            >
              {coaches.map((member) => (
                <option value={member.id} key={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
          </label>
        </fieldset>

        <section className="member-form-section practice-form-section">
          <header>
            <span>02</span>
            <div>
              <b>Weekly time and place</b>
              <small>Set the recurring day, time window, and location.</small>
            </div>
          </header>
          <div className="practice-schedule-grid">
            <label className="practice-day-field">
              <span>Day</span>
              <select
                value={value.weekday}
                onChange={(event) =>
                  setValue({ ...value, weekday: Number(event.target.value) })
                }
              >
                {weekdays.map((day, index) => (
                  <option value={index} key={day}>
                    {day}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Starts</span>
              <input
                type="time"
                value={value.startTime}
                onChange={(event) =>
                  setValue({ ...value, startTime: event.target.value })
                }
                required
              />
            </label>
            <label>
              <span>Ends</span>
              <input
                type="time"
                value={value.endTime}
                onChange={(event) =>
                  setValue({ ...value, endTime: event.target.value })
                }
                required
              />
            </label>
          </div>
          <div className="practice-location-grid">
            <label>
              <span>Venue</span>
              <input
                value={value.venue}
                onChange={(event) =>
                  setValue({ ...value, venue: event.target.value })
                }
                placeholder="Venue name"
              />
            </label>
            <label>
              <span>Address</span>
              <input
                value={value.address}
                onChange={(event) =>
                  setValue({ ...value, address: event.target.value })
                }
                placeholder="Toronto, ON"
              />
            </label>
          </div>
        </section>

        <fieldset className="member-form-section practice-form-section">
          <legend>
            <span>03</span>
            <span>
              <b>Member access</b>
              <small>
                Practices are visible to everyone unless restricted.
              </small>
            </span>
          </legend>
          <div
            className="role-choice-grid practice-visibility-grid"
            role="radiogroup"
            aria-label="Practice visibility"
          >
            {(
              [
                [
                  "all-members",
                  "All members",
                  "Everyone on both teams can see this practice.",
                  Users,
                ],
                [
                  "selected-members",
                  "Selected members",
                  "Limit this practice to the people you choose.",
                  ShieldCheck,
                ],
              ] as const
            ).map(([visibility, label, description, Icon]) => (
              <label
                className={cx(
                  "choice-card",
                  value.visibility === visibility && "selected",
                )}
                key={visibility}
              >
                <input
                  type="radio"
                  name="practice-visibility"
                  checked={value.visibility === visibility}
                  onChange={() =>
                    setValue({
                      ...value,
                      visibility,
                      visibleMemberIds:
                        visibility === "selected-members"
                          ? value.visibleMemberIds
                          : [],
                    })
                  }
                />
                <span className="choice-icon">
                  <Icon />
                </span>
                <span>
                  <b>{label}</b>
                  <small>{description}</small>
                </span>
                <span className="choice-check">
                  <Check />
                </span>
              </label>
            ))}
          </div>
          {value.visibility === "selected-members" && (
            <div className="practice-member-access">
              <header>
                <span>
                  <b>Choose members</b>
                  <small>
                    {selectedMemberIds.length} of {members.length} selected
                  </small>
                </span>
                <span className="member-select-actions">
                  <button
                    className="text-button"
                    type="button"
                    onClick={() =>
                      setValue({
                        ...value,
                        visibleMemberIds: members.map((member) => member.id),
                      })
                    }
                  >
                    Select all
                  </button>
                  {selectedMemberIds.length > 0 && (
                    <button
                      className="text-button"
                      type="button"
                      onClick={() =>
                        setValue({ ...value, visibleMemberIds: [] })
                      }
                    >
                      Clear
                    </button>
                  )}
                </span>
              </header>
              <div
                className="member-picker-grid practice-member-picker"
                aria-label="Members who can see this weekly practice"
              >
                {renderGroupedMembers(members, (member) => {
                  const selected = selectedMemberIds.includes(member.id);
                  return (
                    <button
                      className={selected ? "selected" : ""}
                      type="button"
                      aria-pressed={selected}
                      onClick={() =>
                        setValue({
                          ...value,
                          visibleMemberIds: selected
                            ? selectedMemberIds.filter((id) => id !== member.id)
                            : [...selectedMemberIds, member.id],
                        })
                      }
                      key={member.id}
                    >
                      <Avatar member={member} />
                      <span>
                        <b>{member.name}</b>
                        <small>{teamLabel(member.teamIds)}</small>
                      </span>
                      <Check />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </fieldset>
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary-button" type="submit">
            <Save /> Save practice
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EventForm({
  item,
  data,
  onSave,
  onDelete,
  onClose,
}: {
  item?: EventRecord;
  data: BootstrapData;
  onSave: (value: Omit<EventRecord, "updatedAt">) => Promise<void>;
  onDelete?: () => Promise<void>;
  onClose: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [value, setValue] = useState<Omit<EventRecord, "updatedAt">>(
    item
      ? {
          ...item,
          coachMemberIds: item.coachMemberIds?.length
            ? item.coachMemberIds
            : [data.user.id],
          visibility: item.visibility ?? "all-members",
          visibleMemberIds: item.visibleMemberIds ?? [],
        }
      : {
          id: "",
          title: "",
          type: "performance",
          date: "",
          callTime: "",
          showTime: "",
          endTime: "",
          venue: "",
          address: "Toronto, ON",
          notes: "",
          status: "published",
          routines: [],
          teamIds: ["maxima", "fuerza", "impulso"],
          coachMemberIds: [data.user.id],
          visibility: "all-members",
          visibleMemberIds: [],
        },
  );
  const toggleRoutine = (routineId: string, selected: boolean) =>
    setValue({
      ...value,
      routines: selected
        ? [
            ...value.routines,
            { id: crypto.randomUUID(), routineId, performers: [] },
          ]
        : value.routines.filter((entry) => entry.routineId !== routineId),
    });
  const updateEntry = (entryId: string, patch: Partial<EventRoutine>) =>
    setValue({
      ...value,
      routines: value.routines.map((entry) =>
        entry.id === entryId ? { ...entry, ...patch } : entry,
      ),
    });
  const toggleMember = (
    entry: EventRoutine,
    memberId: string,
    checked: boolean,
  ) => {
    const capability = data.members
      .find((member) => member.id === memberId)
      ?.capabilities?.find((item) => item.routineId === entry.routineId);
    const performers = checked
      ? [...entry.performers, { memberId, position: capability?.slot ?? "" }]
      : entry.performers.filter((person) => person.memberId !== memberId);
    updateEntry(entry.id, { performers });
  };
  const updatePosition = (
    entry: EventRoutine,
    memberId: string,
    position: string,
  ) =>
    updateEntry(entry.id, {
      performers: entry.performers.map((person) =>
        person.memberId === memberId ? { ...person, position } : person,
      ),
    });
  return (
    <>
      <Modal
        title={item ? "Edit event" : "Schedule an event"}
        eyebrow={
          value.type === "practice"
            ? "Practice schedule"
            : "Performance calendar"
        }
        onClose={onClose}
        wide
        className="member-modal"
      >
        <form
          className="form-stack event-form"
          onSubmit={async (event) => {
            event.preventDefault();
            await onSave({
              ...value,
              date: value.date || undefined,
              callTime: value.callTime || undefined,
              showTime: value.showTime || undefined,
              endTime: value.endTime || undefined,
              feePerPerformer: value.feePerPerformer || undefined,
            });
            onClose();
          }}
        >
          <section className="member-form-section event-form-section">
            <header>
              <span>01</span>
              <div>
                <b>Event basics</b>
                <small>
                  Name the event and choose how visible it should be.
                </small>
              </div>
            </header>
            <div className="event-basics-grid">
              <label className="event-title-field">
                <span>Event name</span>
                <input
                  value={value.title}
                  onChange={(event) =>
                    setValue({ ...value, title: event.target.value })
                  }
                  placeholder="e.g. Harbourfront"
                  required
                />
              </label>
              <label>
                <span>Mode</span>
                <select
                  value={value.type}
                  onChange={(event) =>
                    setValue({
                      ...value,
                      type: event.target.value as EventRecord["type"],
                    })
                  }
                >
                  <option value="performance">Performance</option>
                  <option value="competition">Competition</option>
                  <option value="practice">Practice</option>
                  <option value="team-event">Team event</option>
                </select>
              </label>
              <label>
                <span>Status</span>
                <select
                  value={value.status}
                  onChange={(event) =>
                    setValue({
                      ...value,
                      status: event.target.value as EventRecord["status"],
                    })
                  }
                >
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </label>
            </div>
            <div className="field-grid">
              <label>
                <span>Visibility</span>
                <select
                  value={value.visibility ?? "all-members"}
                  onChange={(event) =>
                    setValue({
                      ...value,
                      visibility: event.target
                        .value as EventRecord["visibility"],
                    })
                  }
                >
                  <option value="all-members">All members</option>
                  <option value="selected-members">
                    Selected members only
                  </option>
                </select>
              </label>
              <label>
                <span>Coach</span>
                <select
                  value={value.coachMemberIds?.[0] ?? data.user.id}
                  onChange={(event) =>
                    setValue({ ...value, coachMemberIds: [event.target.value] })
                  }
                >
                  {data.members
                    .filter((member) => member.role === "teacher")
                    .map((member) => (
                      <option value={member.id} key={member.id}>
                        {member.name}
                      </option>
                    ))}
                </select>
              </label>
            </div>
            {value.visibility === "selected-members" && (
              <div
                className="compact-check-grid"
                aria-label="Members who can see this event"
              >
                {renderGroupedMembers(
                  data.members.filter(
                    (member) =>
                      member.role === "student" && member.status === "approved",
                  ),
                  (member) => (
                    <label key={member.id}>
                      <input
                        type="checkbox"
                        checked={
                          value.visibleMemberIds?.includes(member.id) ?? false
                        }
                        onChange={(event) =>
                          setValue({
                            ...value,
                            visibleMemberIds: event.target.checked
                              ? [...(value.visibleMemberIds ?? []), member.id]
                              : (value.visibleMemberIds ?? []).filter(
                                  (id) => id !== member.id,
                                ),
                          })
                        }
                      />
                      {member.name}
                    </label>
                  ),
                )}
              </div>
            )}
            <div className="event-summary-strip">
              <span>
                <CalendarDays />
                {value.date ? formatDate(value.date, true) : "Date not set"}
              </span>
              <span>
                <Users />
                {value.routines.reduce(
                  (count, entry) => count + entry.performers.length,
                  0,
                )}{" "}
                selected
              </span>
              <span>
                <CircleDollarSign />
                {value.feePerPerformer
                  ? `${money(value.feePerPerformer)} each`
                  : "No event fee"}
              </span>
            </div>
          </section>
          <section className="member-form-section event-form-section">
            <header>
              <span>02</span>
              <div>
                <b>Schedule and venue</b>
                <small>Times are optional until the details are locked.</small>
              </div>
            </header>
            <div className="event-schedule-grid">
              <label>
                <span>Date</span>
                <input
                  type="date"
                  value={value.date ?? ""}
                  onChange={(event) =>
                    setValue({ ...value, date: event.target.value })
                  }
                />
              </label>
              <label>
                <span>Call</span>
                <input
                  type="time"
                  value={value.callTime ?? ""}
                  onChange={(event) =>
                    setValue({ ...value, callTime: event.target.value })
                  }
                />
              </label>
              <label>
                <span>Show</span>
                <input
                  type="time"
                  value={value.showTime ?? ""}
                  onChange={(event) =>
                    setValue({ ...value, showTime: event.target.value })
                  }
                />
              </label>
              <label>
                <span>End</span>
                <input
                  type="time"
                  value={value.endTime ?? ""}
                  onChange={(event) =>
                    setValue({ ...value, endTime: event.target.value })
                  }
                />
              </label>
              <label className="event-wide-field">
                <span>Venue</span>
                <input
                  value={value.venue}
                  onChange={(event) =>
                    setValue({ ...value, venue: event.target.value })
                  }
                  placeholder="Venue name"
                />
              </label>
              <label>
                <span>Performer fee</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={value.feePerPerformer ?? ""}
                  onChange={(event) =>
                    setValue({
                      ...value,
                      feePerPerformer: event.target.value
                        ? Number(event.target.value)
                        : undefined,
                    })
                  }
                  placeholder="Optional"
                />
              </label>
              <label className="event-wide-field">
                <span>Address</span>
                <input
                  value={value.address}
                  onChange={(event) =>
                    setValue({ ...value, address: event.target.value })
                  }
                  placeholder="Toronto, ON"
                />
              </label>
              <label className="event-wide-field">
                <span>Notes</span>
                <textarea
                  value={value.notes}
                  onChange={(event) =>
                    setValue({ ...value, notes: event.target.value })
                  }
                  rows={3}
                />
              </label>
            </div>
          </section>
          <section className="member-form-section event-form-section routine-selector">
            <header>
              <span>03</span>
              <div>
                <b>Program and cast</b>
                <small>
                  Select routines and performers for slot details. Event
                  visibility is controlled above.
                </small>
              </div>
            </header>
            <div className="routine-checks">
              {data.routines.map((routine) => (
                <label
                  className={cx(
                    "event-routine-choice",
                    value.routines.some(
                      (entry) => entry.routineId === routine.id,
                    ) && "selected",
                  )}
                  key={routine.id}
                >
                  <input
                    type="checkbox"
                    checked={value.routines.some(
                      (entry) => entry.routineId === routine.id,
                    )}
                    onChange={(event) =>
                      toggleRoutine(routine.id, event.target.checked)
                    }
                  />
                  <span>
                    <b>{routine.name}</b>
                    <small>
                      {teamName(routine.teamId)} ·{" "}
                      {routine.tags.join(", ") || "Untagged"}
                    </small>
                  </span>
                  <Check />
                </label>
              ))}
            </div>
            {value.routines.map((entry) => {
              const routine = data.routines.find(
                (item) => item.id === entry.routineId,
              )!;
              return (
                <article className="cast-builder" key={entry.id}>
                  <div className="cast-builder-head">
                    <span>
                      <TeamBadge teamId={routine.teamId} />
                      <b>{routine.name}</b>
                    </span>
                    {value.type === "competition" && (
                      <label>
                        <span>Category</span>
                        <input
                          value={entry.label ?? ""}
                          onChange={(event) =>
                            updateEntry(entry.id, { label: event.target.value })
                          }
                          placeholder="e.g. Open"
                        />
                      </label>
                    )}
                  </div>
                  <div className="cast-member-grid">
                    {renderGroupedMembers(
                      data.members.filter(
                        (member) => member.status === "approved",
                      ),
                      (member) => {
                        const selected = entry.performers.find(
                          (person) => person.memberId === member.id,
                        );
                        return (
                          <div
                            className={selected ? "selected" : ""}
                            key={member.id}
                          >
                            <label>
                              <input
                                type="checkbox"
                                checked={Boolean(selected)}
                                onChange={(event) =>
                                  toggleMember(
                                    entry,
                                    member.id,
                                    event.target.checked,
                                  )
                                }
                              />
                              <Avatar member={member} />
                              <span>
                                <b>{member.name}</b>
                                <small>{teamLabel(member.teamIds)}</small>
                              </span>
                            </label>
                            {selected && (
                              <input
                                value={selected.position}
                                onChange={(event) =>
                                  updatePosition(
                                    entry,
                                    member.id,
                                    event.target.value,
                                  )
                                }
                                placeholder="Position / slot"
                              />
                            )}
                          </div>
                        );
                      },
                    )}
                  </div>
                </article>
              );
            })}
          </section>
          <div className="modal-actions">
            {item && onDelete && (
              <button
                type="button"
                className="destructive-button modal-delete-action"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 /> Delete event
              </button>
            )}
            <button
              type="button"
              className="secondary-button"
              onClick={onClose}
            >
              Cancel
            </button>
            <button className="primary-button" type="submit">
              <Save /> Save event
            </button>
          </div>
        </form>
      </Modal>
      {confirmDelete && item && onDelete && (
        <ConfirmDialog
          title={`Delete ${item.title}?`}
          copy="This removes the event, its schedule entry, and unpaid generated fee rows. Receipt-backed payment history stays in each private ledger. This cannot be undone."
          confirmLabel="Delete event"
          busy={deleteBusy}
          onClose={() => setConfirmDelete(false)}
          onConfirm={async () => {
            setDeleteBusy(true);
            try {
              await onDelete();
              onClose();
            } finally {
              setDeleteBusy(false);
            }
          }}
        />
      )}
    </>
  );
}

function CoachSchedule({
  data,
  reload,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
}) {
  const [tab, setTab] = useState<"classes" | "events">("events");
  const [classItem, setClassItem] = useState<WeeklyClass | "new">();
  const [eventItem, setEventItem] = useState<EventRecord | "new">();
  const [createOpen, setCreateOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  return (
    <>
      <PageHeader
        eyebrow="Two calendars, one team"
        title="Practices & performances"
        copy="Weekly practices recur by team. Performances and competitions are separately cast and scheduled."
        action={
          <button
            className="primary-button"
            onClick={() =>
              tab === "classes" ? setClassItem("new") : setEventItem("new")
            }
          >
            <Plus /> {tab === "classes" ? "Add practice" : "Add event"}
          </button>
        }
      />
      <CalendarSubscription data={data} />
      <div className="segmented">
        <button
          className={tab === "events" ? "active" : ""}
          onClick={() => setTab("events")}
        >
          Performances & competitions
        </button>
        <button
          className={tab === "classes" ? "active" : ""}
          onClick={() => setTab("classes")}
        >
          Weekly practices
        </button>
      </div>
      {tab === "classes" ? (
        <section className="class-grid">
          {data.classes.map((item) => (
            <article key={item.id}>
              <div className="class-card-head">
                <TeamBadge teamId={item.teamId} />
                <CalendarActionButton
                  href={googleClassUrl(item)}
                  label={`Add ${teamName(item.teamId)} practice to Google Calendar`}
                />
              </div>
              <h2>{weekdays[item.weekday]}</h2>
              <strong>
                {item.startTime}–{item.endTime}
              </strong>
              <p>
                <MapPin /> {item.venue}
              </p>
              <div>
                <button onClick={() => setClassItem(item)}>
                  <Pencil /> Edit
                </button>
              </div>
            </article>
          ))}
        </section>
      ) : (
        <div className="event-list">
          {[...data.events]
            .sort((a, b) => (a.date ?? "9999").localeCompare(b.date ?? "9999"))
            .map((event) => (
              <EventCard
                event={event}
                routines={data.routines}
                members={data.members}
                onEdit={() => setEventItem(event)}
                key={event.id}
              />
            ))}
        </div>
      )}
      {classItem && (
        <ClassForm
          item={classItem === "new" ? undefined : classItem}
          data={data}
          onClose={() => setClassItem(undefined)}
          onSave={async (value) => {
            await api("/api/teacher/classes", {
              method: "POST",
              body: JSON.stringify(value),
            });
            await reload();
          }}
        />
      )}
      {eventItem && (
        <EventForm
          item={eventItem === "new" ? undefined : eventItem}
          data={data}
          onClose={() => setEventItem(undefined)}
          onSave={async (value) => {
            await api("/api/teacher/events", {
              method: "POST",
              body: JSON.stringify(value),
            });
            await reload();
          }}
        />
      )}
    </>
  );
}

function MediaUploader({
  target,
  accept,
  label,
  reload,
}: {
  target: MediaAsset["uploadTarget"];
  accept: string;
  label: string;
  reload: () => Promise<void>;
}) {
  const [status, setStatus] = useState("");
  const upload = async (file?: File) => {
    if (!file || !target) return;
    setStatus("Preparing…");
    try {
      const result = await api<{
        asset: MediaAsset;
        uploadUrl: string;
        method: "PUT";
        headers: Record<string, string>;
      }>("/api/media/uploads", {
        method: "POST",
        body: JSON.stringify({
          title: file.name.replace(/\.[^.]+$/, ""),
          originalName: file.name,
          mime:
            file.type ||
            (accept.startsWith("audio")
              ? "audio/mpeg"
              : accept.startsWith("image")
                ? "image/jpeg"
                : "video/mp4"),
          size: file.size,
          target,
        }),
      });
      setStatus("Uploading…");
      const uploaded = await fetch(result.uploadUrl, {
        method: result.method,
        headers: result.headers,
        body: file,
      });
      if (!uploaded.ok) throw new Error(`Upload failed (${uploaded.status})`);
      setStatus("Finishing…");
      await api(`/api/media/uploads/${result.asset.id}/complete`, {
        method: "POST",
        body: "{}",
      });
      await reload();
      setStatus("Uploaded");
      window.setTimeout(() => setStatus(""), 1500);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Upload failed");
    }
  };
  return (
    <label className="upload-button">
      <Upload /> {status || label}
      <input
        type="file"
        accept={accept}
        onChange={(event) => void upload(event.target.files?.[0])}
      />
    </label>
  );
}

type PracticeEntryKind = NonNullable<PracticeSection["kind"]>;

const tempoMin = 50;
const tempoMax = 125;
const tempoStep = 0.5;
const minSelectionSeconds = 0.5;

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const roundTime = (value: number) => Math.round(value * 10) / 10;

const snapTempo = (value: number) =>
  clampNumber(Math.round(value / tempoStep) * tempoStep, tempoMin, tempoMax);

const formatTempo = (value: number) =>
  Number.isInteger(value) ? String(value) : value.toFixed(1);

const formatTime = (seconds: number) => {
  const safe = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const minutes = Math.floor(safe / 60);
  const wholeSeconds = Math.floor(safe % 60);
  const tenths = Math.floor((safe % 1) * 10);
  return `${minutes}:${String(wholeSeconds).padStart(2, "0")}${
    tenths ? `.${tenths}` : ""
  }`;
};

const practiceEntryKind = (section: PracticeSection): PracticeEntryKind =>
  section.kind ??
  (Math.abs(section.end - section.start) < minSelectionSeconds
    ? "marker"
    : "section");

const isEditableTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  Boolean(target.closest("input, select, textarea"));

const tempoStorageKey = "avante-practice-tempo-v1";
const storedTempo = () => {
  try {
    return snapTempo(Number(sessionStorage.getItem(tempoStorageKey) ?? 100));
  } catch {
    return 100;
  }
};

const saveStoredTempo = (value: number) => {
  try {
    sessionStorage.setItem(tempoStorageKey, String(value));
  } catch {
    // Playback speed is an ephemeral preference; storage failure is harmless.
  }
};

const timeToRatio = (
  time: number,
  viewportStart: number,
  viewportEnd: number,
) =>
  clampNumber(
    (time - viewportStart) /
      Math.max(minSelectionSeconds, viewportEnd - viewportStart),
    0,
    1,
  );

const regionStyle = (
  start: number,
  end: number,
  viewportStart: number,
  viewportEnd: number,
) => {
  const left = timeToRatio(
    Math.max(start, viewportStart),
    viewportStart,
    viewportEnd,
  );
  const right = timeToRatio(
    Math.min(end, viewportEnd),
    viewportStart,
    viewportEnd,
  );
  return {
    left: `${left * 100}%`,
    width: `${Math.max(1.5, (right - left) * 100)}%`,
  };
};

function MiniWaveform({
  wave,
  duration,
  start,
  end,
  kind = "section",
  active,
}: {
  wave: number[];
  duration: number;
  start: number;
  end: number;
  kind?: PracticeEntryKind;
  active?: boolean;
}) {
  const safeDuration = Math.max(duration, end, 1);
  const bars = 80;
  const visible = Array.from({ length: bars }, (_, index) => {
    if (!wave.length) return 15 + (index % 7) * 7;
    const source = Math.min(
      wave.length - 1,
      Math.floor((index / bars) * wave.length),
    );
    return wave[source] ?? 18;
  });
  const markerLeft = timeToRatio(start, 0, safeDuration) * 100;
  return (
    <span className={cx("mini-waveform", kind, active && "active")}>
      <span className="mini-wave-track">
        {kind === "section" && (
          <span
            className="mini-waveform-selection"
            style={regionStyle(start, end, 0, safeDuration)}
          />
        )}
        <span className="mini-waveform-bars">
          {visible.map((height, index) => {
            const barStart = (index / bars) * safeDuration;
            const barEnd = ((index + 1) / bars) * safeDuration;
            const selected =
              kind === "section" && barEnd >= start && barStart <= end;
            return (
              <i
                className={selected ? "selected" : ""}
                key={index}
                style={{ height: `${height}%` }}
              />
            );
          })}
        </span>
        {kind === "marker" && (
          <span
            className="mini-waveform-marker"
            style={{ left: `${markerLeft}%` }}
          />
        )}
      </span>
    </span>
  );
}

function TempoTape({
  value,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
}) {
  const drag = useRef<
    | {
        pointerId: number;
        startX: number;
        startValue: number;
        moved: boolean;
      }
    | undefined
  >(undefined);
  const stops = useMemo(
    () =>
      Array.from(
        { length: (tempoMax - tempoMin) / tempoStep + 1 },
        (_, index) => tempoMin + index * tempoStep,
      ),
    [],
  );
  const commit = (next: number) => onChange(snapTempo(next));
  const nudge = (direction: -1 | 1) => commit(value + direction * tempoStep);

  return (
    <div
      className="tempo-tape"
      role="slider"
      tabIndex={0}
      aria-label="Playback speed"
      aria-valuemin={tempoMin}
      aria-valuemax={tempoMax}
      aria-valuenow={value}
      aria-valuetext={`${formatTempo(value)}%`}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
          event.preventDefault();
          nudge(-1);
        }
        if (event.key === "ArrowRight" || event.key === "ArrowUp") {
          event.preventDefault();
          nudge(1);
        }
        if (event.key === "Home") {
          event.preventDefault();
          commit(tempoMin);
        }
        if (event.key === "End") {
          event.preventDefault();
          commit(tempoMax);
        }
      }}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startValue: value,
          moved: false,
        };
      }}
      onPointerMove={(event) => {
        const current = drag.current;
        if (!current || current.pointerId !== event.pointerId) return;
        const delta = event.clientX - current.startX;
        if (Math.abs(delta) > 3) current.moved = true;
        commit(current.startValue - (delta / 22) * tempoStep);
      }}
      onPointerUp={(event) => {
        const current = drag.current;
        if (!current || current.pointerId !== event.pointerId) return;
        if (!current.moved) {
          const rect = event.currentTarget.getBoundingClientRect();
          const offset = event.clientX - (rect.left + rect.width / 2);
          commit(value + (offset / 22) * tempoStep);
        }
        drag.current = undefined;
      }}
      onPointerCancel={() => {
        drag.current = undefined;
      }}
    >
      <span className="tempo-tape-readout">{formatTempo(value)}%</span>
      <span className="tempo-tape-window" aria-hidden="true">
        <span
          className="tempo-tape-track"
          style={
            {
              "--tempo-index": (value - tempoMin) / tempoStep,
            } as React.CSSProperties
          }
        >
          {stops.map((stop) => (
            <span className="tempo-tape-stop" key={stop}>
              <i />
              {stop % 25 === 0 || stop === value ? (
                <b>{formatTempo(stop)}</b>
              ) : null}
            </span>
          ))}
        </span>
        <span className="tempo-tape-center" />
      </span>
    </div>
  );
}

type WaveDrag =
  | {
      type: "start" | "end" | "marker";
      pointerId: number;
    }
  | {
      type: "pan";
      pointerId: number;
      startX: number;
      startViewport: number;
      moved: boolean;
      tapTime: number;
    };

type WavePointer = {
  clientX: number;
  clientY: number;
};

type PinchZoom = {
  initialDistance: number;
  initialZoom: number;
  anchorTime: number;
  anchorRatio: number;
};

type PracticeEntryScope = "teacher" | "personal";

type EditingSection =
  | {
      mode: "creating";
      source: PracticeEntryScope;
    }
  | {
      mode: "editing";
      source: PracticeEntryScope;
      id: string;
    };

const pointerDistance = (first: WavePointer, second: WavePointer) =>
  Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);

const pointerMidpoint = (first: WavePointer, second: WavePointer) => ({
  clientX: (first.clientX + second.clientX) / 2,
  clientY: (first.clientY + second.clientY) / 2,
});

function PracticePlayer({
  media,
  teacherSections = [],
  personalSections = [],
  canEditCoach = false,
  canEditPersonal = true,
  addLabel,
  onAdd,
  onUpdateCoach,
  onUpdatePersonal,
}: {
  media?: MediaAsset;
  teacherSections?: PracticeSection[];
  personalSections?: PracticeSection[];
  canEditCoach?: boolean;
  canEditPersonal?: boolean;
  addLabel: string;
  onAdd: (section: Omit<PracticeSection, "id">) => Promise<void>;
  onUpdateCoach?: (
    sectionId: string,
    section: Omit<PracticeSection, "id">,
  ) => Promise<void>;
  onUpdatePersonal?: (
    sectionId: string,
    section: Omit<PracticeSection, "id">,
  ) => Promise<void>;
}) {
  const player = useRef<HTMLElement>(null);
  const audio = useRef<HTMLAudioElement>(null);
  const waveTrack = useRef<HTMLDivElement>(null);
  const drag = useRef<WaveDrag | undefined>(undefined);
  const wavePointers = useRef(new Map<number, WavePointer>());
  const pinchZoom = useRef<PinchZoom | undefined>(undefined);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [tempo, setTempoState] = useState(storedTempo);
  const [activeEntry, setActiveEntry] = useState<PracticeSection>();
  const [composerOpen, setComposerOpen] = useState(false);
  const [editing, setEditing] = useState<EditingSection>();
  const [entryMode, setEntryMode] = useState<PracticeEntryKind>("section");
  const [draftName, setDraftName] = useState("");
  const [draftStart, setDraftStart] = useState(0);
  const [draftEnd, setDraftEnd] = useState(15);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [viewportStart, setViewportStart] = useState(0);
  const [wave, setWave] = useState<number[]>([]);
  const allEntries = useMemo(
    () => [...teacherSections, ...personalSections],
    [teacherSections, personalSections],
  );
  const markers = allEntries.filter(
    (section) => practiceEntryKind(section) === "marker",
  );
  const visibleDuration = duration
    ? Math.min(duration, Math.max(8, duration / zoom))
    : 0;
  const viewportEnd = duration
    ? Math.min(duration, viewportStart + visibleDuration)
    : 0;
  const visibleWave = useMemo(() => {
    if (!wave.length || !duration || !visibleDuration) return wave;
    const first = Math.floor((viewportStart / duration) * wave.length);
    const last = Math.max(
      first + 24,
      Math.ceil((viewportEnd / duration) * wave.length),
    );
    return wave.slice(first, Math.min(wave.length, last));
  }, [duration, viewportEnd, viewportStart, visibleDuration, wave]);
  const renderedWave = visibleWave.length
    ? visibleWave
    : Array.from({ length: 72 }, (_, index) => 14 + (index % 7) * 7);
  const setTempo = (value: number) => {
    const next = snapTempo(value);
    setTempoState(next);
    saveStoredTempo(next);
  };
  useEffect(() => {
    if (!media) return;
    let cancelled = false;
    const bars = 420;
    const cacheKey = waveformCacheKey(media, bars);
    setWave([]);
    setProgress(0);
    setDuration(0);
    setActiveEntry(undefined);
    setComposerOpen(false);
    setViewportStart(0);
    setZoom(1);
    void getCachedWaveform(cacheKey)
      .then((cached) => {
        if (!cached || cancelled) return undefined;
        setWave(cached.peaks);
        return cached;
      })
      .then((cached) => {
        if (cached || cancelled) return undefined;
        return fetchAndCacheAudio(media);
      })
      .then(async (bytes) => {
        if (!bytes || cancelled) return;
        const context = new AudioContext();
        const buffer = await context.decodeAudioData(bytes);
        const channel = buffer.getChannelData(0);
        const block = Math.max(1, Math.floor(channel.length / bars));
        const peaks = Array.from({ length: bars }, (_, index) => {
          let peak = 0;
          const start = index * block;
          for (
            let cursor = start;
            cursor < Math.min(channel.length, start + block);
            cursor += 16
          )
            peak = Math.max(peak, Math.abs(channel[cursor] ?? 0));
          return Math.max(5, peak * 100);
        });
        if (!cancelled) {
          setWave(peaks);
          void saveCachedWaveform(cacheKey, peaks, buffer.duration, bars);
        }
        await context.close();
      })
      .catch(() => !cancelled && setWave([]));
    return () => {
      cancelled = true;
    };
  }, [media?.id]);
  useEffect(() => {
    if (audio.current) {
      audio.current.playbackRate = tempo / 100;
      audio.current.preservesPitch = true;
    }
  }, [tempo]);
  useEffect(() => {
    if (!duration) return;
    setDraftEnd((current) => Math.min(duration, Math.max(current, 15)));
    setZoom(duration > 80 ? 4 : 1);
  }, [duration]);
  useEffect(() => {
    if (!duration || !visibleDuration) return;
    setViewportStart((current) =>
      clampNumber(current, 0, Math.max(0, duration - visibleDuration)),
    );
  }, [duration, visibleDuration]);
  if (!media)
    return (
      <Empty
        title="No song uploaded"
        copy="A coach can upload the shared routine song here."
      />
    );
  const loopActive =
    activeEntry && practiceEntryKind(activeEntry) === "section"
      ? activeEntry
      : undefined;
  const seek = (time: number) => {
    if (!audio.current || !duration) return;
    const next = clampNumber(time, 0, duration);
    audio.current.currentTime = next;
    setProgress(next);
  };
  const play = () => {
    void audio.current?.play().catch(() => undefined);
  };
  const togglePlayback = () => {
    if (!audio.current) return;
    audio.current.paused ? play() : audio.current.pause();
  };
  const zoomAt = (next: number, anchorTime: number, anchorRatio = 0.5) => {
    if (!duration) return;
    const bounded = clampNumber(next, 1, 18);
    const nextVisible = Math.min(duration, Math.max(8, duration / bounded));
    setZoom(bounded);
    setViewportStart(
      clampNumber(
        anchorTime - nextVisible * anchorRatio,
        0,
        duration - nextVisible,
      ),
    );
  };
  const fitDraftToViewport = (
    kind: PracticeEntryKind = entryMode,
    start = draftStart,
    end = draftEnd,
  ) => {
    if (!duration) return;
    const safeStart = clampNumber(start, 0, duration);
    const safeEnd =
      kind === "marker"
        ? safeStart
        : clampNumber(Math.max(end, start), 0, duration);
    const span =
      kind === "marker"
        ? Math.min(duration, 12)
        : Math.max(minSelectionSeconds, safeEnd - safeStart);
    const nextVisible = clampNumber(
      span * (kind === "marker" ? 1.8 : 2.6),
      kind === "marker" ? 12 : 10,
      duration,
    );
    const midpoint =
      kind === "marker" ? safeStart : safeStart + (safeEnd - safeStart) / 2;
    zoomAt(duration / nextVisible, midpoint, 0.5);
  };
  const previewDraft = () => {
    if (!audio.current || !duration) return;
    if (activeEntry?.id === "draft" && playing) {
      audio.current.pause();
      return;
    }
    const draftEntry =
      entryMode === "marker"
        ? {
            id: "draft",
            kind: "marker" as const,
            name: draftName || "Draft marker",
            start: draftStart,
            end: draftStart,
            color: "#5eead4",
          }
        : {
            id: "draft",
            kind: "section" as const,
            name: draftName || "Draft loop",
            start: draftStart,
            end: draftEnd,
            color: "#ef476f",
          };
    setActiveEntry(draftEntry);
    seek(draftEntry.start);
    play();
  };
  const selectEntry = (section: PracticeSection) => {
    setActiveEntry(section);
    setComposerOpen(false);
    seek(section.start);
    if (duration) {
      const kind = practiceEntryKind(section);
      const span =
        kind === "marker"
          ? 8
          : Math.max(minSelectionSeconds, section.end - section.start);
      const nextVisible = clampNumber(span * 2.5, 8, duration);
      const anchor =
        kind === "marker" ? section.start : (section.start + section.end) / 2;
      zoomAt(duration / nextVisible, anchor);
    }
    play();
  };
  const toggleEntry = (section: PracticeSection) => {
    if (activeEntry?.id === section.id && playing) {
      audio.current?.pause();
      return;
    }
    selectEntry(section);
  };
  const editEntry = (section: PracticeSection, source: PracticeEntryScope) => {
    const kind = practiceEntryKind(section);
    setActiveEntry(section);
    setEntryMode(kind);
    setEditing({ mode: "editing", source, id: section.id });
    setComposerOpen(true);
    setDraftName(section.name);
    setDraftStart(section.start);
    setDraftEnd(kind === "marker" ? section.start : section.end);
    seek(section.start);
    fitDraftToViewport(kind, section.start, section.end);
    requestAnimationFrame(() =>
      player.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };
  const timeFromPointer = (
    event: React.PointerEvent<HTMLElement>,
    element: HTMLElement,
  ) => {
    const rect = (waveTrack.current ?? element).getBoundingClientRect();
    const percent = clampNumber((event.clientX - rect.left) / rect.width, 0, 1);
    return clampNumber(viewportStart + percent * visibleDuration, 0, duration);
  };
  const percentInViewport = (time: number) =>
    visibleDuration ? ((time - viewportStart) / visibleDuration) * 100 : 0;
  const setHandleTime = (handle: "start" | "end" | "marker", time: number) => {
    const next = roundTime(clampNumber(time, 0, duration || time));
    if (handle === "marker") {
      setDraftStart(next);
      setDraftEnd(next);
      return;
    }
    if (handle === "start")
      setDraftStart(Math.min(next, draftEnd - minSelectionSeconds));
    if (handle === "end")
      setDraftEnd(Math.max(next, draftStart + minSelectionSeconds));
  };
  const beginHandleDrag = (
    handle: "start" | "end" | "marker",
    event: React.PointerEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    drag.current = { type: handle, pointerId: event.pointerId };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onWavePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!duration || !visibleDuration) return;
    player.current?.focus();
    wavePointers.current.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY,
    });
    event.currentTarget.setPointerCapture(event.pointerId);
    const activePointers = Array.from(wavePointers.current.values()).slice(
      0,
      2,
    );
    if (activePointers.length === 2) {
      const rect = (
        waveTrack.current ?? event.currentTarget
      ).getBoundingClientRect();
      const midpoint = pointerMidpoint(activePointers[0]!, activePointers[1]!);
      const anchorRatio = clampNumber(
        (midpoint.clientX - rect.left) / rect.width,
        0,
        1,
      );
      pinchZoom.current = {
        initialDistance: Math.max(
          1,
          pointerDistance(activePointers[0]!, activePointers[1]!),
        ),
        initialZoom: zoom,
        anchorTime: clampNumber(
          viewportStart + anchorRatio * visibleDuration,
          0,
          duration,
        ),
        anchorRatio,
      };
      drag.current = undefined;
      event.preventDefault();
      return;
    }
    drag.current = {
      type: "pan",
      pointerId: event.pointerId,
      startX: event.clientX,
      startViewport: viewportStart,
      moved: false,
      tapTime: timeFromPointer(event, event.currentTarget),
    };
  };
  const onWavePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (wavePointers.current.has(event.pointerId))
      wavePointers.current.set(event.pointerId, {
        clientX: event.clientX,
        clientY: event.clientY,
      });
    const pinch = pinchZoom.current;
    const activePointers = Array.from(wavePointers.current.values()).slice(
      0,
      2,
    );
    if (pinch && activePointers.length === 2) {
      const scale =
        pointerDistance(activePointers[0]!, activePointers[1]!) /
        pinch.initialDistance;
      zoomAt(pinch.initialZoom * scale, pinch.anchorTime, pinch.anchorRatio);
      event.preventDefault();
      return;
    }
    const currentDrag = drag.current;
    if (!currentDrag || currentDrag.pointerId !== event.pointerId) return;
    if (currentDrag.type !== "pan") {
      setHandleTime(
        currentDrag.type,
        timeFromPointer(event, event.currentTarget),
      );
      return;
    }
    const rect = (
      waveTrack.current ?? event.currentTarget
    ).getBoundingClientRect();
    const distance = event.clientX - currentDrag.startX;
    if (Math.abs(distance) > 4) currentDrag.moved = true;
    const seconds = (distance / rect.width) * visibleDuration;
    setViewportStart(
      clampNumber(
        currentDrag.startViewport - seconds,
        0,
        Math.max(0, duration - visibleDuration),
      ),
    );
  };
  const onWavePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    wavePointers.current.delete(event.pointerId);
    if (pinchZoom.current) {
      if (wavePointers.current.size < 2) pinchZoom.current = undefined;
      drag.current = undefined;
      return;
    }
    const currentDrag = drag.current;
    if (!currentDrag || currentDrag.pointerId !== event.pointerId) return;
    if (currentDrag.type === "pan" && !currentDrag.moved)
      seek(currentDrag.tapTime);
    drag.current = undefined;
  };
  const changeZoom = (next: number) => {
    if (!duration) return;
    zoomAt(next, viewportStart + visibleDuration / 2);
  };
  const panBy = (amount: number) => {
    if (!duration || !visibleDuration) return;
    setViewportStart((current) =>
      clampNumber(
        current + visibleDuration * amount,
        0,
        Math.max(0, duration - visibleDuration),
      ),
    );
  };
  const saveDraft = async (kind: PracticeEntryKind) => {
    if (!duration) return;
    const safeStart = roundTime(clampNumber(draftStart, 0, duration));
    const safeEnd =
      kind === "marker"
        ? safeStart
        : roundTime(
            clampNumber(
              Math.max(draftEnd, safeStart + minSelectionSeconds),
              safeStart + minSelectionSeconds,
              duration,
            ),
          );
    setSaving(true);
    try {
      const payload: Omit<PracticeSection, "id"> = {
        kind,
        name:
          draftName.trim() ||
          (kind === "marker"
            ? `Marker ${formatTime(safeStart)}`
            : `Loop ${formatTime(safeStart)}`),
        start: safeStart,
        end: safeEnd,
        color: kind === "marker" ? "#5eead4" : "#ef476f",
      };
      if (editing?.mode === "editing") {
        const updater =
          editing.source === "teacher" ? onUpdateCoach : onUpdatePersonal;
        if (!updater) return;
        await updater(editing.id, payload);
      } else {
        await onAdd(payload);
      }
      setDraftName("");
      setEditing(undefined);
      setComposerOpen(false);
    } finally {
      setSaving(false);
    }
  };
  const cancelDraft = () => {
    if (activeEntry?.id === "draft") {
      audio.current?.pause();
      setActiveEntry(undefined);
    }
    setEditing(undefined);
    setComposerOpen(false);
  };
  const onPlayerKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== " " || isEditableTarget(event.target)) return;
    event.preventDefault();
    togglePlayback();
  };
  const onEntryKeyDown =
    (section: PracticeSection) => (event: React.KeyboardEvent) => {
      if (event.key !== " ") return;
      event.preventDefault();
      toggleEntry(section);
    };
  const setMode = (kind: PracticeEntryKind) => {
    const nextStart = composerOpen
      ? draftStart
      : roundTime(clampNumber(progress, 0, duration || progress));
    const source: PracticeEntryScope = canEditCoach ? "teacher" : "personal";
    setEntryMode(kind);
    setComposerOpen(true);
    setEditing((current) => current ?? { mode: "creating", source });
    if (!composerOpen) setDraftStart(nextStart);
    const nextEnd =
      kind === "marker"
        ? nextStart
        : !composerOpen || draftEnd <= nextStart
          ? Math.min(duration || nextStart + 15, nextStart + 15)
          : draftEnd;
    if (kind === "marker") setDraftEnd(nextStart);
    else if (!composerOpen || draftEnd <= nextStart) setDraftEnd(nextEnd);
    fitDraftToViewport(kind, nextStart, nextEnd);
  };
  const selectionStart = draftStart;
  const selectionEnd = entryMode === "marker" ? draftStart : draftEnd;
  const selectionLeft = clampNumber(percentInViewport(selectionStart), 0, 100);
  const selectionRight = clampNumber(percentInViewport(selectionEnd), 0, 100);
  const markerInView = (section: PracticeSection) =>
    section.start >= viewportStart && section.start <= viewportEnd;
  const sectionIntersectsView = (section: PracticeSection) =>
    section.end >= viewportStart && section.start <= viewportEnd;
  const renderSectionGroup = (
    title: string,
    entries: PracticeSection[],
    source: PracticeEntryScope,
    editable: boolean,
  ) =>
    entries.length ? (
      <div className="section-group">
        <div className="section-group-head">
          <span>{title}</span>
          <small>
            {entries.length} {entries.length === 1 ? "entry" : "entries"}
          </small>
        </div>
        <div className="section-card-row">
          {[...entries]
            .sort((left, right) => {
              const leftKind = practiceEntryKind(left);
              const rightKind = practiceEntryKind(right);
              if (leftKind !== rightKind)
                return leftKind === "section" ? -1 : 1;
              return left.start - right.start;
            })
            .map((section) => {
              const kind = practiceEntryKind(section);
              return (
                <article
                  className={cx(
                    "section-card",
                    kind,
                    activeEntry?.id === section.id && "active",
                  )}
                  key={section.id}
                >
                  <button
                    type="button"
                    className="section-card-play"
                    onClick={() => toggleEntry(section)}
                    onKeyDown={onEntryKeyDown(section)}
                    aria-pressed={activeEntry?.id === section.id}
                  >
                    <span>
                      <b>{section.name}</b>
                      <small>
                        {kind === "marker" ? (
                          formatTime(section.start)
                        ) : (
                          <>
                            {formatTime(section.start)}-
                            {formatTime(section.end)} · Tap to loop
                          </>
                        )}
                      </small>
                    </span>
                    <MiniWaveform
                      wave={wave}
                      duration={duration}
                      start={section.start}
                      end={section.end}
                      kind={kind}
                      active={activeEntry?.id === section.id}
                    />
                  </button>
                  {activeEntry?.id === section.id && <RefreshCw />}
                  {editable && (
                    <button
                      type="button"
                      className="section-card-edit"
                      onClick={() => editEntry(section, source)}
                      aria-label={`Edit ${section.name}`}
                    >
                      <Pencil />
                    </button>
                  )}
                </article>
              );
            })}
        </div>
      </div>
    ) : null;
  const teacherSlices = teacherSections.filter(
    (section) => practiceEntryKind(section) === "section",
  );
  const personalSlices = personalSections.filter(
    (section) => practiceEntryKind(section) === "section",
  );
  const teacherMarkers = teacherSections.filter(
    (section) => practiceEntryKind(section) === "marker",
  );
  const personalMarkers = personalSections.filter(
    (section) => practiceEntryKind(section) === "marker",
  );
  return (
    <section
      className="player"
      ref={player}
      tabIndex={0}
      onKeyDown={onPlayerKeyDown}
    >
      <audio
        ref={audio}
        src={cacheableAudioUrl(media)}
        preload="metadata"
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
        onTimeUpdate={(event) => {
          const current = event.currentTarget.currentTime;
          setProgress(current);
          if (loopActive && current >= loopActive.end)
            event.currentTarget.currentTime = loopActive.start;
        }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      <div className="player-head">
        <span className="album-art">AV</span>
        <span>
          <small>Now practicing</small>
          <b>{media.title}</b>
          <em>{media.originalName}</em>
        </span>
        <button
          className="play-button"
          onClick={togglePlayback}
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause /> : <Play fill="currentColor" />}
        </button>
      </div>
      {markers.length > 0 && (
        <div className="marker-chips" aria-label="Song markers">
          {markers.map((marker) => (
            <button
              type="button"
              className={cx(activeEntry?.id === marker.id && "active")}
              onClick={() => selectEntry(marker)}
              onKeyDown={onEntryKeyDown(marker)}
              key={marker.id}
            >
              <MapPin />
              <span>{marker.name}</span>
              <small>{formatTime(marker.start)}</small>
            </button>
          ))}
        </div>
      )}
      <div className="waveform-shell">
        <div className="waveform-toolbar">
          <span>
            {formatTime(viewportStart)}-{formatTime(viewportEnd)}
          </span>
          <div>
            <button
              type="button"
              className="icon-button"
              onClick={() => panBy(-0.35)}
              aria-label="Pan earlier"
            >
              <ChevronLeft />
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={() => changeZoom(zoom / 1.75)}
              aria-label="Zoom out"
            >
              <ZoomOut />
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={() => changeZoom(zoom * 1.75)}
              aria-label="Zoom in"
            >
              <ZoomIn />
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={() => panBy(0.35)}
              aria-label="Pan later"
            >
              <ChevronRight />
            </button>
          </div>
        </div>
        <div
          className="waveform"
          onPointerDown={onWavePointerDown}
          onPointerMove={onWavePointerMove}
          onPointerUp={onWavePointerUp}
          onPointerCancel={onWavePointerUp}
          aria-label="Seek, pan, and edit song selection"
        >
          <div className="wave-track" ref={waveTrack}>
            <div className="waveform-bars">
              {renderedWave.map((height, index) => {
                const barTime =
                  viewportStart +
                  ((index + 0.5) / renderedWave.length) * visibleDuration;
                return (
                  <i
                    key={index}
                    className={
                      wave.length && barTime <= progress ? "played" : ""
                    }
                    style={{ height: `${height}%` }}
                  />
                );
              })}
              {!wave.length && <span>Decoding waveform...</span>}
            </div>
            {loopActive && sectionIntersectsView(loopActive) && (
              <span
                className="wave-section-band active"
                style={regionStyle(
                  loopActive.start,
                  loopActive.end,
                  viewportStart,
                  viewportEnd,
                )}
              />
            )}
            {markers.filter(markerInView).map((marker) => (
              <button
                type="button"
                className={cx(
                  "wave-marker",
                  activeEntry?.id === marker.id && "active",
                )}
                style={{ left: `${percentInViewport(marker.start)}%` }}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={() => selectEntry(marker)}
                aria-label={`Play marker ${marker.name}`}
                key={marker.id}
              >
                <MapPin />
              </button>
            ))}
            <span
              className="wave-playhead"
              style={{
                left: `${clampNumber(percentInViewport(progress), 0, 100)}%`,
              }}
            />
            {composerOpen && entryMode === "section" && (
              <span
                className="wave-selection"
                onPointerDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  fitDraftToViewport();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  fitDraftToViewport();
                }}
                style={{
                  left: `${selectionLeft}%`,
                  width: `${Math.max(1.5, selectionRight - selectionLeft)}%`,
                }}
              />
            )}
            {composerOpen &&
              entryMode === "section" &&
              draftStart >= viewportStart &&
              draftStart <= viewportEnd && (
                <button
                  type="button"
                  className="wave-handle start"
                  style={{ left: `${selectionLeft}%` }}
                  onPointerDown={(event) => beginHandleDrag("start", event)}
                  aria-label="Drag section start"
                />
              )}
            {composerOpen &&
              entryMode === "section" &&
              draftEnd >= viewportStart &&
              draftEnd <= viewportEnd && (
                <button
                  type="button"
                  className="wave-handle end"
                  style={{ left: `${selectionRight}%` }}
                  onPointerDown={(event) => beginHandleDrag("end", event)}
                  aria-label="Drag section end"
                />
              )}
            {composerOpen && entryMode === "marker" && (
              <button
                type="button"
                className="wave-pin-draft"
                style={{
                  left: `${clampNumber(percentInViewport(draftStart), 0, 100)}%`,
                }}
                onPointerDown={(event) => beginHandleDrag("marker", event)}
                aria-label="Drag marker"
              >
                <MapPin />
              </button>
            )}
          </div>
        </div>
      </div>
      {composerOpen && (
        <div className="selection-readout">
          <button
            type="button"
            className="selection-chip"
            onClick={() => fitDraftToViewport()}
          >
            {entryMode === "marker" ? (
              <>
                <MapPin /> {formatTime(draftStart)}
              </>
            ) : (
              <>
                <Scissors /> {formatTime(draftStart)}-{formatTime(draftEnd)}
              </>
            )}
          </button>
          <button
            type="button"
            className="selection-try"
            onClick={previewDraft}
          >
            {activeEntry?.id === "draft" && playing ? (
              <>
                <Pause /> Pause
              </>
            ) : (
              <>
                <Play fill="currentColor" />
                Try
              </>
            )}
          </button>
        </div>
      )}
      <div className="transport">
        <span>
          {formatTime(progress)} / {formatTime(duration)}
        </span>
        <TempoTape value={tempo} onChange={setTempo} />
      </div>
      {composerOpen ? (
        <form
          className="section-composer"
          onSubmit={(event) => {
            event.preventDefault();
            void saveDraft(entryMode);
          }}
        >
          <div className="composer-mode" role="tablist">
            <button
              type="button"
              className={entryMode === "section" ? "active" : ""}
              onClick={() => setMode("section")}
            >
              <Scissors /> Section
            </button>
            <button
              type="button"
              className={entryMode === "marker" ? "active" : ""}
              onClick={() => setMode("marker")}
            >
              <MapPin /> Marker
            </button>
          </div>
          <input
            value={draftName}
            onChange={(event) => setDraftName(event.target.value)}
            placeholder={
              entryMode === "marker" ? "Marker name" : "Section name"
            }
          />
          <button className="primary-button" type="submit" disabled={saving}>
            <Save />{" "}
            {saving
              ? "Saving"
              : editing?.mode === "editing"
                ? "Save changes"
                : entryMode === "marker"
                  ? "Save marker"
                  : addLabel}
          </button>
          <button
            type="button"
            className="icon-button"
            onClick={cancelDraft}
            aria-label="Cancel"
          >
            <X />
          </button>
        </form>
      ) : null}
      <div className="section-groups">
        {renderSectionGroup(
          "Coach sections",
          teacherSlices,
          "teacher",
          canEditCoach,
        )}
        {renderSectionGroup(
          "Your sections",
          personalSlices,
          "personal",
          canEditPersonal,
        )}
        {renderSectionGroup(
          "Coach markers",
          teacherMarkers,
          "teacher",
          canEditCoach,
        )}
        {renderSectionGroup(
          "Your markers",
          personalMarkers,
          "personal",
          canEditPersonal,
        )}
      </div>
      {!composerOpen ? (
        <div className="practice-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={() => setMode("section")}
          >
            <Scissors /> Add section
          </button>
          <button
            type="button"
            className="secondary-button"
            onClick={() => setMode("marker")}
          >
            <MapPin /> Add marker
          </button>
        </div>
      ) : null}
    </section>
  );
}

function RoutineForm({
  item,
  onClose,
  onSave,
}: {
  item?: Routine;
  onClose: () => void;
  onSave: (value: {
    id?: string;
    teamId: TeamId;
    name: string;
    tags: string[];
    notes: string;
  }) => Promise<void>;
}) {
  const [teamId, setTeamId] = useState<TeamId>(item?.teamId ?? "fuerza");
  const [name, setName] = useState(item?.name ?? "");
  const [tags, setTags] = useState(item?.tags.join(", ") ?? "");
  const [notes, setNotes] = useState(item?.notes ?? "");
  return (
    <Modal
      title={item ? "Edit routine details" : "Create a routine"}
      eyebrow="Routine library"
      onClose={onClose}
    >
      <form
        className="form-stack"
        onSubmit={async (event) => {
          event.preventDefault();
          await onSave({
            id: item?.id,
            teamId,
            name,
            tags: tags.split(","),
            notes,
          });
          onClose();
        }}
      >
        <label>
          <span>Team</span>
          <select
            value={teamId}
            onChange={(event) => setTeamId(event.target.value as TeamId)}
          >
            <option value="maxima">Maxima</option>
            <option value="fuerza">Fuerza</option>
            <option value="impulso">Impulso</option>
          </select>
        </label>
        <label>
          <span>Routine name</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </label>
        <label>
          <span>Tags, separated by commas</span>
          <input
            value={tags}
            onChange={(event) => setTags(event.target.value)}
            placeholder="Partnerwork, Shines, Open"
          />
        </label>
        <label>
          <span>Notes</span>
          <textarea
            rows={4}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </label>
        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary-button" type="submit">
            <Save /> Save routine
          </button>
        </div>
      </form>
    </Modal>
  );
}

function RoutineDetail({
  routine,
  data,
  reload,
  back,
  teacher,
}: {
  routine: Routine;
  data: BootstrapData;
  reload: () => Promise<void>;
  back: () => void;
  teacher: boolean;
}) {
  const song = data.media.find((asset) => asset.id === routine.songMediaId);
  const videos = routine.videoMediaIds
    .map((id) => data.media.find((asset) => asset.id === id))
    .filter((asset): asset is MediaAsset => Boolean(asset));
  const personal = data.personalSections.find(
    (item) => item.memberId === data.user.id && item.routineId === routine.id,
  )?.sections;
  return (
    <>
      <button className="back-button" onClick={back}>
        <ArrowLeft /> All routines
      </button>
      <PageHeader
        eyebrow={`${teamName(routine.teamId)} routine`}
        title={routine.name}
        copy={routine.notes || "Shared music, videos, and practice sections."}
        action={
          teacher ? (
            <div className="button-row">
              <MediaUploader
                target={{ type: "routine-song", routineId: routine.id }}
                accept="audio/*"
                label={song ? "Replace song" : "Upload song"}
                reload={reload}
              />
              <MediaUploader
                target={{ type: "routine-video", routineId: routine.id }}
                accept="video/*"
                label="Upload video"
                reload={reload}
              />
            </div>
          ) : undefined
        }
      />
      <div className="routine-tags">
        {routine.tags.map((tag) => (
          <Pill tone="violet" key={tag}>
            {tag}
          </Pill>
        ))}
      </div>
      <PracticePlayer
        media={song}
        teacherSections={routine.teacherSections}
        personalSections={personal}
        canEditCoach={teacher}
        canEditPersonal={!teacher}
        addLabel={teacher ? "Add coach recommendation" : "Add my section"}
        onAdd={async (section) => {
          await api(
            teacher
              ? `/api/teacher/routines/${routine.id}/sections`
              : `/api/routines/${routine.id}/personal-sections`,
            { method: "POST", body: JSON.stringify(section) },
          );
          await reload();
        }}
        onUpdateCoach={async (sectionId, section) => {
          await api(
            `/api/teacher/routines/${routine.id}/sections/${sectionId}`,
            { method: "PATCH", body: JSON.stringify(section) },
          );
          await reload();
        }}
        onUpdatePersonal={async (sectionId, section) => {
          await api(
            `/api/routines/${routine.id}/personal-sections/${sectionId}`,
            {
              method: "PATCH",
              body: JSON.stringify(section),
            },
          );
          await reload();
        }}
      />
      <section className="panel video-library">
        <header className="panel-head">
          <div>
            <span className="section-label">Coach media</span>
            <h2>Routine videos</h2>
          </div>
          {teacher && (
            <MediaUploader
              target={{ type: "routine-video", routineId: routine.id }}
              accept="video/*"
              label="Upload video"
              reload={reload}
            />
          )}
        </header>
        {videos.length ? (
          <div className="video-grid">
            {videos.map((video) => (
              <article key={video.id}>
                <video
                  controls
                  preload="metadata"
                  src={`/api/media/${video.id}`}
                />
                <b>{video.title}</b>
                <small>{video.originalName}</small>
              </article>
            ))}
          </div>
        ) : (
          <Empty
            title="No routine videos yet"
            copy={
              teacher
                ? "Upload the first teaching or performance video."
                : "Your coach has not uploaded a video yet."
            }
          />
        )}
      </section>
    </>
  );
}

function RoutinesPage({
  data,
  reload,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
}) {
  const [selectedId, setSelectedId] = useState<string>();
  const [editing, setEditing] = useState<Routine | "new">();
  const [deleting, setDeleting] = useState<Routine>();
  const [busy, setBusy] = useState(false);
  const selected = data.routines.find((routine) => routine.id === selectedId);
  if (selected)
    return (
      <RoutineDetail
        routine={selected}
        data={data}
        reload={reload}
        back={() => setSelectedId(undefined)}
        teacher={data.user.role === "teacher"}
      />
    );
  return (
    <>
      <PageHeader
        eyebrow={
          data.user.role === "teacher"
            ? "Coach routine library"
            : `${teamLabel(data.user.teamIds)} repertoire`
        }
        title="Music"
        copy={
          data.user.role === "teacher"
            ? "Create routines, upload the shared song and videos, and publish practice sections."
            : "Practice with the real team track and your coach's recommended sections."
        }
        action={
          data.user.role === "teacher" ? (
            <button
              className="primary-button"
              onClick={() => setEditing("new")}
            >
              <Plus /> Create routine
            </button>
          ) : undefined
        }
      />
      <div className="routine-grid">
        {data.routines.map((routine) => {
          const song = data.media.find(
            (asset) => asset.id === routine.songMediaId,
          );
          return (
            <article className="routine-card" key={routine.id}>
              <button
                className="routine-open"
                onClick={() => setSelectedId(routine.id)}
              >
                <span className={cx("routine-art", `team-${routine.teamId}`)}>
                  <Music2 />
                  <small>{teamName(routine.teamId)}</small>
                </span>
                <span>
                  <TeamBadge teamId={routine.teamId} />
                  <h2>{routine.name}</h2>
                  <p>{routine.tags.join(" · ") || "No tags"}</p>
                  <small>
                    {song
                      ? `Shared song · ${routine.videoMediaIds.length} videos`
                      : "Song not uploaded"}
                  </small>
                </span>
                <ChevronRight />
              </button>
              {data.user.role === "teacher" && (
                <span className="routine-card-actions">
                  <button
                    className="edit-routine"
                    onClick={() => setEditing(routine)}
                  >
                    <Pencil /> Edit details
                  </button>
                  <button
                    className="delete-routine"
                    onClick={() => setDeleting(routine)}
                    aria-label={`Delete ${routine.name}`}
                  >
                    <Trash2 /> Delete
                  </button>
                </span>
              )}
            </article>
          );
        })}
      </div>
      {editing && (
        <RoutineForm
          item={editing === "new" ? undefined : editing}
          onClose={() => setEditing(undefined)}
          onSave={async (value) => {
            await api("/api/teacher/routines", {
              method: "POST",
              body: JSON.stringify(value),
            });
            await reload();
          }}
        />
      )}
      {deleting && (
        <Modal
          title={`Delete ${deleting.name}?`}
          eyebrow="Routine library"
          onClose={() => !busy && setDeleting(undefined)}
        >
          <div className="remove-confirmation">
            <span className="remove-icon">
              <Trash2 />
            </span>
            <p>
              This removes the routine from the library and detaches it from
              events, casts, generated event-fee rows, and saved per-routine
              practice sections. Uploaded media records stay in storage.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={() => setDeleting(undefined)}
                disabled={busy}
              >
                Keep routine
              </button>
              <button
                type="button"
                className="destructive-button"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await api(`/api/teacher/routines/${deleting.id}`, {
                      method: "DELETE",
                    });
                    setDeleting(undefined);
                    await reload();
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <Trash2 /> {busy ? "Deleting" : "Delete routine"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function PeoplePage({
  data,
  reload,
  onEditCapabilities,
  sectionNav,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
  onEditCapabilities?: (member: Member) => void;
  sectionNav?: React.ReactNode;
}) {
  const [editing, setEditing] = useState<Member | "new">();
  const [confirming, setConfirming] = useState<Member>();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"approved" | "revoked">("approved");
  const [roleFilter, setRoleFilter] = useState<"all" | Member["role"]>("all");
  const [teamFilter, setTeamFilter] = useState<"all" | TeamId>("all");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const people = data.members
    .filter((member) =>
      status === "revoked"
        ? member.status === "revoked"
        : member.status !== "revoked",
    )
    .filter((member) => roleFilter === "all" || member.role === roleFilter)
    .filter(
      (member) => teamFilter === "all" || member.teamIds.includes(teamFilter),
    )
    .filter((member) =>
      `${member.name} ${member.email}`
        .toLowerCase()
        .includes(query.trim().toLowerCase()),
    )
    .sort(
      (a, b) =>
        Number(a.role === "student") - Number(b.role === "student") ||
        a.name.localeCompare(b.name),
    );
  const mutateStatus = async (member: Member, action: "remove" | "restore") => {
    setBusy(member.id);
    setError("");
    try {
      await api(
        action === "remove"
          ? `/api/teacher/members/${member.id}`
          : `/api/teacher/members/${member.id}/restore`,
        { method: action === "remove" ? "DELETE" : "POST", body: "{}" },
      );
      setConfirming(undefined);
      setEditing(undefined);
      await reload();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : `Could not ${action} access`,
      );
    } finally {
      setBusy("");
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="Membership"
        title="Team"
        copy="Manage who can enter Avante, what they can do, and which teams they belong to."
        action={
          <button className="primary-button" onClick={() => setEditing("new")}>
            <Plus /> Add person
          </button>
        }
      />
      {sectionNav}
      <section className="panel people-panel">
        <header className="people-toolbar">
          <label className="people-search">
            <Search />
            <span className="sr-only">Search people</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name or email"
            />
          </label>
          <div className="people-status" aria-label="Membership status">
            <button
              className={status === "approved" ? "active" : ""}
              onClick={() => setStatus("approved")}
            >
              Active
            </button>
            <button
              className={status === "revoked" ? "active" : ""}
              onClick={() => setStatus("revoked")}
            >
              Removed
            </button>
          </div>
          <div className="people-filters" aria-label="Filter people">
            {(["all", "student", "teacher"] as const).map((role) => (
              <button
                className={roleFilter === role ? "active" : ""}
                onClick={() => setRoleFilter(role)}
                key={role}
              >
                {role === "all"
                  ? "Everyone"
                  : role === "student"
                    ? "Members"
                    : "Coaches"}
              </button>
            ))}
            {(["maxima", "fuerza", "impulso"] as const).map((teamId) => (
              <button
                className={teamFilter === teamId ? "active" : ""}
                onClick={() =>
                  setTeamFilter(teamFilter === teamId ? "all" : teamId)
                }
                key={teamId}
              >
                {teamName(teamId)}
              </button>
            ))}
          </div>
          <small>{people.length} people</small>
        </header>
        {error && <div className="people-error">{error}</div>}
        <div className="people-table" role="table" aria-label="Avante people">
          <div className="people-row people-head" role="row">
            <span role="columnheader">Person</span>
            <span role="columnheader">Role</span>
            <span role="columnheader">Teams</span>
            <span role="columnheader">Birthday</span>
            <span role="columnheader">Status and actions</span>
          </div>
          {people.length === 0 && (
            <Empty
              title={status === "revoked" ? "No removed people" : "No matches"}
              copy={
                query || roleFilter !== "all" || teamFilter !== "all"
                  ? "Try changing your search or filters."
                  : "Add someone when they are ready to join Avante."
              }
            />
          )}
          {renderGroupedMembers(people, (member) => (
            <div className="people-row" role="row" key={member.id}>
              <span className="person-cell" role="cell">
                <Avatar member={member} />
                <span>
                  <b>{member.name}</b>
                  <small>{member.email}</small>
                </span>
              </span>
              <span className="people-role" role="cell">
                {member.role === "teacher" ? "Coach" : "Member"}
              </span>
              <span role="cell">
                <TeamBadges teamIds={member.teamIds} />
              </span>
              <span className="people-birthday" role="cell">
                {formatBirthday(member.birthday) || "Not entered"}
              </span>
              <span className="people-access" role="cell">
                <Pill tone={member.status === "approved" ? "sage" : "neutral"}>
                  {member.status === "approved"
                    ? "Active"
                    : member.status === "revoked"
                      ? "Removed"
                      : member.status}
                </Pill>
                {member.status !== "revoked" ? (
                  <span className="people-actions">
                    {onEditCapabilities && (
                      <button
                        className="icon-button"
                        onClick={() => onEditCapabilities(member)}
                        aria-label={`Manage skills and slots for ${member.name}`}
                        title="Manage skills and slots"
                      >
                        <Sparkles />
                      </button>
                    )}
                    <button
                      className="icon-button"
                      onClick={() => setEditing(member)}
                      aria-label={`Edit ${member.name}`}
                    >
                      <Pencil />
                    </button>
                    <button
                      className="icon-button danger-button"
                      onClick={() => setConfirming(member)}
                      disabled={member.id === data.user.id}
                      aria-label={
                        member.id === data.user.id
                          ? "You cannot remove yourself"
                          : `Remove ${member.name}`
                      }
                    >
                      <Trash2 />
                    </button>
                  </span>
                ) : (
                  <button
                    className="secondary-button compact-button"
                    onClick={() => void mutateStatus(member, "restore")}
                    disabled={busy === member.id}
                  >
                    <RotateCcw /> {busy === member.id ? "Restoring" : "Restore"}
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      </section>
      {editing && (
        <MemberForm
          item={editing === "new" ? undefined : editing}
          onClose={() => setEditing(undefined)}
          onRemove={
            editing !== "new" && editing.id !== data.user.id
              ? () => {
                  setEditing(undefined);
                  setConfirming(editing);
                }
              : undefined
          }
          onSave={async () => {
            await reload();
            setEditing(undefined);
          }}
        />
      )}
      {confirming && (
        <Modal
          title={`Remove ${confirming.name}?`}
          eyebrow="Remove access"
          onClose={() => !busy && setConfirming(undefined)}
        >
          <div className="remove-confirmation">
            <span className="remove-icon">
              <Trash2 />
            </span>
            <p>
              They will be signed out, their private calendar link will stop
              working, and they will disappear from Active people. Historical
              casts and payment records will be kept.
            </p>
            {error && <div className="auth-message">{error}</div>}
            <div className="modal-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={() => setConfirming(undefined)}
                disabled={Boolean(busy)}
              >
                Keep access
              </button>
              <button
                type="button"
                className="destructive-button"
                onClick={() => void mutateStatus(confirming, "remove")}
                disabled={Boolean(busy)}
              >
                <Trash2 /> {busy ? "Removing" : "Remove access"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function MemberForm({
  item,
  onClose,
  onSave,
  onRemove,
}: {
  item?: Member;
  onClose: () => void;
  onSave: () => Promise<void>;
  onRemove?: () => void;
}) {
  const [name, setName] = useState(item?.name ?? "");
  const [email, setEmail] = useState(item?.email ?? "");
  const [role, setRole] = useState<Member["role"]>(item?.role ?? "student");
  const [teamIds, setTeamIds] = useState<TeamId[]>(item?.teamIds ?? ["fuerza"]);
  const [birthday, setBirthday] = useState(item?.birthday ?? "");
  const [phone, setPhone] = useState(item?.phone ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const toggleTeam = (teamId: TeamId) =>
    setTeamIds((current) =>
      current.includes(teamId)
        ? current.filter((value) => value !== teamId)
        : (["maxima", "fuerza", "impulso"] as TeamId[]).filter(
            (value) => value === teamId || current.includes(value),
          ),
    );
  return (
    <Modal
      title={item ? "Edit person" : "Add a person"}
      eyebrow="Membership"
      onClose={onClose}
      wide
    >
      <form
        className="member-form"
        onSubmit={async (event) => {
          event.preventDefault();
          if (role === "student" && teamIds.length === 0) {
            setError("Members need at least one team.");
            return;
          }
          setBusy(true);
          setError("");
          try {
            await api(
              item ? `/api/teacher/members/${item.id}` : "/api/teacher/members",
              {
                method: item ? "PATCH" : "POST",
                body: JSON.stringify({
                  name,
                  email,
                  role,
                  teamIds,
                  birthday: birthday || undefined,
                  phone: phone || undefined,
                }),
              },
            );
            await onSave();
          } catch (cause) {
            setError(
              cause instanceof Error ? cause.message : "Could not save access",
            );
          } finally {
            setBusy(false);
          }
        }}
      >
        <section className="member-form-section">
          <header>
            <span>01</span>
            <div>
              <b>Identity</b>
              <small>The exact Google account this person will use.</small>
            </div>
          </header>
          <div className="member-identity-grid">
            <label>
              <span>Name</span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Full name"
                required
              />
            </label>
            <label>
              <span>Exact Google email</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@gmail.com"
                required
              />
            </label>
          </div>
        </section>
        <fieldset className="member-form-section">
          <legend>
            <span>02</span>
            <span>
              <b>Access</b>
              <small>Choose what this person can do in Avante.</small>
            </span>
          </legend>
          <div className="role-choice-grid">
            {(["student", "teacher"] as const).map((value) => (
              <label
                className={cx("choice-card", role === value && "selected")}
                key={value}
              >
                <input
                  type="radio"
                  name="role"
                  value={value}
                  checked={role === value}
                  onChange={() => setRole(value)}
                />
                <span className="choice-icon">
                  {value === "student" ? <Users /> : <ShieldCheck />}
                </span>
                <span>
                  <b>{value === "student" ? "Member" : "Coach"}</b>
                  <small>
                    {value === "student"
                      ? "Sees their teams, casts, routines, calendar, and payments."
                      : "Can manage people, schedules, routines, and payments."}
                  </small>
                </span>
                <span className="choice-check">
                  <Check />
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="member-form-section">
          <legend>
            <span>03</span>
            <span>
              <b>Teams</b>
              <small>Select one team or both.</small>
            </span>
          </legend>
          <div className="team-choice-grid">
            {(["maxima", "fuerza", "impulso"] as const).map((teamId) => (
              <label
                className={cx(
                  "team-choice",
                  `team-choice-${teamId}`,
                  teamIds.includes(teamId) && "selected",
                )}
                key={teamId}
              >
                <input
                  type="checkbox"
                  checked={teamIds.includes(teamId)}
                  onChange={() => toggleTeam(teamId)}
                />
                <span className="team-choice-mark">
                  <Check />
                </span>
                <span>
                  <b>{teamName(teamId)}</b>
                  <small>{`${teamName(teamId)} performance team`}</small>
                </span>
              </label>
            ))}
          </div>
          <small className="team-selection-summary">
            {teamIds.length > 0
              ? `${teamLabel(teamIds)} selected`
              : role === "student"
                ? "Choose at least one team"
                : "No team selected"}
          </small>
        </fieldset>
        <section className="member-form-section">
          <header>
            <span>04</span>
            <div>
              <b>Personal details</b>
              <small>Visible to coaches and editable by this person.</small>
            </div>
          </header>
          <div className="member-identity-grid">
            <div className="birthday-field">
              <span>Birthday</span>
              <BirthdaySelect value={birthday} onChange={setBirthday} />
            </div>
            <label>
              <span>Phone</span>
              <input
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                placeholder="Optional"
              />
            </label>
          </div>
        </section>
        <div className="membership-note">
          <ShieldCheck />
          <span>
            Only this exact Google account can enter. There is no separate
            invitation to accept.
            {item && " Changing email or access level signs out old sessions."}
          </span>
        </div>
        {error && <div className="auth-message">{error}</div>}
        {item && onRemove && (
          <div className="member-danger-zone">
            <span>
              <b>Remove access</b>
              <small>
                Keep historical casts and payments, but sign them out.
              </small>
            </span>
            <button type="button" onClick={onRemove}>
              <Trash2 /> Remove access
            </button>
          </div>
        )}
        <div className="modal-actions member-form-actions">
          <button type="button" className="secondary-button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary-button" type="submit" disabled={busy}>
            <Check />
            {busy
              ? "Saving"
              : item
                ? "Save changes"
                : role === "teacher"
                  ? "Add coach"
                  : "Add member"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

type PaymentDraft = Omit<PaymentRecord, "id" | "updatedAt" | "markedAt">;

const paymentCategoryNames: Record<PaymentCategory, string> = {
  "team-fee": "Team Fees",
  costume: "Costume",
  merch: "Merch",
  "competition-fee": "Competition Fees",
  "team-event": "Team Events",
  other: "Other",
};
const paymentCategoryName = (payment: PaymentRecord) =>
  payment.category
    ? paymentCategoryNames[payment.category]
    : payment.kind === "monthly"
      ? "Team Fees"
      : "Other";
const isTeamFeePayment = (payment: PaymentRecord) =>
  payment.category === "team-fee" ||
  (!payment.category && payment.kind === "monthly");
const paymentMethodNames: Record<PaymentMethod, string> = {
  cash: "Cash",
  "e-transfer": "E-transfer",
};
const paymentAmountCents = (payment: PaymentRecord) =>
  payment.amountCents ?? Math.round((payment.amount ?? 0) * 100);
const paymentHstCents = (payment: PaymentRecord) =>
  payment.details?.type === "team-fee" && payment.details.hstAdded
    ? (payment.details.hstCents ?? 0)
    : 0;
const paymentSubtotalCents = (payment: PaymentRecord) =>
  payment.details?.type === "team-fee" && payment.details.hstAdded
    ? (payment.details.taxableSubtotalCents ??
      paymentAmountCents(payment) - paymentHstCents(payment))
    : paymentAmountCents(payment);
const paymentClassPassCents = (payment: PaymentRecord) =>
  payment.details?.type === "team-fee" && payment.details.classPassAdded
    ? (payment.details.classPassCents ?? 0)
    : 0;
const paymentBaseTeamFeeCents = (payment: PaymentRecord) =>
  payment.details?.type === "team-fee"
    ? payment.details.baseFeeCents
    : paymentSubtotalCents(payment) - paymentClassPassCents(payment);
const paymentPaidCents = (payment: PaymentRecord, receipts: PaymentReceipt[]) =>
  receipts
    .filter((receipt) => receipt.paymentId === payment.id)
    .reduce((total, receipt) => total + receipt.amountCents, 0) ||
  payment.paidCents ||
  0;
const paymentRemainingCents = (
  payment: PaymentRecord,
  receipts: PaymentReceipt[],
) =>
  payment.status === "waived"
    ? 0
    : Math.max(
        0,
        paymentTotalForMethod(payment, "e-transfer") -
          paymentPaidCents(payment, receipts),
      );

function PaymentHstLine({ payment }: { payment: PaymentRecord }) {
  const quote = paymentQuote(payment);
  if (!quote.hstCents) return null;
  return (
    <small className="payment-hst-line">
      {quote.conditional ? "E-transfer · " : ""}
      {money(quote.baseCents / 100)} + HST (13%) {money(quote.hstCents / 100)}
    </small>
  );
}

function PaymentForm({
  item,
  data,
  onClose,
  onSave,
  onDelete,
}: {
  item?: PaymentRecord;
  data: BootstrapData;
  onClose: () => void;
  onSave: (value: PaymentDraft) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const students = data.members
    .filter(
      (member) => member.role === "student" && member.status !== "revoked",
    )
    .sort(compareMembers);
  const [memberId, setMemberId] = useState(
    item?.memberId ?? students[0]?.id ?? "",
  );
  const [kind, setKind] = useState<PaymentRecord["kind"]>(
    item?.kind ?? "monthly",
  );
  const [label, setLabel] = useState(item?.label ?? "");
  const [month, setMonth] = useState(
    item?.month ?? new Date().toISOString().slice(0, 7),
  );
  const [eventId, setEventId] = useState(item?.eventId ?? "");
  const [amount, setAmount] = useState(
    item?.amount == null ? "" : String(item.amount),
  );
  const [dueDate, setDueDate] = useState(item?.dueDate ?? "");
  const [status, setStatus] = useState<PaymentRecord["status"]>(
    item?.status ?? "unrecorded",
  );
  const [note, setNote] = useState(item?.note ?? "");
  const [payee, setPayee] = useState<PaymentRecord["payee"]>(item?.payee);
  const [cashOnly, setCashOnly] = useState(Boolean(item?.cashOnly));
  const [electronicHst, setElectronicHst] = useState(
    item?.electronicHst ?? true,
  );
  const lockedPricing = Boolean(
    item &&
    ((item.paidCents ?? 0) > 0 ||
      item.status === "paid" ||
      item.settlementPricing ||
      (item.details?.type === "team-fee" && item.details.hstAdded)),
  );
  const originalPass =
    item?.details?.type === "team-fee" && item.details.classPassAdded
      ? (item.details.classPassCents ?? 0)
      : null;
  const [passEnabled, setPassEnabled] = useState(originalPass !== null);
  const [passAmount, setPassAmount] = useState(
    String((originalPass ?? 5000) / 100),
  );
  const passChanged =
    (passEnabled ? Math.round(Number(passAmount) * 100) : null) !==
    originalPass;
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [error, setError] = useState("");
  if (confirmDelete && item && onDelete)
    return (
      <ConfirmDialog
        title={`Delete ${item.label}?`}
        copy="This permanently removes the payment entry, its receipt history, and related in-app notifications from the Member’s private ledger. Previously delivered push notifications cannot be recalled. This cannot be undone."
        confirmLabel="Delete payment"
        destructive
        busy={deleteBusy}
        onClose={() => setConfirmDelete(false)}
        onConfirm={async () => {
          setDeleteBusy(true);
          setError("");
          try {
            await onDelete();
            onClose();
          } catch (cause) {
            setError(
              cause instanceof Error
                ? cause.message
                : "Could not delete payment",
            );
            setConfirmDelete(false);
          } finally {
            setDeleteBusy(false);
          }
        }}
      />
    );
  return (
    <Modal
      title={item ? "Edit payment" : "Add payment"}
      eyebrow="Coach ledger"
      onClose={() => !busy && !deleteBusy && onClose()}
      wide
    >
      <form
        className="payment-form"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!memberId) {
            setError("Choose a member.");
            return;
          }
          setBusy(true);
          setError("");
          try {
            const revised =
              item &&
              isTeamFeePayment(item) &&
              !lockedPricing &&
              (passChanged ||
                Number(amount) !==
                  (item.amount ?? (item.amountCents ?? 0) / 100))
                ? withClassPass(
                    {
                      ...item,
                      amount: Number(amount),
                      amountCents: Math.round(Number(amount) * 100),
                    },
                    passEnabled ? Math.round(Number(passAmount) * 100) : null,
                  )
                : undefined;
            await onSave({
              memberId,
              note,
              payee,
              cashOnly,
              electronicHst,
              kind,
              label,
              month: kind === "monthly" ? month || undefined : undefined,
              eventId: kind === "event" ? eventId || undefined : undefined,
              amount: amount ? Number(amount) : undefined,
              dueDate: dueDate || undefined,
              status,
              ...(revised
                ? {
                    amount: revised.amount,
                    amountCents: revised.amountCents,
                    category: revised.category,
                    details: revised.details,
                  }
                : {}),
            });
            onClose();
          } catch (cause) {
            setError(
              cause instanceof Error ? cause.message : "Could not save payment",
            );
          } finally {
            setBusy(false);
          }
        }}
      >
        <section className="member-form-section">
          <header>
            <span>01</span>
            <div>
              <b>Payment owner</b>
              <small>Members only see their own ledger rows.</small>
            </div>
          </header>
          <div className="payment-student-grid">
            {renderGroupedMembers(students, (student) => (
              <label
                className={cx(
                  "payment-student-choice",
                  memberId === student.id && "selected",
                )}
                key={student.id}
              >
                <input
                  type="radio"
                  name="payment-member"
                  checked={memberId === student.id}
                  onChange={() => setMemberId(student.id)}
                />
                <Avatar member={student} />
                <span>
                  <b>{student.name}</b>
                  <small>{teamLabel(student.teamIds)}</small>
                </span>
                <Check />
              </label>
            ))}
          </div>
        </section>
        <section className="member-form-section">
          <header>
            <span>02</span>
            <div>
              <b>Ledger details</b>
              <small>Amount and due date stay private to this member.</small>
            </div>
          </header>
          <div className="field-grid field-grid-3">
            <label className="field-wide" htmlFor="payment-label">
              <span>Label</span>
              <input
                id="payment-label"
                aria-label="Label"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="August team fee"
                required
              />
            </label>
            <label htmlFor="payment-status">
              <span>Status</span>
              <select
                id="payment-status"
                aria-label="Status"
                value={status}
                onChange={(event) =>
                  setStatus(event.target.value as PaymentRecord["status"])
                }
              >
                <option value="unrecorded">Not reviewed</option>
                <option value="due">Due</option>
                {status === "paid" && <option value="paid">Paid</option>}
                <option value="waived">Waived</option>
              </select>
            </label>
            <label htmlFor="payment-kind">
              <span>Kind</span>
              <select
                id="payment-kind"
                aria-label="Kind"
                value={kind}
                onChange={(event) =>
                  setKind(event.target.value as PaymentRecord["kind"])
                }
              >
                <option value="monthly">Monthly</option>
                <option value="event">Event</option>
              </select>
            </label>
            <label
              htmlFor={kind === "monthly" ? "payment-month" : "payment-event"}
            >
              <span>{kind === "monthly" ? "Month" : "Event"}</span>
              {kind === "monthly" ? (
                <input
                  id="payment-month"
                  aria-label="Month"
                  type="month"
                  value={month}
                  onChange={(event) => setMonth(event.target.value)}
                />
              ) : (
                <select
                  id="payment-event"
                  aria-label="Event"
                  value={eventId}
                  onChange={(event) => setEventId(event.target.value)}
                >
                  <option value="">No event</option>
                  {data.events.map((event) => (
                    <option value={event.id} key={event.id}>
                      {event.title}
                    </option>
                  ))}
                </select>
              )}
            </label>
            <label htmlFor="payment-amount">
              <span>
                {item?.details?.type === "team-fee" && item.details.hstAdded
                  ? "Total including HST"
                  : "Amount"}
              </span>
              <input
                id="payment-amount"
                aria-label="Amount"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="Optional"
                readOnly={Boolean(
                  item?.settlementPricing ||
                  (item?.details?.type === "team-fee" && item.details.hstAdded),
                )}
              />
              {item?.details?.type === "team-fee" && item.details.hstAdded && (
                <small>
                  Team fee {money(paymentBaseTeamFeeCents(item) / 100)}
                  {paymentClassPassCents(item) > 0
                    ? ` + class pass ${money(paymentClassPassCents(item) / 100)}`
                    : ""}{" "}
                  + HST {money(paymentHstCents(item) / 100)}
                </small>
              )}
            </label>
            <label htmlFor="payment-due-date">
              <span>Due date</span>
              <input
                id="payment-due-date"
                aria-label="Due date"
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
              />
            </label>
          </div>
        </section>
        {item && isTeamFeePayment(item) && (
          <section className="payment-instructions-editor">
            <button
              type="button"
              role="switch"
              aria-label="Monthly class pass"
              aria-checked={passEnabled}
              disabled={lockedPricing}
              className="payment-tax-switch"
              onClick={() => setPassEnabled(!passEnabled)}
            >
              <span>
                <b>Monthly class pass</b>
                <small>Add the optional Monday class pass to this fee.</small>
              </span>
              <span className="switch-track">
                <span />
              </span>
            </button>
            {passEnabled && (
              <label>
                <span>Class pass amount (CAD)</span>
                <input
                  aria-label="Class pass amount (CAD)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={passAmount}
                  disabled={lockedPricing}
                  onChange={(event) => setPassAmount(event.target.value)}
                />
              </label>
            )}
            {!lockedPricing && (
              <p>
                Subtotal:{" "}
                {money(
                  (Number(amount) * 100 -
                    (originalPass ?? 0) +
                    (passEnabled ? Number(passAmount) * 100 : 0)) /
                    100,
                )}{" "}
                before any enabled HST.
              </p>
            )}
            {lockedPricing && (
              <small>
                Recorded payments stay unchanged. Use a separate adjustment for
                a class pass.
              </small>
            )}
          </section>
        )}
        <PaymentInstructionsEditor
          payees={data.paymentPayees ?? []}
          payee={payee}
          onPayee={setPayee}
          cashOnly={cashOnly}
          onCashOnly={setCashOnly}
          electronicHst={electronicHst}
          onElectronicHst={setElectronicHst}
          locked={lockedPricing}
        />
        <label className="payment-note-field">
          <span>Note for the Member</span>
          <textarea
            aria-label="Note for the Member"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={5000}
            rows={4}
            placeholder="Payment instructions, who to send it to, or anything the Member should know…"
          />
          <small>
            Visible on this Member’s payment. Line breaks are preserved.
          </small>
        </label>
        {error && <div className="auth-message">{error}</div>}
        {item && onDelete && (
          <div className="member-danger-zone">
            <span>
              <b>Delete payment</b>
              <small>
                Remove this entry, its receipts, and related reminders.
              </small>
            </span>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={busy || deleteBusy}
            >
              <Trash2 /> Delete entry
            </button>
          </div>
        )}
        <div className="modal-actions member-form-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button className="primary-button" type="submit" disabled={busy}>
            <Save /> {busy ? "Saving" : item ? "Save payment" : "Add payment"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function BatchPaymentForm({
  data,
  onClose,
  reload,
}: {
  data: BootstrapData;
  onClose: () => void;
  reload: () => Promise<void>;
}) {
  const members = data.members
    .filter(
      (member) => member.role === "student" && member.status === "approved",
    )
    .sort((a, b) => a.name.localeCompare(b.name));
  const [step, setStep] = useState<"details" | "members" | "review">("details");
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [memberQuery, setMemberQuery] = useState("");
  const [teamFilter, setTeamFilter] = useState<"all" | TeamId>("all");
  const [category, setCategory] = useState<PaymentCategory>("team-fee");
  const [status, setStatus] = useState<PaymentRecord["status"]>("paid");
  const [amount, setAmount] = useState("130");
  const [month, setMonth] = useState(businessDate().slice(0, 7));
  const [classPass, setClassPass] = useState(false);
  const [classPassAmount, setClassPassAmount] = useState("50");
  const [addHst, setAddHst] = useState(true);
  const [cashOnly, setCashOnly] = useState(false);
  const [payee, setPayee] = useState<PaymentRecord["payee"]>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | "">("");
  const [season, setSeason] = useState("1");
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");
  const [paidOn, setPaidOn] = useState(isoDate(new Date()));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const paymentMethodRef = useRef<HTMLSelectElement>(null);
  const categories = Object.entries(paymentCategoryNames) as Array<
    [PaymentCategory, string]
  >;
  const cents = (value: string) => Math.round(Number(value || 0) * 100);
  const feeSubtotalCents =
    category === "team-fee"
      ? cents(amount) + (classPass ? cents(classPassAmount) : 0)
      : cents(amount);
  const hstApplied =
    !cashOnly &&
    status !== "waived" &&
    addHst &&
    (status === "due" || paymentMethod === "e-transfer");
  const hstCents = hstApplied ? calculateHstCents(feeSubtotalCents) : 0;
  const totalCents = feeSubtotalCents + hstCents;
  const details: PaymentRecord["details"] =
    category === "team-fee"
      ? {
          type: "team-fee",
          month,
          baseFeeCents: cents(amount),
          classPassAdded: classPass,
          classPassCents: classPass ? cents(classPassAmount) : undefined,
          hstAdded: false,
        }
      : category === "costume"
        ? { type: "costume", season: Number(season) as 1 | 2 | 3 | 4 | 5 | 6 }
        : category === "competition-fee"
          ? {
              type: "competition-fee",
              competitionName: label || "CSBC 2026",
              competitionEventId: data.events.find(
                (event) => event.title === label,
              )?.id,
            }
          : category === "team-event"
            ? {
                type: "team-event",
                eventName: label || "Team event",
                eventId: data.events.find((event) => event.title === label)?.id,
              }
            : category === "merch"
              ? {
                  type: "merch",
                  orderStatus: "delivered",
                  grossCents: cents(amount),
                  creditAppliedCents: 0,
                  paidCents: cents(amount),
                }
              : {
                  type: "other",
                  label: label || "Other",
                  description: note || undefined,
                };
  const visibleMembers = members.filter(
    (member) =>
      (teamFilter === "all" || member.teamIds.includes(teamFilter)) &&
      member.name.toLowerCase().includes(memberQuery.trim().toLowerCase()),
  );
  const actionLabel =
    status === "paid"
      ? "Record payment"
      : status === "due"
        ? "Create due item"
        : "Waive item";
  return (
    <Modal title={actionLabel} onClose={onClose} wide>
      <form
        className="batch-payment-form"
        onSubmit={async (event) => {
          event.preventDefault();
          if (step !== "review") return;
          setBusy(true);
          setError("");
          try {
            await api("/api/teacher/payments/batch", {
              method: "POST",
              body: JSON.stringify({
                memberIds,
                payment: {
                  kind: category === "team-fee" ? "monthly" : "event",
                  category,
                  label:
                    label ||
                    categories.find(([id]) => id === category)?.[1] ||
                    "Payment",
                  month: category === "team-fee" ? month : undefined,
                  amountCents: feeSubtotalCents,
                  amount: feeSubtotalCents / 100,
                  electronicHst: addHst,
                  cashOnly,
                  payee,
                  currency: "CAD",
                  dueDate: status === "due" ? isoDate(new Date()) : undefined,
                  status,
                  paidOn: status === "paid" ? paidOn : undefined,
                  paymentMethod: status === "paid" ? paymentMethod : undefined,
                  note: note || undefined,
                  details,
                },
              }),
            });
            await reload();
            onClose();
          } catch (cause) {
            setError(
              cause instanceof Error
                ? cause.message
                : "Could not save payments",
            );
          } finally {
            setBusy(false);
          }
        }}
      >
        <ol className="form-steps" aria-label="Payment workflow">
          <li className={step === "details" ? "active" : "complete"}>
            1. Details
          </li>
          <li
            className={
              step === "members"
                ? "active"
                : step === "review"
                  ? "complete"
                  : ""
            }
          >
            2. Members
          </li>
          <li className={step === "review" ? "active" : ""}>3. Review</li>
        </ol>
        {step === "details" && (
          <section className="form-step form-stack">
            <div className="field-grid">
              <label>
                <span>Purpose</span>
                <select
                  value={status}
                  onChange={(event) =>
                    setStatus(event.target.value as PaymentRecord["status"])
                  }
                >
                  <option value="paid">Record payment received</option>
                  <option value="due">Create amount due</option>
                  <option value="waived">Waive an item</option>
                </select>
              </label>
              <label>
                <span>Category</span>
                <select
                  value={category}
                  onChange={(event) => {
                    const next = event.target.value as PaymentCategory;
                    setCategory(next);
                    setAmount(next === "team-fee" ? "130" : "");
                  }}
                >
                  {categories.map(([id, name]) => (
                    <option value={id} key={id}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              {status !== "waived" && (
                <label>
                  <span>
                    {category === "team-fee" ? "Monthly fee" : "Amount (CAD)"}
                  </span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    required
                  />
                </label>
              )}
              {status === "paid" && (
                <>
                  <label>
                    <span>Paid on</span>
                    <input
                      type="date"
                      value={paidOn}
                      onChange={(event) => setPaidOn(event.target.value)}
                      required
                    />
                  </label>
                  <label>
                    <span>Payment method</span>
                    <select
                      ref={paymentMethodRef}
                      value={paymentMethod}
                      onChange={(event) => {
                        setPaymentMethod(event.target.value as PaymentMethod);
                        setError("");
                      }}
                      aria-invalid={
                        error === "Choose Cash or E-transfer before continuing."
                      }
                      required
                    >
                      <option value="">Choose method</option>
                      <option value="cash">Cash</option>
                      <option value="e-transfer">E-transfer</option>
                    </select>
                    <small>Required to record a received payment.</small>
                  </label>
                </>
              )}
              {category === "team-fee" && (
                <>
                  <label>
                    <span>Payment month</span>
                    <input
                      type="month"
                      value={month}
                      onChange={(event) => setMonth(event.target.value)}
                      required
                    />
                  </label>
                  <label className="checkbox-field">
                    <input
                      type="checkbox"
                      checked={classPass}
                      onChange={(event) => setClassPass(event.target.checked)}
                    />
                    <span>Monthly class pass added</span>
                  </label>
                  {classPass && (
                    <label>
                      <span>Class pass amount</span>
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={classPassAmount}
                        onChange={(event) =>
                          setClassPassAmount(event.target.value)
                        }
                        required
                      />
                    </label>
                  )}
                  {status !== "waived" && (
                    <>
                      <dl
                        className="payment-tax-breakdown field-wide"
                        aria-label="Team Fee total"
                      >
                        <div>
                          <dt>
                            {category === "team-fee"
                              ? "Team fee"
                              : "Base amount"}
                          </dt>
                          <dd>{money(cents(amount) / 100)}</dd>
                        </div>
                        {classPass && (
                          <div>
                            <dt>Monthly class pass</dt>
                            <dd>{money(cents(classPassAmount) / 100)}</dd>
                          </div>
                        )}
                        <div>
                          <dt>HST (13%)</dt>
                          <dd>{money(hstCents / 100)}</dd>
                        </div>
                        <div className="total">
                          <dt>Total owing</dt>
                          <dd>{money(totalCents / 100)}</dd>
                        </div>
                      </dl>
                    </>
                  )}
                </>
              )}
              {category === "costume" && (
                <label>
                  <span>Season</span>
                  <select
                    value={season}
                    onChange={(event) => setSeason(event.target.value)}
                  >
                    {[1, 2, 3, 4, 5, 6].map((value) => (
                      <option value={value} key={value}>
                        Season {value}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {category === "competition-fee" && (
                <label>
                  <span>Competition</span>
                  <input
                    list="competition-options"
                    value={label}
                    onChange={(event) => setLabel(event.target.value)}
                    placeholder="CSBC 2026"
                    required
                  />
                  <datalist id="competition-options">
                    <option value="CSBC 2026" />
                    {data.competitionOptions.map((option) => (
                      <option value={option.name} key={option.id} />
                    ))}
                    {data.events
                      .filter((event) => event.type === "competition")
                      .map((event) => (
                        <option value={event.title} key={event.id} />
                      ))}
                    <option value="Other" />
                  </datalist>
                  <small>New names are saved for future entries.</small>
                </label>
              )}
              {(category === "other" ||
                category === "merch" ||
                category === "team-event") && (
                <label>
                  <span>Details</span>
                  <input
                    value={label}
                    onChange={(event) => setLabel(event.target.value)}
                    required
                  />
                </label>
              )}
            </div>
            <PaymentInstructionsEditor
              payees={data.paymentPayees ?? []}
              payee={payee}
              onPayee={setPayee}
              cashOnly={cashOnly}
              onCashOnly={(cash) => {
                setCashOnly(cash);
                if (cash) setPaymentMethod("cash");
              }}
              electronicHst={addHst}
              onElectronicHst={setAddHst}
            />
            <label className="payment-note-field">
              <span>Note for the Member</span>
              <textarea
                aria-label="Note for the Member"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                rows={4}
                maxLength={5000}
                placeholder="Where to send payment, what it covers, or anything the Member should know…"
              />
            </label>
          </section>
        )}
        {step === "members" && (
          <section className="form-step member-selection-step">
            <header>
              <div>
                <h3>Choose members</h3>
                <p>Each selected member receives a separate private record.</p>
              </div>
              <Pill tone={memberIds.length ? "sage" : "neutral"}>
                {memberIds.length} selected
              </Pill>
            </header>
            <div className="member-selection-toolbar">
              <label className="people-search">
                <Search />
                <span className="sr-only">Search members</span>
                <input
                  type="search"
                  value={memberQuery}
                  onChange={(event) => setMemberQuery(event.target.value)}
                  placeholder="Search members"
                />
              </label>
              <div className="segmented">
                {(["all", "maxima", "fuerza", "impulso"] as const).map(
                  (value) => (
                    <button
                      type="button"
                      className={teamFilter === value ? "active" : ""}
                      onClick={() => setTeamFilter(value)}
                      key={value}
                    >
                      {value === "all" ? "All" : teamName(value)}
                    </button>
                  ),
                )}
              </div>
            </div>
            <div className="member-select-actions">
              <button
                className="text-button"
                type="button"
                onClick={() =>
                  setMemberIds([
                    ...new Set([
                      ...memberIds,
                      ...visibleMembers.map((member) => member.id),
                    ]),
                  ])
                }
              >
                Select visible
              </button>
              {memberIds.length > 0 && (
                <button
                  className="text-button"
                  type="button"
                  onClick={() => setMemberIds([])}
                >
                  Clear selection
                </button>
              )}
            </div>
            <div className="member-picker-grid">
              {renderGroupedMembers(visibleMembers, (member) => (
                <button
                  type="button"
                  className={memberIds.includes(member.id) ? "selected" : ""}
                  aria-pressed={memberIds.includes(member.id)}
                  onClick={() =>
                    setMemberIds((current) =>
                      current.includes(member.id)
                        ? current.filter((id) => id !== member.id)
                        : [...current, member.id],
                    )
                  }
                  key={member.id}
                >
                  <Avatar member={member} />
                  <span>
                    <b>{member.name}</b>
                    <small>{teamLabel(member.teamIds)}</small>
                  </span>
                  <Check />
                </button>
              ))}
            </div>
          </section>
        )}
        {step === "review" && (
          <section className="form-step payment-review">
            <header>
              <span className="section-label">Final review</span>
              <h3>{actionLabel}</h3>
              <p>
                This will create {memberIds.length} private{" "}
                {memberIds.length === 1 ? "record" : "records"}
                {status === "due"
                  ? " and schedule the requested notifications"
                  : ""}
                .
              </p>
            </header>
            <dl>
              <div>
                <dt>Category</dt>
                <dd>{paymentCategoryNames[category]}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{status}</dd>
              </div>
              {status !== "waived" && (
                <>
                  <div>
                    <dt>
                      {category === "team-fee" ? "Team fee" : "Base amount"}
                    </dt>
                    <dd>{money(cents(amount) / 100)}</dd>
                  </div>
                  {category === "team-fee" && classPass && (
                    <div>
                      <dt>Monthly class pass</dt>
                      <dd>{money(cents(classPassAmount) / 100)}</dd>
                    </div>
                  )}
                  <div>
                    <dt>HST (13%)</dt>
                    <dd>{money(hstCents / 100)}</dd>
                  </div>
                </>
              )}
              <div className="payment-review-total">
                <dt>
                  {status === "waived"
                    ? "Amount"
                    : status === "due" && !cashOnly
                      ? "E-transfer per member"
                      : "Total per member"}
                </dt>
                <dd>
                  {status === "waived" ? "Waived" : money(totalCents / 100)}
                </dd>
              </div>
              {status === "due" && !cashOnly && (
                <div>
                  <dt>Cash alternative</dt>
                  <dd>{money(feeSubtotalCents / 100)}</dd>
                </div>
              )}
              {status === "paid" && paymentMethod && (
                <div>
                  <dt>Payment method</dt>
                  <dd>{paymentMethodNames[paymentMethod]}</dd>
                </div>
              )}
              <div>
                <dt>Members</dt>
                <dd>{memberIds.length}</dd>
              </div>
              {payee && !cashOnly && (
                <div>
                  <dt>E-transfer recipient</dt>
                  <dd>
                    {payee.name} · {payee.email}
                  </dd>
                </div>
              )}
              {note && (
                <div>
                  <dt>Note</dt>
                  <dd>{note}</dd>
                </div>
              )}
            </dl>
            {status === "due" && (
              <div className="notification-preview">
                <BellRing />
                <span>
                  <b>Notification timing</b>
                  <small>
                    An in-app notification is created now; members who enabled
                    push receive the same alert on this device. Supported manual
                    dues receive a seven-day reminder if still unpaid.
                  </small>
                </span>
              </div>
            )}
          </section>
        )}
        {error && (
          <div className="auth-message" role="alert">
            {error}
          </div>
        )}
        <div className="modal-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={
              step === "details"
                ? onClose
                : () => setStep(step === "review" ? "members" : "details")
            }
          >
            {step === "details" ? "Cancel" : "Back"}
          </button>
          {step === "details" ? (
            <button
              key="payment-details-next"
              type="button"
              className="primary-button"
              disabled={status !== "waived" && totalCents <= 0}
              onClick={() => {
                if (status === "paid" && !paymentMethod) {
                  setError("Choose Cash or E-transfer before continuing.");
                  paymentMethodRef.current?.focus();
                  return;
                }
                setError("");
                setStep("members");
              }}
            >
              Choose members <ChevronRight />
            </button>
          ) : step === "members" ? (
            <button
              key="payment-members-review"
              type="button"
              className="primary-button"
              disabled={!memberIds.length}
              onClick={(event) => {
                event.preventDefault();
                setStep("review");
              }}
            >
              Review {memberIds.length}{" "}
              {memberIds.length === 1 ? "record" : "records"} <ChevronRight />
            </button>
          ) : (
            <button
              key="payment-review-submit"
              className="primary-button"
              type="submit"
              disabled={busy}
            >
              <Save /> {busy ? "Saving…" : actionLabel}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}

function PaymentReceiptDialog({
  payment,
  data,
  onClose,
  reload,
}: {
  payment: PaymentRecord;
  data: BootstrapData;
  onClose: () => void;
  reload: () => Promise<void>;
}) {
  const paidCents = paymentPaidCents(payment, data.paymentReceipts);
  const storedRemainingCents = paymentRemainingCents(
    payment,
    data.paymentReceipts,
  );
  const receipts = data.paymentReceipts
    .filter((receipt) => receipt.paymentId === payment.id)
    .sort((a, b) => b.recordedAt.localeCompare(a.recordedAt));
  const [amount, setAmount] = useState(
    ((paymentTotalForMethod(payment, "e-transfer") - paidCents) / 100).toFixed(
      2,
    ),
  );
  const [paidOn, setPaidOn] = useState(businessDate());
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | "">("");
  const [note, setNote] = useState("");
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const paymentMethodRef = useRef<HTMLSelectElement>(null);
  const quote = paymentQuote(payment);
  const totalOwingCents = paymentTotalForMethod(
    payment,
    paymentMethod || "e-transfer",
  );
  const displayedHstCents =
    quote.fixed || paymentMethod !== "cash" ? quote.hstCents : 0;
  const remainingCents = Math.max(0, totalOwingCents - paidCents);
  return (
    <Modal title={`Record payment · ${payment.label}`} onClose={onClose} wide>
      <form
        className="payment-receipt-form form-stack"
        onSubmit={async (event) => {
          event.preventDefault();
          setError("");
          if (!paymentMethod) {
            setError(
              "Choose Cash or E-transfer before recording this payment.",
            );
            paymentMethodRef.current?.focus();
            return;
          }
          setBusy(true);
          try {
            await api(`/api/teacher/payments/${payment.id}/receipts`, {
              method: "POST",
              body: JSON.stringify({
                amountCents: Math.round(Number(amount) * 100),
                paidOn,
                paymentMethod,
                expectedTotalCents: totalOwingCents,
                note,
                idempotencyKey,
              }),
            });
            await reload();
            onClose();
          } catch (cause) {
            setError(
              cause instanceof Error
                ? cause.message
                : "Could not record payment",
            );
          } finally {
            setBusy(false);
          }
        }}
      >
        {
          <dl className="payment-tax-breakdown">
            <div>
              <dt>Base amount</dt>
              <dd>{money(quote.baseCents / 100)}</dd>
            </div>
            <div>
              <dt>HST (13%)</dt>
              <dd>{money(displayedHstCents / 100)}</dd>
            </div>
            <div className="total">
              <dt>Total owing</dt>
              <dd>{money(totalOwingCents / 100)}</dd>
            </div>
          </dl>
        }
        <div className="payment-settlement-summary">
          <span>
            <small>Amount due</small>
            <b>{money(totalOwingCents / 100)}</b>
          </span>
          <span>
            <small>Paid so far</small>
            <b>{money(paidCents / 100)}</b>
          </span>
          <span className="remaining">
            <small>Remaining</small>
            <b>{money(remainingCents / 100)}</b>
          </span>
        </div>
        <div className="field-grid field-grid-2">
          <label>
            <span>Payment method</span>
            <select
              ref={paymentMethodRef}
              value={paymentMethod}
              onChange={(event) => {
                const method = event.target.value as PaymentMethod;
                if (Math.round(Number(amount) * 100) === remainingCents)
                  setAmount(
                    (
                      (paymentTotalForMethod(payment, method || "e-transfer") -
                        paidCents) /
                      100
                    ).toFixed(2),
                  );
                setPaymentMethod(method);
                setError("");
              }}
              aria-invalid={
                error ===
                "Choose Cash or E-transfer before recording this payment."
              }
            >
              <option value="">Choose method</option>
              <option
                value="cash"
                disabled={payment.settlementPricing?.method === "e-transfer"}
              >
                Cash
              </option>
              <option
                value="e-transfer"
                disabled={
                  payment.cashOnly ||
                  payment.settlementPricing?.method === "cash"
                }
              >
                E-transfer
              </option>
            </select>
            <small>Required to record a received payment.</small>
          </label>
          <label>
            <span>Amount received</span>
            <input
              type="number"
              min="0.01"
              max={(remainingCents / 100).toFixed(2)}
              step="0.01"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              required
            />
          </label>
          <label>
            <span>Paid on</span>
            <input
              type="date"
              value={paidOn}
              onChange={(event) => setPaidOn(event.target.value)}
              required
            />
          </label>
          {quote.conditional && (
            <p className="field-wide">
              The first installment selects the cash or e-transfer total. Use
              that method for later installments.
            </p>
          )}
          <label className="field-wide">
            <span>Note (optional)</span>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={2}
              placeholder="For example, short payment to collect later"
            />
          </label>
        </div>
        {receipts.length > 0 && (
          <section className="receipt-history">
            <header>
              <b>Payment history</b>
              <small>{receipts.length} received</small>
            </header>
            {receipts.map((receipt) => (
              <div key={receipt.id}>
                <span>
                  <b>{money(receipt.amountCents / 100)}</b>
                  <small>
                    {receipt.paymentMethod
                      ? paymentMethodNames[receipt.paymentMethod]
                      : "Method not recorded"}
                    {receipt.note ? ` · ${receipt.note}` : ""}
                  </small>
                </span>
                <time>{formatDate(receipt.paidOn)}</time>
              </div>
            ))}
          </section>
        )}
        {error && (
          <div className="auth-message" role="alert">
            {error}
          </div>
        )}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="primary-button"
            type="submit"
            disabled={
              busy ||
              !amount ||
              Number(amount) <= 0 ||
              Math.round(Number(amount) * 100) > remainingCents
            }
          >
            <Check /> {busy ? "Recording…" : "Record amount"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function PaymentsPage({
  data,
  reload,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
}) {
  const teacher = data.user.role === "teacher";
  const [editing, setEditing] = useState<PaymentRecord | "new">();
  const [settling, setSettling] = useState<PaymentRecord>();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "due" | "paid" | "review" | "all"
  >(teacher ? "due" : "due");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<"waived" | "delete">();
  const [bulkError, setBulkError] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const records = [...data.payments].sort((a, b) =>
    (b.recordedAt ?? b.updatedAt).localeCompare(a.recordedAt ?? a.updatedAt),
  );
  const member = (id: string) =>
    data.members.find((person) => person.id === id);
  const paymentMethodSummary = (paymentId: string) =>
    [
      ...new Set(
        data.paymentReceipts
          .filter((receipt) => receipt.paymentId === paymentId)
          .map((receipt) =>
            receipt.paymentMethod
              ? paymentMethodNames[receipt.paymentMethod]
              : "Method not recorded",
          ),
      ),
    ].join(" + ");
  const currentMonth = businessDate().slice(0, 7);
  const currentMonthLabel = new Date(
    `${currentMonth}-01T12:00:00Z`,
  ).toLocaleDateString("en-CA", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  const cycleRecords = records.filter(
    (payment) =>
      payment.category === "team-fee" && payment.month === currentMonth,
  );
  const expectedCents = cycleRecords.reduce(
    (sum, payment) => sum + paymentTotalForMethod(payment, "e-transfer"),
    0,
  );
  const collectedCents = cycleRecords.reduce(
    (sum, payment) => sum + paymentPaidCents(payment, data.paymentReceipts),
    0,
  );
  const outstandingCents = cycleRecords.reduce(
    (sum, payment) =>
      sum + paymentRemainingCents(payment, data.paymentReceipts),
    0,
  );
  const needsReview = (payment: PaymentRecord) =>
    payment.status === "unrecorded" ||
    ((payment.status === "due" || payment.status === "paid") &&
      paymentAmountCents(payment) <= 0);
  const filteredRecords = records
    .filter((payment) => {
      if (statusFilter === "review") return needsReview(payment);
      if (statusFilter === "all") return true;
      return payment.status === statusFilter;
    })
    .filter((payment) =>
      `${member(payment.memberId)?.name ?? ""} ${payment.label} ${paymentCategoryName(payment)}`
        .toLowerCase()
        .includes(query.trim().toLowerCase()),
    );
  const orderedRecords = [...filteredRecords].sort((a, b) => {
    const left = member(a.memberId),
      right = member(b.memberId);
    return (
      compareMembers(
        left ?? { name: "Former member", teamIds: [] },
        right ?? { name: "Former member", teamIds: [] },
      ) ||
      a.memberId.localeCompare(b.memberId) ||
      a.label.localeCompare(b.label)
    );
  });
  const groupedRecords = Object.entries(
    orderedRecords.reduce<Record<string, PaymentRecord[]>>(
      (groups, payment) => {
        const key = memberGroup(member(payment.memberId));
        (groups[key] ??= []).push(payment);
        return groups;
      },
      {},
    ),
  );
  const memberOutstanding = records.filter(
    (payment) => payment.status === "due" && paymentAmountCents(payment) > 0,
  );
  const memberHistory = records.filter(
    (payment) => payment.status === "paid" || payment.status === "waived",
  );
  return (
    <>
      <PageHeader
        eyebrow={teacher ? "Coach ledger" : "Private to you"}
        title="Payments"
        copy={
          teacher
            ? "Review monthly cycles, collect outstanding balances, and keep each member’s ledger private."
            : "Outstanding items show a clear amount and detail. Paid and waived records stay in History."
        }
        action={
          teacher ? (
            <button
              type="button"
              className="primary-button"
              onClick={() => setEditing("new")}
            >
              <Plus /> Record or create
            </button>
          ) : undefined
        }
      />
      {teacher ? (
        <>
          <section className="cycle-card">
            <header>
              <div>
                <span className="section-label">Automated monthly cycle</span>
                <h2>{currentMonthLabel} team fees</h2>
                <p>
                  Generated by the monthly scheduler. The standard fee is $130;
                  the optional class pass remains separate.
                </p>
              </div>
              <Pill tone={cycleRecords.length ? "sage" : "neutral"}>
                {cycleRecords.length ? "Generated" : "Awaiting cycle"}
              </Pill>
            </header>
            <div className="metric-grid">
              <MetricCard
                label="Expected"
                value={money(expectedCents / 100)}
                detail={`${cycleRecords.length} member records`}
                tone="primary"
              />
              <MetricCard
                label="Collected"
                value={money(collectedCents / 100)}
                detail={`${cycleRecords.filter((payment) => payment.status === "paid").length} paid`}
                tone="sage"
              />
              <MetricCard
                label="Outstanding"
                value={money(outstandingCents / 100)}
                detail={`${cycleRecords.filter((payment) => payment.status === "due").length} due`}
                tone="gold"
              />
            </div>
          </section>
          <section className="ledger-toolbar">
            <label className="people-search">
              <Search />
              <span className="sr-only">Search payments</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search member, category, or detail"
              />
            </label>
            <div className="section-tabs" aria-label="Payment status">
              {(["due", "paid", "review", "all"] as const).map((value) => (
                <button
                  className={statusFilter === value ? "active" : ""}
                  onClick={() => {
                    setStatusFilter(value);
                    setSelectedIds([]);
                  }}
                  key={value}
                >
                  {value === "review"
                    ? "Needs review"
                    : value.charAt(0).toUpperCase() + value.slice(1)}
                </button>
              ))}
            </div>
          </section>
          {bulkError && !bulkAction && (
            <p role="alert" className="auth-message">
              {bulkError}
            </p>
          )}
          {selectedIds.length > 0 && (
            <section className="bulk-action-bar">
              <span>
                <b>{selectedIds.length} selected</b>
                <small>Review an action for these selected records.</small>
              </span>
              <div>
                <button
                  className="secondary-button"
                  onClick={() => setSelectedIds([])}
                >
                  Clear
                </button>
                <button
                  className="secondary-button"
                  onClick={() => {
                    setBulkError("");
                    setBulkAction("waived");
                  }}
                >
                  Waive
                </button>
                <button
                  className="destructive-button"
                  onClick={() => {
                    setBulkError("");
                    setBulkAction("delete");
                  }}
                >
                  <Trash2 /> Delete
                </button>
              </div>
            </section>
          )}
          <section className="ledger-groups">
            {groupedRecords.length ? (
              groupedRecords.map(([group, payments]) => (
                <article className="record-group" key={group}>
                  <header>
                    <div>
                      <span className="section-label">Ledger group</span>
                      <h2>{group}</h2>
                    </div>
                    <span>
                      {payments.length}{" "}
                      {payments.length === 1 ? "record" : "records"}
                    </span>
                  </header>
                  <div>
                    {payments.map((payment, index) => (
                      <div className="ledger-row" key={payment.id}>
                        {(index === 0 ||
                          payments[index - 1]?.memberId !==
                            payment.memberId) && (
                          <h3 className="member-group-heading">
                            {member(payment.memberId)?.name ?? "Former member"}
                          </h3>
                        )}
                        <label className="record-select">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(payment.id)}
                            onChange={(event) =>
                              setSelectedIds(
                                event.target.checked
                                  ? [...selectedIds, payment.id]
                                  : selectedIds.filter(
                                      (id) => id !== payment.id,
                                    ),
                              )
                            }
                          />
                          <span className="sr-only">
                            Select {payment.label}
                          </span>
                        </label>
                        {member(payment.memberId) ? (
                          <Avatar member={member(payment.memberId)!} />
                        ) : (
                          <CircleDollarSign />
                        )}
                        <span>
                          <b>
                            {member(payment.memberId)?.name ?? "Former member"}
                          </b>
                          <small>
                            {payment.label}
                            {payment.note ? ` · ${payment.note}` : ""}
                            {paymentMethodSummary(payment.id)
                              ? ` · ${paymentMethodSummary(payment.id)}`
                              : ""}
                          </small>
                        </span>
                        <strong className="payment-balance">
                          {paymentAmountCents(payment) > 0 ? (
                            <>
                              <span>
                                {money(
                                  paymentRemainingCents(
                                    payment,
                                    data.paymentReceipts,
                                  ) / 100,
                                )}
                              </span>
                              <small>
                                {money(
                                  paymentPaidCents(
                                    payment,
                                    data.paymentReceipts,
                                  ) / 100,
                                )}{" "}
                                paid of{" "}
                                {money(
                                  paymentTotalForMethod(payment, "e-transfer") /
                                    100,
                                )}
                              </small>
                              <PaymentHstLine payment={payment} />
                            </>
                          ) : (
                            "Needs amount"
                          )}
                        </strong>
                        <Pill
                          tone={
                            needsReview(payment)
                              ? "gold"
                              : payment.status === "paid"
                                ? "sage"
                                : payment.status === "due"
                                  ? "rose"
                                  : "neutral"
                          }
                        >
                          {needsReview(payment)
                            ? "Needs review"
                            : payment.status}
                        </Pill>
                        <span className="ledger-actions">
                          {payment.status === "due" &&
                            paymentRemainingCents(
                              payment,
                              data.paymentReceipts,
                            ) > 0 && (
                              <button
                                className="secondary-button compact-button"
                                type="button"
                                onClick={() => setSettling(payment)}
                              >
                                <CircleDollarSign /> Record payment
                              </button>
                            )}
                          <button
                            className="icon-button"
                            onClick={() => setEditing(payment)}
                            aria-label={`Edit ${payment.label} for ${member(payment.memberId)?.name ?? "member"}`}
                          >
                            <Pencil />
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                </article>
              ))
            ) : (
              <Empty
                title="No matching payment records"
                copy="Change the status filter or search to widen the ledger."
              />
            )}
          </section>
        </>
      ) : (
        <>
          {memberOutstanding.length > 0 && (
            <section className="payment-reminder">
              <BellRing />
              <div>
                <b>
                  {memberOutstanding.length} payment
                  {memberOutstanding.length === 1 ? "" : "s"} due
                </b>
                <p>
                  These reminders are also included in your subscribed Avante
                  calendar.
                </p>
              </div>
            </section>
          )}
          <nav className="section-tabs" aria-label="Your payment records">
            <button
              className={statusFilter === "due" ? "active" : ""}
              onClick={() => setStatusFilter("due")}
            >
              Outstanding
            </button>
            <button
              className={statusFilter === "paid" ? "active" : ""}
              onClick={() => setStatusFilter("paid")}
            >
              History
            </button>
          </nav>
          <section className="member-payment-list">
            {(statusFilter === "due" ? memberOutstanding : memberHistory)
              .length ? (
              (statusFilter === "due" ? memberOutstanding : memberHistory).map(
                (payment) => (
                  <article className="member-payment-card" key={payment.id}>
                    <span className="payment-category-icon">
                      <CircleDollarSign />
                    </span>
                    <div>
                      <span className="section-label">
                        {paymentCategoryName(payment)}
                      </span>
                      <h2>{payment.label}</h2>
                      <p>
                        {payment.dueDate
                          ? `Due ${formatDate(payment.dueDate, true)}`
                          : "Private payment record"}
                      </p>
                    </div>
                    <strong
                      className={cx(
                        "member-payment-amount",
                        payment.status === "due" && "sr-only",
                      )}
                    >
                      {payment.status === "waived"
                        ? "Waived"
                        : payment.status === "due"
                          ? `${money(paymentRemainingCents(payment, data.paymentReceipts) / 100)} remaining`
                          : money(
                              paymentPaidCents(payment, data.paymentReceipts) /
                                100,
                            )}
                      {paymentPaidCents(payment, data.paymentReceipts) > 0 && (
                        <small>
                          {money(
                            paymentPaidCents(payment, data.paymentReceipts) /
                              100,
                          )}{" "}
                          paid of {money(paymentAmountCents(payment) / 100)}
                        </small>
                      )}
                      <PaymentHstLine payment={payment} />
                    </strong>
                    <Pill
                      tone={
                        payment.status === "paid"
                          ? "sage"
                          : payment.status === "due"
                            ? "gold"
                            : "neutral"
                      }
                    >
                      {payment.status}
                    </Pill>
                    <div className="payment-card-detail">
                      <MemberPaymentInstructions payment={payment} />
                      <CoachNote note={payment.note} />
                    </div>
                  </article>
                ),
              )
            ) : (
              <Empty
                title={
                  statusFilter === "due"
                    ? "Nothing outstanding"
                    : "No payment history yet"
                }
                copy={
                  statusFilter === "due"
                    ? "You have no published payment due items."
                    : "Paid and waived records will appear here."
                }
              />
            )}
          </section>
        </>
      )}
      {editing &&
        (editing === "new" ? (
          <BatchPaymentForm
            data={data}
            reload={reload}
            onClose={() => setEditing(undefined)}
          />
        ) : (
          <PaymentForm
            item={editing}
            data={data}
            onClose={() => setEditing(undefined)}
            onSave={async (value) => {
              await api(`/api/teacher/payments/${editing.id}`, {
                method: "PATCH",
                body: JSON.stringify(value),
              });
              await reload();
            }}
            onDelete={async () => {
              await api(`/api/teacher/payments/${editing.id}`, {
                method: "DELETE",
              });
              await reload();
            }}
          />
        ))}
      {settling && (
        <PaymentReceiptDialog
          payment={
            data.payments.find((payment) => payment.id === settling.id) ??
            settling
          }
          data={data}
          reload={reload}
          onClose={() => setSettling(undefined)}
        />
      )}
      {bulkAction && (
        <ConfirmDialog
          title={`${bulkAction === "delete" ? "Delete" : "Waive"} ${selectedIds.length} records?`}
          copy={
            bulkAction === "delete"
              ? "Permanently delete these selected payment entries, their receipt history, and related in-app reminders. This cannot be undone. Previously delivered notifications cannot be recalled."
              : "Each selected record will be waived and removed from the Member’s outstanding list. No payment receipt will be created."
          }
          confirmLabel={
            bulkAction === "delete" ? "Delete records" : "Waive records"
          }
          destructive={bulkAction === "delete"}
          error={bulkError}
          busy={bulkBusy}
          onClose={() => setBulkAction(undefined)}
          onConfirm={async () => {
            setBulkBusy(true);
            try {
              setBulkError("");
              const results = await Promise.allSettled(
                selectedIds.map((id) =>
                  api(
                    `/api/teacher/payments/${id}`,
                    bulkAction === "delete"
                      ? { method: "DELETE" }
                      : {
                          method: "PATCH",
                          body: JSON.stringify({ status: bulkAction }),
                        },
                  ),
                ),
              );
              const failed = selectedIds.filter(
                (_, index) => results[index]?.status === "rejected",
              );
              setSelectedIds(failed);
              if (!failed.length) setBulkAction(undefined);
              else
                setBulkError(
                  `${failed.length} records could not be updated. Only those records remain selected. Please retry.`,
                );
              await reload();
            } catch (cause) {
              setBulkError(
                cause instanceof Error
                  ? cause.message
                  : "Could not refresh payments. Reload before continuing.",
              );
            } finally {
              setBulkBusy(false);
            }
          }}
        />
      )}
    </>
  );
}

function AnnouncementForm({
  item,
  onClose,
  onSave,
  onDelete,
}: {
  item?: Announcement;
  onClose: () => void;
  onSave: (value: {
    title: string;
    body: string;
    teamId?: TeamId;
  }) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const [title, setTitle] = useState(item?.title ?? "");
  const [bodyText, setBodyText] = useState(item?.body ?? "");
  const [team, setTeam] = useState<TeamId | "">(item?.teamId ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <Modal
      title={item ? "Edit announcement" : "New announcement"}
      eyebrow="Coach communications"
      onClose={() => !busy && onClose()}
    >
      <form
        className="form-stack compact-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError("");
          try {
            await onSave({
              title,
              body: bodyText,
              teamId: team || undefined,
            });
            onClose();
          } catch (cause) {
            setError(
              cause instanceof Error
                ? cause.message
                : "Could not save announcement",
            );
          } finally {
            setBusy(false);
          }
        }}
      >
        <label>
          <span>Title</span>
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            required
          />
        </label>
        <label>
          <span>Audience</span>
          <select
            value={team}
            onChange={(event) => setTeam(event.target.value as TeamId | "")}
          >
            <option value="">Everyone</option>
            <option value="maxima">Maxima</option>
            <option value="fuerza">Fuerza</option>
            <option value="impulso">Impulso</option>
          </select>
        </label>
        <label>
          <span>Message</span>
          <textarea
            rows={7}
            value={bodyText}
            onChange={(event) => setBodyText(event.target.value)}
            required
          />
        </label>
        {error && <div className="auth-message">{error}</div>}
        {item && onDelete && (
          <div className="member-danger-zone">
            <span>
              <b>Delete announcement</b>
              <small>
                This removes the update from Coach and Member feeds.
              </small>
            </span>
            <button
              type="button"
              onClick={async () => {
                setBusy(true);
                try {
                  await onDelete();
                  onClose();
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
            >
              <Trash2 /> Delete
            </button>
          </div>
        )}
        <div className="modal-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button className="primary-button" type="submit" disabled={busy}>
            <Megaphone /> {busy ? "Saving" : item ? "Save update" : "Publish"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function AnnouncementsPage({
  data,
  reload,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
}) {
  const [editing, setEditing] = useState<Announcement | "new">();
  return (
    <>
      <PageHeader
        eyebrow="Coach communications"
        title="Announcements"
        copy="Publish a clear update to everyone or one team."
        action={
          <button
            type="button"
            className="primary-button"
            onClick={() => setEditing("new")}
          >
            <Plus /> New update
          </button>
        }
      />
      <div className="two-column announcements-layout">
        <section className="panel">
          <header className="panel-head">
            <div>
              <span className="section-label">Composer</span>
              <h2>Share an update</h2>
            </div>
            <button
              type="button"
              className="secondary-button compact-button"
              onClick={() => setEditing("new")}
            >
              <Megaphone /> Compose
            </button>
          </header>
          <div className="announcement-composer-empty">
            <Megaphone />
            <span>
              <b>Use the composer when there is something to send.</b>
              <small>
                Existing updates stay editable in the feed on the right.
              </small>
            </span>
          </div>
        </section>
        <section className="announcement-feed">
          {data.announcements.map((item) => (
            <article key={item.id}>
              <span className="announcement-icon">
                <Megaphone />
              </span>
              <div>
                <span>
                  <b>{item.title}</b>
                  <TeamBadge teamId={item.teamId} />
                  {item.id.startsWith("announcement-team-fee-") && (
                    <Pill tone="gold">Automated</Pill>
                  )}
                </span>
                <p>{item.body}</p>
                <small>
                  {new Date(item.createdAt).toLocaleDateString("en-CA", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </small>
                <span className="announcement-actions">
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => setEditing(item)}
                    aria-label={`Edit ${item.title}`}
                  >
                    <Pencil />
                  </button>
                </span>
              </div>
            </article>
          ))}
        </section>
      </div>
      {editing && (
        <AnnouncementForm
          item={editing === "new" ? undefined : editing}
          onClose={() => setEditing(undefined)}
          onSave={async (value) => {
            await api(
              editing === "new"
                ? "/api/teacher/announcements"
                : `/api/teacher/announcements/${editing.id}`,
              {
                method: editing === "new" ? "POST" : "PATCH",
                body: JSON.stringify(value),
              },
            );
            await reload();
          }}
          onDelete={
            editing !== "new"
              ? async () => {
                  await api(`/api/teacher/announcements/${editing.id}`, {
                    method: "DELETE",
                  });
                  await reload();
                }
              : undefined
          }
        />
      )}
    </>
  );
}

function MemberHome({
  data,
  setPage,
}: {
  data: BootstrapData;
  setPage: (page: Page) => void;
}) {
  const next = [...data.events]
    .filter(
      (event) =>
        event.date &&
        event.date >= businessDate() &&
        memberPerformsInEvent(event, data.user.id),
    )
    .sort((a, b) => {
      const aStart = `${a.date ?? "9999-12-31"}T${a.callTime ?? a.showTime ?? "23:59"}`;
      const bStart = `${b.date ?? "9999-12-31"}T${b.callTime ?? b.showTime ?? "23:59"}`;
      return aStart.localeCompare(bStart);
    })[0];
  const due = data.payments.filter(
    (payment) => payment.status === "due" && paymentAmountCents(payment) > 0,
  );
  const assignedRoutines =
    next?.routines.filter((entry) =>
      entry.performers.some((member) => member.memberId === data.user.id),
    ) ?? [];
  return (
    <>
      <PageHeader
        eyebrow={`${teamLabel(data.user.teamIds)} team`}
        title={`Hi, ${data.user.name.split(" ")[0]}.`}
        copy="Your next performance, assigned routines, private payments, and team updates."
      />
      {next ? (
        <section className="student-hero">
          <div>
            <Pill tone={next.type === "competition" ? "gold" : "rose"}>
              Your next performance
            </Pill>
            <span>{formatDate(next.date, true)}</span>
            <h2>{next.title}</h2>
            <dl className="student-hero-times" aria-label="Performance times">
              <div>
                <dt>
                  <Clock3 /> Call time
                </dt>
                <dd>{next.callTime || "TBD"}</dd>
              </div>
              <div>
                <dt>
                  <Sparkles /> Performance time
                </dt>
                <dd>{next.showTime || "TBD"}</dd>
              </div>
            </dl>
            <p>
              <MapPin /> {next.venue}
            </p>
            <CoachNote note={next.notes} title="Event notes" />
            <button
              className="light-button"
              onClick={() => setPage("schedule")}
            >
              Open my calendar <ChevronRight />
            </button>
          </div>
          <aside className="student-hero-side">
            <span>Your routines</span>
            {assignedRoutines.map((entry) => {
              const person = entry.performers.find(
                (member) => member.memberId === data.user.id,
              );
              return (
                <div key={entry.id}>
                  <b>{routineDisplayName(entry, data.routines)}</b>
                  <small>
                    {person?.position || entry.label || "Selected performer"}
                  </small>
                </div>
              );
            })}
            <MemberPerformanceChecklist
              event={next}
              entries={assignedRoutines}
              routines={data.routines}
              memberId={data.user.id}
              key={`${data.user.id}:${next.id}`}
            />
          </aside>
        </section>
      ) : (
        <Empty
          title="No selected performance ahead"
          copy="Your weekly practices still appear in Schedule."
        />
      )}
      <div className="two-column student-columns">
        <section className="panel">
          <header className="panel-head">
            <div>
              <span className="section-label">Weekly rhythm</span>
              <h2>Team practices</h2>
            </div>
          </header>
          <div className="class-list">
            {data.classes.map((item) => (
              <div key={item.id}>
                <CalendarDays />
                <span>
                  <b>{weekdays[item.weekday]}</b>
                  <small>
                    {item.startTime}–{item.endTime}
                  </small>
                </span>
                <TeamBadge teamId={item.teamId} />
              </div>
            ))}
          </div>
        </section>
        <section className="panel">
          <header className="panel-head">
            <div>
              <span className="section-label">Private ledger</span>
              <h2>Payments</h2>
            </div>
          </header>
          {due.length ? (
            <div className="due-list">
              {due.map((payment) => (
                <button key={payment.id} onClick={() => setPage("payments")}>
                  <CircleDollarSign />
                  <span>
                    <b>{payment.label}</b>
                    <small>
                      {money(
                        paymentRemainingCents(payment, data.paymentReceipts) /
                          100,
                      )}{" "}
                      remaining
                    </small>
                  </span>
                  <ChevronRight />
                </button>
              ))}
            </div>
          ) : (
            <Empty
              title="Nothing marked due"
              copy="Paid or unreviewed records are not treated as missing."
            />
          )}
        </section>
      </div>
      <section className="announcement-strip">
        {data.announcements.map((item) => (
          <article key={item.id}>
            <Megaphone />
            <div>
              <b>{item.title}</b>
              <p>{item.body}</p>
            </div>
          </article>
        ))}
      </section>
    </>
  );
}

function CalendarSubscription({ data }: { data: BootstrapData }) {
  const [copied, setCopied] = useState(false);
  const webcalUrl = data.calendarFeedUrl.replace(/^https?:\/\//, "webcal://");
  return (
    <section className="calendar-subscribe">
      <span className="calendar-icon">
        <RefreshCw />
      </span>
      <div>
        <b>Subscribe once. Receive updates automatically.</b>
        <p>
          Copy this private feed, then paste it into Google Calendar's "From
          URL" screen. Mobile Google Calendar may require that manual paste.
        </p>
        <code>{data.calendarFeedUrl}</code>
      </div>
      <div>
        <button
          className="secondary-button"
          onClick={async () => {
            await navigator.clipboard.writeText(data.calendarFeedUrl);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? <Check /> : <Copy />} {copied ? "Copied" : "Copy URL"}
        </button>
        <a
          className="secondary-button"
          href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl"
          target="_blank"
          rel="noreferrer"
        >
          Google setup <ExternalLink />
        </a>
        <a className="secondary-button" href={webcalUrl}>
          <CalendarPlus /> Subscribe
        </a>
        <a className="secondary-button" href="/api/calendar.ics">
          <Download /> Download .ics
        </a>
      </div>
    </section>
  );
}

function MemberSchedule({ data }: { data: BootstrapData }) {
  return (
    <>
      <PageHeader
        eyebrow="Personal calendar"
        title="Practices and selected performances"
        copy="Weekly practices recur automatically. A performance appears when your coach publishes it."
      />
      <CalendarSubscription data={data} />
      <section className="calendar-section">
        <header>
          <span className="section-label">Every week</span>
          <h2>{teamLabel(data.user.teamIds)} practices</h2>
        </header>
        <div className="class-grid student-class-grid">
          {data.classes.map((item) => (
            <article key={item.id}>
              <div className="class-card-head">
                <TeamBadge teamId={item.teamId} />
                <CalendarActionButton
                  href={googleClassUrl(item)}
                  label={`Add ${teamName(item.teamId)} class to Google Calendar`}
                />
              </div>
              <h2>{weekdays[item.weekday]}</h2>
              <strong>
                {item.startTime}–{item.endTime}
              </strong>
              <p>{item.venue}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="calendar-section">
        <header>
          <span className="section-label">Your castings</span>
          <h2>Performances & competitions</h2>
        </header>
        <div className="student-event-list">
          {data.events.length ? (
            [...data.events]
              .sort((a, b) =>
                (a.date ?? "9999").localeCompare(b.date ?? "9999"),
              )
              .map((event) => (
                <EventCard
                  event={event}
                  routines={data.routines}
                  members={data.members}
                  key={event.id}
                />
              ))
          ) : (
            <Empty
              title="No performances assigned"
              copy="New events will appear here when a coach publishes them."
            />
          )}
        </div>
      </section>
    </>
  );
}

type ScheduleCategory = "all" | "practice" | EventRecord["type"];

function SchedulePage({
  data,
  reload,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
}) {
  const coach = data.user.role === "teacher";
  const [category, setCategory] = useState<ScheduleCategory>("all");
  const [archive, setArchive] = useState(false);
  const [query, setQuery] = useState("");
  const [classItem, setClassItem] = useState<WeeklyClass | "new">();
  const [eventItem, setEventItem] = useState<EventRecord | "new">();
  const [createOpen, setCreateOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [quickText, setQuickText] = useState("");
  const [preferences, setPreferences] = useState(data.calendarPreferences);
  const today = businessDate();
  const coaches = data.members.filter((member) => member.role === "teacher");
  const eventMatchesQuery = (event: EventRecord) => {
    const coachNames = (event.coachMemberIds ?? [])
      .map((id) => coaches.find((member) => member.id === id)?.name ?? "")
      .join(" ");
    return `${event.title} ${event.venue} ${coachNames} ${(event.teamIds ?? []).map(teamName).join(" ")}`
      .toLowerCase()
      .includes(query.trim().toLowerCase());
  };
  const events = data.events
    .filter((event) => category === "all" || event.type === category)
    .filter((event) =>
      archive
        ? Boolean(event.date && event.date < today)
        : !event.date || event.date >= today,
    )
    .filter(eventMatchesQuery)
    .sort((a, b) => (a.date ?? "9999").localeCompare(b.date ?? "9999"));
  const practices = data.classes.filter((item) => {
    if (category !== "all" && category !== "practice") return false;
    const coachNames = (item.coachMemberIds ?? [])
      .map((id) => coaches.find((member) => member.id === id)?.name ?? "")
      .join(" ");
    return `${teamName(item.teamId)} ${item.venue} ${coachNames}`
      .toLowerCase()
      .includes(query.trim().toLowerCase());
  });
  const savePreferences = async (next: CalendarPreferences) => {
    setPreferences(next);
    await api("/api/calendar/preferences", {
      method: "PATCH",
      body: JSON.stringify({
        weeklyPractices: next.weeklyPractices,
        teamEvents: next.teamEvents,
        competitions: next.competitions,
        performances: next.performances,
      }),
    });
    await reload();
  };
  const parseQuickEvent = () => {
    const text = quickText.trim();
    if (!text) return;
    const parsed = parseQuickEventText(text, today);
    setEventItem({
      id: "",
      title: parsed.title,
      type: parsed.type,
      date: parsed.date,
      showTime: parsed.showTime,
      venue: parsed.venue,
      address: "Toronto, ON",
      notes: `Created from quick entry: ${text}`,
      status: "published",
      routines: [],
      teamIds: ["maxima", "fuerza", "impulso"],
      coachMemberIds: [data.user.id],
      visibility: "all-members",
      visibleMemberIds: [],
      updatedAt: new Date().toISOString(),
    });
  };
  return (
    <>
      <PageHeader
        title="Schedule"
        copy="Practices, performances, competitions, and team events in one place. Past items move to the archive automatically."
        action={
          coach ? (
            <button
              className="primary-button"
              onClick={() => setCreateOpen(true)}
            >
              <Plus /> Add to schedule
            </button>
          ) : undefined
        }
      />
      <section className="schedule-toolbar">
        <div
          className="segmented schedule-category-tabs"
          aria-label="Schedule category"
        >
          {(
            [
              ["all", "All"],
              ["practice", "Weekly Practices"],
              ["performance", "Performances"],
              ["competition", "Competitions"],
              ["team-event", "Team Events"],
            ] as const
          ).map(([id, label]) => (
            <button
              className={category === id ? "active" : ""}
              onClick={() => setCategory(id)}
              key={id}
            >
              {label}
            </button>
          ))}
        </div>
        <div
          className="segmented schedule-history-tabs"
          aria-label="Schedule history"
        >
          <button
            className={!archive ? "active" : ""}
            onClick={() => setArchive(false)}
          >
            Current
          </button>
          <button
            className={archive ? "active" : ""}
            onClick={() => setArchive(true)}
          >
            Archived
          </button>
        </div>
        <button
          className="secondary-button schedule-calendar-button"
          onClick={() => setCalendarOpen(true)}
        >
          <CalendarPlus /> Calendar settings
        </button>
      </section>
      {archive ? (
        <div className="archived-schedule">
          <label className="people-search archive-search">
            <Search />
            <span className="sr-only">Search archived schedule</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search archived schedule"
            />
          </label>
          <div className="event-list">
            {events.length ? (
              events.map((event) => (
                <EventCard
                  event={event}
                  routines={data.routines}
                  members={data.members}
                  onEdit={coach ? () => setEventItem(event) : undefined}
                  key={event.id}
                />
              ))
            ) : (
              <Empty
                title="No archived items"
                copy="Past schedule items will appear here automatically."
              />
            )}
          </div>
        </div>
      ) : (
        <TeamCalendarPage
          embedded
          data={{ ...data, events, teamEvents: events }}
          practices={practices}
          includeUnpublished={coach}
          onEditEvent={coach ? (event) => setEventItem(event) : undefined}
          onEditPractice={
            coach ? (practice) => setClassItem(practice) : undefined
          }
          scheduleQuery={query}
          onScheduleQueryChange={setQuery}
        />
      )}
      {createOpen && coach && (
        <Modal
          title="Add to schedule"
          onClose={() => setCreateOpen(false)}
          wide
        >
          <div className="schedule-create-launcher">
            <button
              type="button"
              onClick={() => {
                setCreateOpen(false);
                setEventItem("new");
              }}
            >
              <CalendarDays />
              <span>
                <b>Structured event</b>
                <small>
                  Build a performance, competition, or team event with privacy,
                  cast, and slots.
                </small>
              </span>
              <ChevronRight />
            </button>
            <button
              type="button"
              onClick={() => {
                setCreateOpen(false);
                setClassItem("new");
              }}
            >
              <RefreshCw />
              <span>
                <b>Weekly practice</b>
                <small>Add or update a recurring team practice.</small>
              </span>
              <ChevronRight />
            </button>
            <section className="quick-entry-card">
              <header>
                <span className="section-label">Fast path</span>
                <h3>Quick event entry</h3>
                <p>
                  Type or dictate the details, then review every structured
                  field before publishing.
                </p>
              </header>
              <label>
                <span>Event details</span>
                <textarea
                  value={quickText}
                  onChange={(event) => setQuickText(event.target.value)}
                  placeholder="Performance at Harbourfront tomorrow at 7 pm"
                  rows={3}
                />
              </label>
              <div className="composer-actions">
                <SpeechInputAction onTranscript={setQuickText} />
                <button
                  className="primary-button"
                  type="button"
                  disabled={!quickText.trim()}
                  onClick={() => {
                    parseQuickEvent();
                    setCreateOpen(false);
                  }}
                >
                  <Check /> Review structured event
                </button>
              </div>
            </section>
          </div>
        </Modal>
      )}
      {calendarOpen && (
        <Modal
          title="Calendar settings"
          onClose={() => setCalendarOpen(false)}
          wide
        >
          <div className="calendar-settings">
            <CalendarSubscription data={data} />
            <section className="calendar-feed-preferences">
              <header>
                <span className="section-label">Subscribed feed</span>
                <h3>Choose what your private feed includes</h3>
                <p>
                  Changes update the subscribed feed automatically; they do not
                  add duplicate calendar events.
                </p>
              </header>
              <div className="preference-grid">
                <label>
                  <input
                    type="checkbox"
                    checked={preferences.weeklyPractices}
                    onChange={(event) =>
                      void savePreferences({
                        ...preferences,
                        weeklyPractices: event.target.checked,
                      })
                    }
                  />
                  <span>
                    <b>Weekly Practices</b>
                    <small>Every recurring practice</small>
                  </span>
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={preferences.teamEvents}
                    onChange={(event) =>
                      void savePreferences({
                        ...preferences,
                        teamEvents: event.target.checked,
                      })
                    }
                  />
                  <span>
                    <b>Team Events</b>
                    <small>All visible team events</small>
                  </span>
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={preferences.competitions}
                    onChange={(event) =>
                      void savePreferences({
                        ...preferences,
                        competitions: event.target.checked,
                      })
                    }
                  />
                  <span>
                    <b>Competitions</b>
                    <small>All visible competitions</small>
                  </span>
                </label>
                <label className="preference-select">
                  <span>
                    <b>Performances</b>
                    <small>Choose all or only your castings</small>
                  </span>
                  <select
                    aria-label="Performance calendar preference"
                    value={preferences.performances}
                    onChange={(event) =>
                      void savePreferences({
                        ...preferences,
                        performances: event.target
                          .value as CalendarPreferences["performances"],
                      })
                    }
                  >
                    <option value="all">All performances</option>
                    <option value="cast-only">Only performances I’m in</option>
                    <option value="none">None</option>
                  </select>
                </label>
              </div>
            </section>
          </div>
        </Modal>
      )}
      {classItem && (
        <ClassForm
          item={classItem === "new" ? undefined : classItem}
          data={data}
          onClose={() => setClassItem(undefined)}
          onSave={async (value) => {
            await api("/api/teacher/classes", {
              method: "POST",
              body: JSON.stringify(value),
            });
            await reload();
          }}
        />
      )}
      {eventItem && (
        <EventForm
          item={eventItem === "new" ? undefined : eventItem}
          data={data}
          onClose={() => setEventItem(undefined)}
          onSave={async (value) => {
            await api("/api/teacher/events", {
              method: "POST",
              body: JSON.stringify(value),
            });
            await reload();
          }}
          onDelete={
            eventItem === "new"
              ? undefined
              : async () => {
                  await api(`/api/teacher/events/${eventItem.id}`, {
                    method: "DELETE",
                  });
                  await reload();
                }
          }
        />
      )}
    </>
  );
}

function PersonalMusic({
  data,
  reload,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
}) {
  const [selectedId, setSelectedId] = useState(data.personalTracks[0]?.id);
  const selected =
    data.personalTracks.find((track) => track.id === selectedId) ??
    data.personalTracks[0];
  const media = data.media.find((asset) => asset.id === selected?.mediaId);
  return (
    <>
      <PageHeader
        eyebrow="Personal practice library"
        title="My music"
        copy="Upload your own songs, change speed without changing pitch, and save reusable loop sections. Only you and coaches can access these files."
        action={
          <MediaUploader
            target={{ type: "personal-track" }}
            accept="audio/*"
            label="Upload my song"
            reload={reload}
          />
        }
      />
      {data.personalTracks.length ? (
        <>
          <div className="track-picker">
            {data.personalTracks.map((track) => (
              <button
                className={selected?.id === track.id ? "active" : ""}
                onClick={() => setSelectedId(track.id)}
                key={track.id}
              >
                <Music2 />
                <span>
                  <b>{track.title}</b>
                  <small>{track.sections.length} saved sections</small>
                </span>
              </button>
            ))}
          </div>
          {selected && (
            <PracticePlayer
              media={media}
              personalSections={selected.sections}
              canEditPersonal
              addLabel="Add section"
              onAdd={async (section) => {
                await api(`/api/personal-tracks/${selected.id}/sections`, {
                  method: "POST",
                  body: JSON.stringify(section),
                });
                await reload();
              }}
              onUpdatePersonal={async (sectionId, section) => {
                await api(
                  `/api/personal-tracks/${selected.id}/sections/${sectionId}`,
                  { method: "PATCH", body: JSON.stringify(section) },
                );
                await reload();
              }}
            />
          )}
        </>
      ) : (
        <section className="panel">
          <Empty
            title="Your music library is empty"
            copy="Upload any audio track to start a private practice workspace."
          />
        </section>
      )}
    </>
  );
}

function ProfilePage({
  data,
  reload,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
}) {
  const current = data.members.find((member) => member.id === data.user.id)!;
  const [name, setName] = useState(current.name);
  const [birthday, setBirthday] = useState(current.birthday ?? "");
  const [phone, setPhone] = useState(current.phone ?? "");
  const [saved, setSaved] = useState(false);
  return (
    <>
      <PageHeader
        eyebrow="Your account"
        title="Profile"
        copy="Keep your personal details current. Coaches can see birthdays; other members can see only month and day."
        action={
          <button
            className="secondary-button"
            type="button"
            onClick={() => void signOut()}
          >
            <LogOut /> Log out
          </button>
        }
      />
      <section className="profile-card">
        <div className="profile-identity">
          <Avatar member={current} />
          <span>
            <b>{current.name}</b>
            <small>{current.email}</small>
            <TeamBadges teamIds={current.teamIds} />
          </span>
          <div className="profile-photo-actions">
            <MediaUploader
              target={{ type: "profile-photo" }}
              accept="image/jpeg,image/png,image/webp"
              label={current.photoMediaId ? "Change photo" : "Upload photo"}
              reload={reload}
            />
          </div>
        </div>
        <section className="profile-capabilities">
          <header>
            <span className="section-label">Performance capabilities</span>
            <h2>Routines and usual slots</h2>
          </header>
          {current.capabilities?.length ? (
            <div>
              {current.capabilities.map((capability) => {
                const routine = data.routines.find(
                  (item) => item.id === capability.routineId,
                );
                return (
                  <span key={capability.id}>
                    <TeamBadge teamId={capability.teamId} />
                    <b>{routine?.name ?? "Routine"}</b>
                    <small>{capability.slot}</small>
                  </span>
                );
              })}
            </div>
          ) : (
            <p>Your Coach has not added any usual routine slots yet.</p>
          )}
        </section>
        <form
          className="form-stack"
          onSubmit={async (event) => {
            event.preventDefault();
            await api("/api/profile", {
              method: "PATCH",
              body: JSON.stringify({
                name,
                birthday: birthday || undefined,
                phone: phone || undefined,
              }),
            });
            await reload();
            setSaved(true);
            window.setTimeout(() => setSaved(false), 1500);
          }}
        >
          <label>
            <span>Display name</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </label>
          <div className="birthday-field">
            <span>Birthday</span>
            <BirthdaySelect value={birthday} onChange={setBirthday} />
            <small>Month and day only.</small>
          </div>
          <label>
            <span>Phone (optional)</span>
            <input
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          </label>
          <div className="profile-security">
            <ShieldCheck />
            <span>
              <b>Google identity</b>
              <small>Your sign-in email can only be changed by a coach.</small>
            </span>
          </div>
          <button className="primary-button" type="submit">
            {saved ? <Check /> : <Save />} {saved ? "Saved" : "Save profile"}
          </button>
        </form>
        <div className="profile-session">
          <span>
            <b>{data.authMode === "development" ? "Development" : "Session"}</b>
            <small>
              {data.user.role === "teacher"
                ? "Coach access"
                : `${teamLabel(data.user.teamIds)} member access`}
            </small>
          </span>
          <button
            className="secondary-button"
            type="button"
            onClick={() => void signOut()}
          >
            <LogOut /> Log out
          </button>
        </div>
      </section>
    </>
  );
}

function CapabilityEditor({
  data,
  member,
  reload,
  onClose,
}: {
  data: BootstrapData;
  member: Member;
  reload: () => Promise<void>;
  onClose: () => void;
}) {
  const [items, setItems] = useState<MemberCapability[]>(
    member.capabilities ?? [],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const eligibleRoutines = data.routines.filter((routine) =>
    member.teamIds.includes(routine.teamId),
  );
  const add = () => {
    const routine = eligibleRoutines[0];
    if (!routine) return;
    setItems([
      ...items,
      {
        id: crypto.randomUUID(),
        teamId: routine.teamId,
        routineId: routine.id,
        slot: "",
      },
    ]);
  };
  return (
    <Modal title={`Skills and slots · ${member.name}`} onClose={onClose} wide>
      <form
        className="capability-editor"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError("");
          try {
            await api(`/api/teacher/members/${member.id}/capabilities`, {
              method: "PUT",
              body: JSON.stringify({ capabilities: items }),
            });
            await reload();
            onClose();
          } catch (cause) {
            setError(
              cause instanceof Error
                ? cause.message
                : "Could not save capabilities",
            );
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className="member-context-card">
          <Avatar member={member} />
          <span>
            <b>{member.name}</b>
            <small>{teamLabel(member.teamIds)}</small>
          </span>
          <TeamBadges teamIds={member.teamIds} />
        </div>
        <p className="form-intro">
          These defaults autofill performance casts. Coaches can still change
          any assignment on an individual event.
        </p>
        <div className="capability-list">
          {items.map((item, index) => (
            <div className="capability-row" key={item.id}>
              <label>
                <span>Routine</span>
                <select
                  aria-label={`Routine ${index + 1}`}
                  value={item.routineId}
                  onChange={(event) => {
                    const routine = data.routines.find(
                      (entry) => entry.id === event.target.value,
                    )!;
                    setItems(
                      items.map((entry) =>
                        entry.id === item.id
                          ? {
                              ...entry,
                              routineId: routine.id,
                              teamId: routine.teamId,
                            }
                          : entry,
                      ),
                    );
                  }}
                >
                  {data.routines
                    .filter((routine) =>
                      member.teamIds.includes(routine.teamId),
                    )
                    .map((routine) => (
                      <option value={routine.id} key={routine.id}>
                        {teamName(routine.teamId)} · {routine.name}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                <span>Usual slot</span>
                <input
                  aria-label={`Usual slot ${index + 1}`}
                  value={item.slot}
                  onChange={(event) =>
                    setItems(
                      items.map((entry) =>
                        entry.id === item.id
                          ? { ...entry, slot: event.target.value }
                          : entry,
                      ),
                    )
                  }
                  placeholder="F1 or M2 floater"
                  required
                />
              </label>
              <button
                type="button"
                className="icon-button danger-button"
                onClick={() =>
                  setItems(items.filter((entry) => entry.id !== item.id))
                }
                aria-label={`Remove capability ${index + 1}`}
              >
                <Trash2 />
              </button>
            </div>
          ))}
          {!items.length && (
            <Empty
              title={
                eligibleRoutines.length
                  ? "No performance defaults"
                  : "No eligible routines"
              }
              copy={
                eligibleRoutines.length
                  ? "Add a routine and usual slot when this member has a repeatable assignment."
                  : "Add this member to a team with an active routine before assigning a usual slot."
              }
              action={
                eligibleRoutines.length ? (
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={add}
                  >
                    <Plus /> Add capability
                  </button>
                ) : undefined
              }
            />
          )}
        </div>
        {items.length > 0 && (
          <button
            className="secondary-button capability-add"
            type="button"
            onClick={add}
          >
            <Plus /> Add another capability
          </button>
        )}
        {error && <div className="auth-message">{error}</div>}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary-button" type="submit" disabled={busy}>
            <Save /> {busy ? "Saving…" : "Save capabilities"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function TeamPage({
  data,
  reload,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
}) {
  const [capabilityMember, setCapabilityMember] = useState<Member>();
  const [section, setSection] = useState<"directory" | "attendance">(
    "directory",
  );
  const sectionNav = (
    <nav className="section-tabs team-primary-tabs" aria-label="Team section">
      <button
        className={section === "directory" ? "active" : ""}
        onClick={() => setSection("directory")}
      >
        Directory
      </button>
      <button
        className={section === "attendance" ? "active" : ""}
        onClick={() => setSection("attendance")}
      >
        Attendance
      </button>
    </nav>
  );
  if (data.user.role === "teacher")
    return (
      <>
        {section === "directory" ? (
          <PeoplePage
            data={data}
            reload={reload}
            onEditCapabilities={setCapabilityMember}
            sectionNav={sectionNav}
          />
        ) : (
          <>
            <PageHeader
              title="Team"
              copy="Record a practice date, review the whole roster, or open one Member’s attendance history."
            />
            {sectionNav}
            <AttendancePanel data={data} reload={reload} />
          </>
        )}
        {capabilityMember && (
          <CapabilityEditor
            data={data}
            member={
              data.members.find(
                (member) => member.id === capabilityMember.id,
              ) ?? capabilityMember
            }
            reload={reload}
            onClose={() => setCapabilityMember(undefined)}
          />
        )}
      </>
    );
  return <MemberTeamDirectory data={data} reload={reload} />;
}

function AttendancePanel({
  data,
  reload,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
}) {
  const [team, setTeam] = useState<TeamId>("fuerza");
  const [view, setView] = useState<"overview" | "record">("overview");
  const [range, setRange] = useState<"90" | "all">("90");
  const [date, setDate] = useState(businessDate());
  const [focusedMemberId, setFocusedMemberId] = useState<string>();
  const [draft, setDraft] = useState<
    Record<string, { status?: AttendanceStatus; note: string }>
  >({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const roster = data.members
    .filter(
      (member) =>
        member.role === "student" &&
        member.status === "approved" &&
        member.teamIds.includes(team),
    )
    .sort((a, b) => a.name.localeCompare(b.name));
  const sessions = data.attendanceSessions
    .filter((session) => session.teamId === team)
    .sort((a, b) => b.date.localeCompare(a.date));
  const cutoff = isoDate(addDays(dateFromIso(businessDate()), -90));
  const allTeamMarks = data.attendanceMarks.filter(
    (mark) => mark.teamId === team,
  );
  const marks = allTeamMarks.filter(
    (mark) => range === "all" || mark.sessionId.split(":")[1]! >= cutoff,
  );
  const selectedSession = sessions.find((session) => session.date === date);
  useEffect(() => {
    const next: Record<string, { status?: AttendanceStatus; note: string }> =
      {};
    for (const member of roster) {
      const mark = allTeamMarks.find(
        (entry) =>
          entry.sessionId === `${team}:${date}` && entry.memberId === member.id,
      );
      next[member.id] = { status: mark?.status, note: mark?.note ?? "" };
    }
    setDraft(next);
  }, [team, date, data.attendanceMarks, data.members]);
  const summary = (memberId: string) => {
    const own = marks.filter((mark) => mark.memberId === memberId);
    const count = (status: AttendanceStatus) =>
      own.filter((mark) => mark.status === status).length;
    const denominator = count("present") + count("late") + count("absent");
    return {
      own: own.sort((a, b) => b.sessionId.localeCompare(a.sessionId)),
      present: count("present"),
      late: count("late"),
      absent: count("absent"),
      excused: count("excused"),
      rate:
        denominator > 0
          ? Math.round(((count("present") + count("late")) / denominator) * 100)
          : undefined,
    };
  };
  const focused = roster.find((member) => member.id === focusedMemberId);
  if (focused) {
    const stats = summary(focused.id);
    return (
      <section className="attendance-panel">
        <button
          className="back-link"
          type="button"
          onClick={() => setFocusedMemberId(undefined)}
        >
          <ArrowLeft /> Team attendance
        </button>
        <div className="attendance-member-head">
          <Avatar member={focused} />
          <span>
            <small>Attendance history</small>
            <h2>{focused.name}</h2>
            <TeamBadge teamId={team} />
          </span>
        </div>
        <div className="metric-grid attendance-metrics">
          <MetricCard
            label="Attendance"
            value={stats.rate == null ? "—" : `${stats.rate}%`}
            detail="Excused dates excluded"
            tone="primary"
          />
          <MetricCard
            label="Present / late"
            value={`${stats.present} / ${stats.late}`}
            detail={`${stats.absent} absent`}
            tone="sage"
          />
          <MetricCard
            label="Excused"
            value={String(stats.excused)}
            detail={`${stats.own.length} dates recorded`}
            tone="gold"
          />
        </div>
        <div className="attendance-history">
          {stats.own.length ? (
            stats.own.map((mark) => (
              <article key={mark.id}>
                <time>{formatDate(mark.sessionId.split(":")[1], true)}</time>
                <Pill
                  tone={
                    mark.status === "present"
                      ? "sage"
                      : mark.status === "absent"
                        ? "rose"
                        : mark.status === "late"
                          ? "gold"
                          : "neutral"
                  }
                >
                  {mark.status}
                </Pill>
                <span>{mark.note || "No note"}</span>
              </article>
            ))
          ) : (
            <Empty
              title="No attendance recorded"
              copy="This Member will appear here after a practice date is saved."
            />
          )}
        </div>
      </section>
    );
  }
  return (
    <section className="attendance-panel">
      <header className="attendance-toolbar">
        <div className="segmented" aria-label="Attendance team">
          {(["maxima", "fuerza", "impulso"] as const).map((teamId) => (
            <button
              className={team === teamId ? "active" : ""}
              onClick={() => setTeam(teamId)}
              key={teamId}
            >
              {teamName(teamId)}
            </button>
          ))}
        </div>
        <div className="section-tabs" aria-label="Attendance view">
          <button
            className={view === "overview" ? "active" : ""}
            onClick={() => setView("overview")}
          >
            Overview
          </button>
          <button
            className={view === "record" ? "active" : ""}
            onClick={() => setView("record")}
          >
            Record by date
          </button>
        </div>
        {view === "overview" && (
          <label className="attendance-range">
            <span className="sr-only">Attendance date range</span>
            <select
              value={range}
              onChange={(event) => setRange(event.target.value as "90" | "all")}
            >
              <option value="90">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </label>
        )}
      </header>
      {view === "overview" ? (
        <>
          <div className="attendance-legend">
            <b>{teamName(team)} overview</b>
            <span>
              Attendance = (present + late) ÷ (present + late + absent). Excused
              and unrecorded dates are excluded.
            </span>
          </div>
          <div
            className="attendance-table"
            role="table"
            aria-label="Team attendance"
          >
            <div className="attendance-row attendance-head" role="row">
              <span role="columnheader">Member</span>
              <span role="columnheader">Present</span>
              <span role="columnheader">Late</span>
              <span role="columnheader">Absent</span>
              <span role="columnheader">Excused</span>
              <span role="columnheader">Rate</span>
            </div>
            {roster.map((member) => {
              const stats = summary(member.id);
              return (
                <button
                  className="attendance-row"
                  role="row"
                  type="button"
                  onClick={() => setFocusedMemberId(member.id)}
                  key={member.id}
                >
                  <span className="person-cell" role="cell">
                    <Avatar member={member} />
                    <b>{member.name}</b>
                  </span>
                  <span role="cell">{stats.present}</span>
                  <span role="cell">{stats.late}</span>
                  <span role="cell">{stats.absent}</span>
                  <span role="cell">{stats.excused}</span>
                  <strong role="cell">
                    {stats.rate == null ? "—" : `${stats.rate}%`}
                    <ChevronRight />
                  </strong>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <form
          className="attendance-entry"
          onSubmit={async (event) => {
            event.preventDefault();
            const recorded = roster
              .filter((member) => draft[member.id]?.status)
              .map((member) => ({
                memberId: member.id,
                status: draft[member.id]!.status!,
                note: draft[member.id]!.note,
              }));
            if (!recorded.length) {
              setMessage("Choose a status for at least one Member.");
              return;
            }
            setBusy(true);
            setMessage("");
            try {
              await api("/api/teacher/attendance/sessions", {
                method: "PUT",
                body: JSON.stringify({ teamId: team, date, marks: recorded }),
              });
              await reload();
              setMessage(`${recorded.length} attendance records saved.`);
            } catch (cause) {
              setMessage(
                cause instanceof Error
                  ? cause.message
                  : "Could not save attendance",
              );
            } finally {
              setBusy(false);
            }
          }}
        >
          <div className="attendance-date-card">
            <label>
              <span>Practice date</span>
              <input
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                required
              />
            </label>
            <span>
              <b>
                {selectedSession
                  ? "Editing saved attendance"
                  : "New attendance date"}
              </b>
              <small>
                {roster.length} active Members on {teamName(team)}
              </small>
            </span>
          </div>
          <div className="attendance-entry-list">
            {roster.map((member) => (
              <article key={member.id}>
                <div className="attendance-entry-member">
                  <Avatar member={member} />
                  <b>{member.name}</b>
                </div>
                <div
                  className="attendance-status-options"
                  role="radiogroup"
                  aria-label={`${member.name} attendance`}
                >
                  {(["present", "late", "absent", "excused"] as const).map(
                    (status) => (
                      <label
                        className={
                          draft[member.id]?.status === status ? "selected" : ""
                        }
                        key={status}
                      >
                        <input
                          type="radio"
                          name={`attendance-${member.id}`}
                          checked={draft[member.id]?.status === status}
                          onChange={() =>
                            setDraft({
                              ...draft,
                              [member.id]: {
                                ...draft[member.id],
                                status,
                                note: draft[member.id]?.note ?? "",
                              },
                            })
                          }
                        />
                        {status}
                      </label>
                    ),
                  )}
                </div>
                <input
                  aria-label={`Attendance note for ${member.name}`}
                  value={draft[member.id]?.note ?? ""}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      [member.id]: {
                        ...draft[member.id],
                        note: event.target.value,
                      },
                    })
                  }
                  placeholder="Optional note"
                />
              </article>
            ))}
          </div>
          {message && <div className="status-message">{message}</div>}
          <div className="attendance-save-bar">
            <span>
              {Object.values(draft).filter((entry) => entry.status).length} of{" "}
              {roster.length} recorded
            </span>
            <button className="primary-button" type="submit" disabled={busy}>
              <Save /> {busy ? "Saving…" : "Save attendance"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function MemberTeamDirectory({
  data,
  reload,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [team, setTeam] = useState<"all" | TeamId>("all");
  const [birthdays, setBirthdays] = useState(false);
  const currentMember = data.members.find(
    (member) => member.id === data.user.id,
  )!;
  const [editingBirthday, setEditingBirthday] = useState(false);
  const [ownBirthday, setOwnBirthday] = useState(currentMember.birthday ?? "");
  const [birthdayBusy, setBirthdayBusy] = useState(false);
  useEffect(() => {
    setOwnBirthday(currentMember.birthday ?? "");
  }, [currentMember.birthday]);
  const people = data.members
    .filter((member) => member.status === "approved")
    .filter((member) => team === "all" || member.teamIds.includes(team))
    .filter((member) =>
      member.name.toLowerCase().includes(query.trim().toLowerCase()),
    )
    .filter((member) => !birthdays || Boolean(member.birthday))
    .sort((a, b) => {
      if (a.id === data.user.id) return -1;
      if (b.id === data.user.id) return 1;
      if (birthdays)
        return (a.birthday ?? "99-99").localeCompare(b.birthday ?? "99-99");
      return a.name.localeCompare(b.name);
    });
  return (
    <>
      <PageHeader
        title="Team"
        copy="Everyone across Maxima, Fuerza and Impulso. Emails and phone numbers remain private."
      />
      <section
        className="directory-toolbar"
        aria-label="Team directory filters"
      >
        <label className="people-search">
          <Search />
          <span className="sr-only">Search team members</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Find a teammate"
          />
        </label>
        <div className="segmented">
          {(["all", "maxima", "fuerza", "impulso"] as const).map((value) => (
            <button
              className={team === value ? "active" : ""}
              onClick={() => setTeam(value)}
              key={value}
            >
              {value === "all" ? "All teams" : teamName(value)}
            </button>
          ))}
        </div>
        <button
          className={cx("secondary-button", birthdays && "active")}
          onClick={() => setBirthdays((current) => !current)}
        >
          <CalendarDays /> Upcoming birthdays
        </button>
      </section>
      <section className="team-directory">
        {renderGroupedMembers(people, (member) => (
          <article
            className={cx(
              "team-directory-card",
              member.id === data.user.id && "is-you",
            )}
            key={member.id}
          >
            <Avatar member={member} />
            <div>
              <span className="team-directory-name">
                <h2>{member.name}</h2>
                {member.id === data.user.id && <Pill tone="sage">You</Pill>}
              </span>
              <TeamBadges teamIds={member.teamIds} />
              <p>
                {member.birthday
                  ? `Birthday · ${formatBirthday(member.birthday)}`
                  : "Birthday not added"}
              </p>
              {member.capabilities?.length ? (
                <ul>
                  {member.capabilities.map((capability) => (
                    <li key={capability.id}>
                      {data.routines.find(
                        (routine) => routine.id === capability.routineId,
                      )?.name ?? "Routine"}{" "}
                      — {capability.slot}
                    </li>
                  ))}
                </ul>
              ) : null}
              {member.id === data.user.id && (
                <button
                  className="text-button team-birthday-edit"
                  type="button"
                  onClick={() => setEditingBirthday(true)}
                >
                  <Pencil /> Edit my birthday
                </button>
              )}
            </div>
          </article>
        ))}
        {!people.length && (
          <Empty
            title="No teammates match"
            copy="Try a different name or clear a team filter."
          />
        )}
      </section>
      {editingBirthday && (
        <Modal
          title="Edit my birthday"
          onClose={() => setEditingBirthday(false)}
        >
          <form
            className="form-stack birthday-modal-form"
            onSubmit={async (event) => {
              event.preventDefault();
              setBirthdayBusy(true);
              try {
                await api("/api/profile", {
                  method: "PATCH",
                  body: JSON.stringify({
                    name: currentMember.name,
                    birthday: ownBirthday || undefined,
                    phone: currentMember.phone,
                  }),
                });
                await reload();
                setEditingBirthday(false);
              } finally {
                setBirthdayBusy(false);
              }
            }}
          >
            <p>
              Your teammates see only the month and day. Coaches can update this
              from the Team directory too.
            </p>
            <BirthdaySelect value={ownBirthday} onChange={setOwnBirthday} />
            <div className="modal-actions">
              <button
                className="secondary-button"
                type="button"
                onClick={() => setEditingBirthday(false)}
              >
                Cancel
              </button>
              <button
                className="primary-button"
                type="submit"
                disabled={birthdayBusy}
              >
                <Save /> {birthdayBusy ? "Saving…" : "Save birthday"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}

function MerchProductForm({
  item,
  reload,
  onClose,
}: {
  item?: MerchProduct;
  reload: () => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(item?.name ?? "");
  const [sku, setSku] = useState(item?.sku ?? "");
  const [sizes, setSizes] = useState<string[]>(item?.sizeOptions ?? []);
  const [pricing, setPricing] = useState<"paid" | "complimentary">(
    item?.pricing.type ?? "paid",
  );
  const [price, setPrice] = useState(
    item?.pricing.type === "paid" ? String(item.pricing.priceCents / 100) : "",
  );
  const [active, setActive] = useState(item?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <Modal
      title={item ? "Edit merch item" : "Add merch item"}
      onClose={onClose}
    >
      <form
        className="form-stack merch-product-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError("");
          try {
            await api("/api/teacher/merch/products", {
              method: "POST",
              body: JSON.stringify({
                id: item?.id,
                name,
                sku,
                sizeOptions: sizes,
                pricing:
                  pricing === "complimentary"
                    ? { type: "complimentary" }
                    : {
                        type: "paid",
                        priceCents: Math.round(Number(price) * 100),
                      },
                active,
              }),
            });
            await reload();
            onClose();
          } catch (cause) {
            setError(
              cause instanceof Error
                ? cause.message
                : "Could not save merch item",
            );
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className="field-grid">
          <label>
            <span>Name</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </label>
          <label>
            <span>SKU</span>
            <input
              value={sku}
              onChange={(event) => setSku(event.target.value)}
              required
            />
          </label>
        </div>
        <fieldset className="choice-fieldset">
          <legend>Available sizes</legend>
          <div className="choice-chip-group">
            {["XS", "S", "M", "L", "XL", "2XL"].map((size) => (
              <button
                type="button"
                className={sizes.includes(size) ? "active" : ""}
                aria-pressed={sizes.includes(size)}
                onClick={() =>
                  setSizes(
                    sizes.includes(size)
                      ? sizes.filter((value) => value !== size)
                      : [...sizes, size],
                  )
                }
                key={size}
              >
                {size}
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset className="choice-fieldset">
          <legend>Pricing</legend>
          <div className="segmented merch-pricing-choice">
            <button
              type="button"
              className={pricing === "paid" ? "active" : ""}
              onClick={() => setPricing("paid")}
            >
              Paid
            </button>
            <button
              type="button"
              className={pricing === "complimentary" ? "active" : ""}
              onClick={() => setPricing("complimentary")}
            >
              Complimentary
            </button>
          </div>
        </fieldset>
        {pricing === "paid" && (
          <label>
            <span>Price (CAD)</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              required
            />
          </label>
        )}
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
          <span>Active in the Member catalog</span>
        </label>
        {error && <div className="auth-message">{error}</div>}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary-button" type="submit" disabled={busy}>
            <Save /> {busy ? "Saving…" : item ? "Save item" : "Add item"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function MerchOrderDialog({
  product,
  creditCents,
  reload,
  onClose,
}: {
  product: MerchProduct;
  creditCents: number;
  reload: () => Promise<void>;
  onClose: () => void;
}) {
  const [size, setSize] = useState("");
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const token = useRef(crypto.randomUUID());
  const grossCents =
    product.pricing.type === "paid" ? product.pricing.priceCents : 0;
  const appliedCents = Math.min(grossCents, creditCents);
  const dueCents = grossCents - appliedCents;
  return (
    <Modal
      title={complete ? "Order received" : `Review order · ${product.name}`}
      onClose={onClose}
    >
      {complete ? (
        <Empty
          title="Your order is in"
          copy="You can follow its status from Ordered through Delivered in Purchase history."
          action={
            <button className="primary-button" onClick={onClose}>
              Done
            </button>
          }
        />
      ) : (
        <div className="merch-order-review">
          {product.sizeOptions.length > 0 && (
            <fieldset className="choice-fieldset">
              <legend>Choose a size</legend>
              <div className="choice-chip-group">
                {product.sizeOptions.map((value) => (
                  <button
                    type="button"
                    className={size === value ? "active" : ""}
                    aria-pressed={size === value}
                    onClick={() => setSize(value)}
                    key={value}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </fieldset>
          )}
          <dl className="order-totals">
            <div>
              <dt>Item price</dt>
              <dd>{money(grossCents / 100)}</dd>
            </div>
            <div>
              <dt>Annual credit applied</dt>
              <dd>−{money(appliedCents / 100)}</dd>
            </div>
            <div className="order-total">
              <dt>Amount due</dt>
              <dd>{money(dueCents / 100)}</dd>
            </div>
          </dl>
          <p className="form-intro">
            Confirming creates one private order record. Your Coach will update
            delivery status.
          </p>
          <div className="modal-actions">
            <button
              className="secondary-button"
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="primary-button"
              type="button"
              disabled={busy || (product.sizeOptions.length > 0 && !size)}
              onClick={async () => {
                setBusy(true);
                try {
                  await api("/api/merch/orders", {
                    method: "POST",
                    body: JSON.stringify({
                      productId: product.id,
                      size: size || undefined,
                      orderToken: token.current,
                    }),
                  });
                  await reload();
                  setComplete(true);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Check /> {busy ? "Placing order…" : "Confirm order"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function MerchPage({
  data,
  reload,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
}) {
  const coach = data.user.role === "teacher";
  const [tab, setTab] = useState<"catalog" | "orders" | "insights">("catalog");
  const [editing, setEditing] = useState<MerchProduct | "new">();
  const [ordering, setOrdering] = useState<MerchProduct>();
  const [resettingCredits, setResettingCredits] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const merchMember = (id: string) =>
    data.members.find((member) => member.id === id);
  const merchPayments = data.payments
    .filter((payment) => payment.category === "merch")
    .sort((a, b) =>
      compareMembers(
        merchMember(a.memberId) ?? { name: "Former member", teamIds: [] },
        merchMember(b.memberId) ?? { name: "Former member", teamIds: [] },
      ),
    );
  const paidMerch = merchPayments.filter(
    (payment) => payment.status === "paid",
  );
  const received = paidMerch.reduce(
    (sum, item) =>
      sum + (item.amountCents ?? Math.round((item.amount ?? 0) * 100)),
    0,
  );
  const activeMembers = data.members.filter(
    (member) => member.role === "student" && member.status === "approved",
  );
  const credit = data.merchCredits.find(
    (item) =>
      item.memberId === data.user.id && item.year === new Date().getFullYear(),
  );
  const creditRemaining = Math.max(
    0,
    (credit?.grantedCents ?? 0) - (credit?.usedCents ?? 0),
  );
  const revenueByItem = Object.entries(
    paidMerch.reduce<Record<string, number>>((totals, payment) => {
      totals[payment.label] =
        (totals[payment.label] ?? 0) +
        (payment.amountCents ?? Math.round((payment.amount ?? 0) * 100));
      return totals;
    }, {}),
  ).sort(([left], [right]) => left.localeCompare(right));
  return (
    <>
      <PageHeader
        title="Merch"
        copy={
          coach
            ? "Manage the catalog, orders, revenue, and annual credits."
            : "Browse team merch and track every order through delivery."
        }
        action={
          coach ? (
            <button
              className="primary-button"
              onClick={() => setEditing("new")}
            >
              <Plus /> Add item
            </button>
          ) : undefined
        }
      />
      <section className="metric-grid merch-metrics">
        {coach ? (
          <>
            <MetricCard
              label="Active catalog"
              value={data.merchProducts.filter((item) => item.active).length}
              detail="items available"
              tone="primary"
            />
            <MetricCard
              label="Open orders"
              value={
                merchPayments.filter(
                  (item) =>
                    item.details?.type === "merch" &&
                    item.details.orderStatus !== "delivered",
                ).length
              }
              detail="awaiting delivery"
              tone="gold"
            />
            <MetricCard
              label="Recorded revenue"
              value={money(received / 100)}
              detail="paid merch records"
              tone="sage"
            />
          </>
        ) : (
          <MetricCard
            label="Annual credit remaining"
            value={money(creditRemaining / 100)}
            detail="applied automatically during review"
            tone="primary"
          />
        )}
      </section>
      <nav className="section-tabs" aria-label="Merch sections">
        <button
          className={tab === "catalog" ? "active" : ""}
          onClick={() => setTab("catalog")}
        >
          Catalog
        </button>
        <button
          className={tab === "orders" ? "active" : ""}
          onClick={() => setTab("orders")}
        >
          {coach ? "Orders" : "Purchase history"}
        </button>
        {coach && (
          <button
            className={tab === "insights" ? "active" : ""}
            onClick={() => setTab("insights")}
          >
            Credits & revenue
          </button>
        )}
      </nav>
      {tab === "catalog" && (
        <section className="merch-grid">
          {data.merchProducts.length ? (
            data.merchProducts.map((product) => (
              <article
                className={cx("merch-card", !product.active && "inactive")}
                key={product.id}
              >
                <header>
                  <span className="section-label">{product.sku}</span>
                  {coach && (
                    <Pill tone={product.active ? "sage" : "neutral"}>
                      {product.active ? "Active" : "Hidden"}
                    </Pill>
                  )}
                </header>
                <div>
                  <h2>{product.name}</h2>
                  <strong>
                    {product.pricing.type === "complimentary"
                      ? "Complimentary"
                      : money(product.pricing.priceCents / 100)}
                  </strong>
                </div>
                {product.sizeOptions.length > 0 && (
                  <p>{product.sizeOptions.join(" · ")}</p>
                )}
                {coach ? (
                  <button
                    className="secondary-button"
                    onClick={() => setEditing(product)}
                  >
                    <Pencil /> Edit item
                  </button>
                ) : (
                  <button
                    className="primary-button"
                    onClick={() => setOrdering(product)}
                  >
                    Review order
                  </button>
                )}
              </article>
            ))
          ) : (
            <Empty
              title={
                coach
                  ? "Build the first merch item"
                  : "Merch will be available soon"
              }
              copy={
                coach
                  ? "Add an item with its SKU, sizes, and pricing. It stays editable after publishing."
                  : "Your Coach has not published any products yet. Check back after the catalog opens."
              }
              action={
                coach ? (
                  <button
                    className="primary-button"
                    onClick={() => setEditing("new")}
                  >
                    <Plus /> Add first item
                  </button>
                ) : undefined
              }
            />
          )}
        </section>
      )}
      {tab === "orders" && (
        <section className="record-panel">
          <header className="record-panel-head">
            <div>
              <span className="section-label">Order tracking</span>
              <h2>{coach ? "Member orders" : "Your purchase history"}</h2>
            </div>
            <span>{merchPayments.length} records</span>
          </header>
          <div className="order-list">
            {merchPayments.length ? (
              merchPayments.map((payment, index) => (
                <article className="order-row" key={payment.id}>
                  {coach &&
                    (index === 0 ||
                      memberGroup(
                        merchMember(merchPayments[index - 1]!.memberId),
                      ) !== memberGroup(merchMember(payment.memberId))) && (
                      <h3 className="member-group-heading">
                        {memberGroup(merchMember(payment.memberId))}
                      </h3>
                    )}
                  <div>
                    <b>
                      {coach
                        ? `${data.members.find((member) => member.id === payment.memberId)?.name ?? "Former member"} · ${payment.label}`
                        : payment.label}
                    </b>
                    <small>
                      {payment.details?.type === "merch"
                        ? `${payment.details.size ?? "No size"} · ${payment.details.orderStatus.replaceAll("-", " ")}`
                        : "Merch"}
                    </small>
                  </div>
                  <strong>{money(payment.amount)}</strong>
                  {coach && payment.details?.type === "merch" ? (
                    <label className="compact-field">
                      <span className="sr-only">
                        Order status for {payment.label}
                      </span>
                      <select
                        value={payment.details.orderStatus}
                        onChange={async (event) => {
                          await api(`/api/teacher/payments/${payment.id}`, {
                            method: "PATCH",
                            body: JSON.stringify({
                              details: {
                                ...payment.details,
                                orderStatus: event.target.value,
                              },
                            }),
                          });
                          await reload();
                        }}
                      >
                        <option value="ordered">Ordered</option>
                        <option value="processed">Processed</option>
                        <option value="pending-delivery">
                          Pending Delivery
                        </option>
                        <option value="delivered">Delivered</option>
                      </select>
                    </label>
                  ) : (
                    <Pill tone={payment.status === "paid" ? "sage" : "gold"}>
                      {payment.status}
                    </Pill>
                  )}
                </article>
              ))
            ) : (
              <Empty
                title="No merch orders"
                copy={
                  coach
                    ? "New member orders will appear here with delivery controls."
                    : "Orders you confirm will appear here with their delivery status."
                }
              />
            )}
          </div>
        </section>
      )}
      {coach && tab === "insights" && (
        <div className="merch-insights">
          <section className="record-panel">
            <header className="record-panel-head">
              <div>
                <span className="section-label">Annual credit</span>
                <h2>{new Date().getFullYear()} member credit</h2>
              </div>
            </header>
            <p>
              Grant or reset $20 for all {activeMembers.length} active members.
              This replaces each current balance for the selected year.
            </p>
            <button
              className="secondary-button"
              onClick={() => setResettingCredits(true)}
            >
              <RefreshCw /> Review bulk reset
            </button>
          </section>
          <section className="record-panel">
            <header className="record-panel-head">
              <div>
                <span className="section-label">Revenue</span>
                <h2>Revenue by item</h2>
              </div>
              <strong>{money(received / 100)}</strong>
            </header>
            <div className="insight-list">
              {revenueByItem.length ? (
                revenueByItem.map(([item, cents]) => (
                  <p key={item}>
                    <span>{item}</span>
                    <strong>{money(cents / 100)}</strong>
                  </p>
                ))
              ) : (
                <Empty
                  title="No paid merch yet"
                  copy="Revenue appears after an order has a paid record."
                />
              )}
            </div>
          </section>
        </div>
      )}
      {editing && (
        <MerchProductForm
          item={editing === "new" ? undefined : editing}
          reload={reload}
          onClose={() => setEditing(undefined)}
        />
      )}
      {ordering && (
        <MerchOrderDialog
          product={ordering}
          creditCents={creditRemaining}
          reload={reload}
          onClose={() => setOrdering(undefined)}
        />
      )}
      {resettingCredits && (
        <ConfirmDialog
          title={`Reset ${new Date().getFullYear()} merch credit?`}
          copy={`This will set the annual credit to $20.00 with $0 used for ${activeMembers.length} active members. Existing order history is unchanged.`}
          confirmLabel={`Reset ${activeMembers.length} credits`}
          busy={resetBusy}
          onClose={() => setResettingCredits(false)}
          onConfirm={async () => {
            setResetBusy(true);
            try {
              await api("/api/teacher/merch/credits/reset", {
                method: "POST",
                body: JSON.stringify({
                  memberIds: activeMembers.map((member) => member.id),
                  year: new Date().getFullYear(),
                  grantedCents: 2000,
                }),
              });
              await reload();
              setResettingCredits(false);
            } finally {
              setResetBusy(false);
            }
          }}
        />
      )}
    </>
  );
}

function FeedbackItemCard({
  item,
  data,
  coach,
  reload,
}: {
  item: FeedbackItem;
  data: BootstrapData;
  coach: boolean;
  reload: () => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(true);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const entries = data.feedbackTimeline
    .filter((entry) => entry.feedbackItemId === item.id)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const submitEntry = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api(
        coach
          ? `/api/teacher/feedback-items/${item.id}/progress`
          : `/api/feedback-items/${item.id}/responses`,
        { method: "POST", body: JSON.stringify({ body }) },
      );
      setBody("");
      await reload();
    } finally {
      setBusy(false);
    }
  };
  return (
    <article className={cx("feedback-item", item.archivedAt && "archived")}>
      <header className="feedback-item-header">
        <button
          className="feedback-disclosure"
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          <span>
            <b>{item.title}</b>
            <time>{formatTimestampDate(item.givenAt)}</time>
          </span>
          <ChevronDown className={expanded ? "expanded" : ""} />
        </button>
        {coach && (
          <span className="feedback-item-actions">
            <button
              className="secondary-button compact-button"
              type="button"
              onClick={async () => {
                await api(`/api/teacher/feedback-items/${item.id}/archive`, {
                  method: "PATCH",
                  body: JSON.stringify({ archived: !item.archivedAt }),
                });
                await reload();
              }}
            >
              <Archive /> {item.archivedAt ? "Restore" : "Archive"}
            </button>
            <button
              className="icon-button danger-button"
              type="button"
              aria-label={`Delete ${item.title}`}
              onClick={() => setDeleting(true)}
            >
              <Trash2 />
            </button>
          </span>
        )}
      </header>
      {expanded && (
        <div className="feedback-item-body">
          <p className="feedback-description">{item.notes}</p>
          {entries.length > 0 && (
            <ol className="feedback-progress-list">
              {entries.map((entry) => {
                const author = data.members.find(
                  (member) => member.id === entry.createdByMemberId,
                );
                return (
                  <li key={entry.id}>
                    <span
                      className={cx(
                        "feedback-progress-icon",
                        entry.kind === "member-response" && "member",
                      )}
                    >
                      {entry.kind === "member-response" ? (
                        <MessageSquare />
                      ) : (
                        <Sparkles />
                      )}
                    </span>
                    <div>
                      <header>
                        <b>
                          {entry.kind === "member-response"
                            ? `${author?.name ?? "Member"} responded`
                            : `${author?.name ?? "Coach"} added progress`}
                        </b>
                        <time>{formatTimestampDate(entry.createdAt)}</time>
                      </header>
                      <p>{entry.body}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
          {item.archivedAt ? (
            <p className="feedback-archived-note">
              Restore this item to add another update or response.
            </p>
          ) : (
            <form
              className="feedback-response-composer"
              onSubmit={(event) => {
                event.preventDefault();
                void submitEntry();
              }}
            >
              <label>
                <span>{coach ? "Add progress" : "Your note or question"}</span>
                {!coach && (
                  <small>
                    Have a question or need clarification? Add a note for your
                    Coach about this feedback.
                  </small>
                )}
                <textarea
                  rows={3}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  placeholder={
                    coach
                      ? "Describe what changed or what to keep developing"
                      : "Ask a question or share what you are working on"
                  }
                />
              </label>
              <div className="composer-actions">
                <SpeechInputAction
                  onTranscript={(transcript) =>
                    setBody((current) =>
                      current ? `${current.trim()} ${transcript}` : transcript,
                    )
                  }
                />
                <button
                  className="primary-button"
                  type="submit"
                  disabled={!body.trim() || busy}
                >
                  <Plus />{" "}
                  {busy ? "Saving…" : coach ? "Add progress" : "Post note"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
      {deleting && (
        <ConfirmDialog
          title={`Delete “${item.title}”?`}
          copy="This permanently deletes the feedback item and its full progress history. Archive it instead if it may be useful later."
          confirmLabel="Delete feedback"
          busy={busy}
          onClose={() => setDeleting(false)}
          onConfirm={async () => {
            setBusy(true);
            try {
              await api(`/api/teacher/feedback-items/${item.id}`, {
                method: "DELETE",
              });
              await reload();
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
    </article>
  );
}

function FeedbackPage({
  data,
  reload,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
}) {
  const coach = data.user.role === "teacher";
  const members = data.members.filter(
    (member) => member.role === "student" && member.status === "approved",
  );
  const [team, setTeam] = useState<TeamId>("fuerza");
  const [memberId, setMemberId] = useState(members[0]?.id ?? data.user.id);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"current" | "archived">("current");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const matchingMembers = members
    .filter((member) => member.teamIds.includes(team))
    .filter((member) =>
      member.name.toLowerCase().includes(query.trim().toLowerCase()),
    )
    .sort((a, b) => a.name.localeCompare(b.name));
  useEffect(() => {
    if (coach && !matchingMembers.some((member) => member.id === memberId))
      setMemberId(matchingMembers[0]?.id ?? "");
  }, [coach, memberId, matchingMembers]);
  const selectedMember = members.find((member) => member.id === memberId);
  const items = data.feedbackItems
    .filter((item) => (coach ? item.memberId === memberId : true))
    .filter((item) =>
      view === "archived" ? item.archivedAt : !item.archivedAt,
    )
    .sort((a, b) => b.givenAt.localeCompare(a.givenAt));
  return (
    <>
      <PageHeader
        title="Feedback"
        copy={
          coach
            ? "Create focused feedback and keep each Member’s progress together."
            : "Review your private feedback, progress updates, and questions."
        }
      />
      <div className={cx("feedback-layout", !coach && "member-only")}>
        {coach && (
          <aside className="member-picker-panel">
            <label className="people-search">
              <Search />
              <span className="sr-only">Find a member</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Find a member"
              />
            </label>
            <div className="section-tabs feedback-team-tabs" aria-label="Team">
              {(["maxima", "fuerza", "impulso"] as const).map((teamId) => (
                <button
                  className={team === teamId ? "active" : ""}
                  onClick={() => setTeam(teamId)}
                  key={teamId}
                >
                  {teamName(teamId)}
                </button>
              ))}
            </div>
            <div className="member-picker-list">
              {matchingMembers.map((member) => (
                <button
                  className={member.id === memberId ? "active" : ""}
                  onClick={() => setMemberId(member.id)}
                  key={member.id}
                >
                  <Avatar member={member} />
                  <span>
                    <b>{member.name}</b>
                    <small>{teamLabel(member.teamIds)}</small>
                  </span>
                  {member.id === memberId && <Check />}
                </button>
              ))}
            </div>
          </aside>
        )}
        <section className="feedback-workspace">
          {coach && selectedMember && (
            <header className="feedback-member-head">
              <Avatar member={selectedMember} />
              <div>
                <span className="section-label">Private feedback</span>
                <h2>{selectedMember.name}</h2>
                <TeamBadges teamIds={selectedMember.teamIds} />
              </div>
            </header>
          )}
          {coach && selectedMember && (
            <form
              className="feedback-composer"
              onSubmit={async (event) => {
                event.preventDefault();
                setBusy(true);
                setMessage("");
                try {
                  await api("/api/teacher/feedback-items", {
                    method: "POST",
                    body: JSON.stringify({ memberId, title, notes }),
                  });
                  setTitle("");
                  setNotes("");
                  await reload();
                  setMessage("Feedback added to this Member’s current list.");
                } catch (cause) {
                  setMessage(
                    cause instanceof Error
                      ? cause.message
                      : "Could not save feedback",
                  );
                } finally {
                  setBusy(false);
                }
              }}
            >
              <label>
                <span>Feedback title</span>
                <input
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="e.g. Fuerza partner-work timing"
                  maxLength={120}
                  required
                />
              </label>
              <label>
                <span>Notes and direction</span>
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={4}
                  placeholder="Add specific, actionable feedback"
                  required
                />
              </label>
              <div className="composer-actions">
                <SpeechInputAction
                  onTranscript={(transcript) =>
                    setNotes((current) =>
                      current ? `${current.trim()} ${transcript}` : transcript,
                    )
                  }
                />
                <button
                  className="primary-button"
                  type="submit"
                  disabled={!title.trim() || !notes.trim() || busy}
                >
                  <Plus /> {busy ? "Saving…" : "Add feedback"}
                </button>
              </div>
              {message && <div className="status-message">{message}</div>}
            </form>
          )}
          <nav
            className="section-tabs feedback-state-tabs"
            aria-label="Feedback state"
          >
            <button
              className={view === "current" ? "active" : ""}
              onClick={() => setView("current")}
            >
              Current
            </button>
            <button
              className={view === "archived" ? "active" : ""}
              onClick={() => setView("archived")}
            >
              Archived
            </button>
          </nav>
          <section className="feedback-items" aria-label={`${view} feedback`}>
            {items.length ? (
              items.map((item) => (
                <FeedbackItemCard
                  item={item}
                  data={data}
                  coach={coach}
                  reload={reload}
                  key={item.id}
                />
              ))
            ) : (
              <Empty
                title={
                  view === "archived"
                    ? "No archived feedback"
                    : "No current feedback"
                }
                copy={
                  coach
                    ? `Add the first focused item for ${selectedMember?.name ?? "this Member"}.`
                    : view === "archived"
                      ? "Archived feedback will remain available here."
                      : "Your Coach has not added feedback yet."
                }
              />
            )}
          </section>
        </section>
      </div>
    </>
  );
}

const youtubePlaylistIdFromInput = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  try {
    const url = new URL(trimmed);
    return url.searchParams.get("list")?.trim() ?? "";
  } catch {
    return /^[a-zA-Z0-9_-]{10,}$/.test(trimmed) ? trimmed : "";
  }
};

function VideoPlaylistForm({
  data,
  item,
  onClose,
  reload,
}: {
  data: BootstrapData;
  item?: VideoPlaylist;
  onClose: () => void;
  reload: () => Promise<void>;
}) {
  const [kind, setKind] = useState<VideoPlaylist["kind"]>(
    item?.kind ?? "routine",
  );
  const [title, setTitle] = useState(item?.title ?? "");
  const [routineId, setRoutineId] = useState(
    item?.routineId ?? data.routines[0]?.id ?? "",
  );
  const [playlist, setPlaylist] = useState(item?.youtubePlaylistId ?? "");
  const [active, setActive] = useState(item?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  return (
    <Modal
      title={item ? "Edit video playlist" : "Add video playlist"}
      onClose={onClose}
    >
      <form
        className="form-stack video-form"
        onSubmit={async (event) => {
          event.preventDefault();
          const youtubePlaylistId = youtubePlaylistIdFromInput(playlist);
          if (!youtubePlaylistId) {
            setError("Enter a valid YouTube playlist URL or playlist ID.");
            return;
          }
          setBusy(true);
          setError("");
          try {
            await api("/api/teacher/videos", {
              method: "POST",
              body: JSON.stringify({
                id: item?.id,
                kind,
                title,
                youtubePlaylistId,
                routineId: kind === "routine" ? routineId : undefined,
                active,
              }),
            });
            await reload();
            onClose();
          } catch (cause) {
            setError(
              cause instanceof Error ? cause.message : "Could not save video",
            );
          } finally {
            setBusy(false);
          }
        }}
      >
        <label>
          <span>Video type</span>
          <select
            aria-label="Video type"
            value={kind}
            onChange={(event) =>
              setKind(event.target.value as VideoPlaylist["kind"])
            }
          >
            <option value="routine">Routine playlist</option>
            <option value="training">Training playlist</option>
          </select>
        </label>
        {kind === "routine" && (
          <label>
            <span>Routine</span>
            <select
              aria-label="Routine"
              value={routineId}
              onChange={(event) => setRoutineId(event.target.value)}
              required
            >
              {data.routines.map((routine) => (
                <option value={routine.id} key={routine.id}>
                  {teamName(routine.teamId)} · {routine.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          <span>Display title</span>
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Partnerwork walkthrough"
            required
          />
        </label>
        <label>
          <span>YouTube playlist URL or ID</span>
          <input
            value={playlist}
            onChange={(event) => setPlaylist(event.target.value)}
            placeholder="https://youtube.com/playlist?list=…"
            required
          />
          <small>
            Only the playlist ID is stored. Playback uses YouTube’s
            privacy-enhanced embed.
          </small>
        </label>
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
          <span>Published and visible to eligible members</span>
        </label>
        {error && <div className="auth-message">{error}</div>}
        {item && !deleting && (
          <button
            className="danger-button video-delete"
            type="button"
            onClick={() => setDeleting(true)}
          >
            <Trash2 /> Remove playlist
          </button>
        )}
        {item && deleting && (
          <section className="video-delete-confirmation" role="alert">
            <div>
              <b>Remove {item.title}?</b>
              <p>
                This removes the playlist from Coach and Member views. Existing
                uploaded routine videos are not affected.
              </p>
            </div>
            <div>
              <button
                className="secondary-button"
                type="button"
                onClick={() => setDeleting(false)}
                disabled={busy}
              >
                Keep playlist
              </button>
              <button
                className="danger-button"
                type="button"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await api(`/api/teacher/videos/${item.id}`, {
                      method: "DELETE",
                    });
                    await reload();
                    onClose();
                  } catch (cause) {
                    setError(
                      cause instanceof Error
                        ? cause.message
                        : "Could not remove playlist",
                    );
                    setBusy(false);
                  }
                }}
              >
                <Trash2 /> {busy ? "Removing…" : "Remove playlist"}
              </button>
            </div>
          </section>
        )}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary-button" type="submit" disabled={busy}>
            <Save /> {busy ? "Saving…" : "Save playlist"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function VideosPage({
  data,
  reload,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
}) {
  const [tab, setTab] = useState<"routine" | "training">("routine");
  const [editing, setEditing] = useState<VideoPlaylist | "new">();
  const playlists = data.videoPlaylists.filter((item) => item.kind === tab);
  const uploadedRoutineVideos =
    tab === "routine"
      ? data.routines.flatMap((routine) =>
          routine.videoMediaIds
            .map((id) => data.media.find((asset) => asset.id === id))
            .filter((asset): asset is MediaAsset => Boolean(asset))
            .map((asset) => ({ routine, asset })),
        )
      : [];
  const coach = data.user.role === "teacher";
  return (
    <>
      <PageHeader
        title="Videos"
        copy="Routine playlists are personalized to your assignments; training videos are available to everyone."
        action={
          coach ? (
            <button
              className="primary-button"
              onClick={() => setEditing("new")}
            >
              <Plus /> Add video playlist
            </button>
          ) : undefined
        }
      />
      <div className="segmented video-tabs">
        <button
          className={tab === "routine" ? "active" : ""}
          onClick={() => setTab("routine")}
        >
          Routines
        </button>
        <button
          className={tab === "training" ? "active" : ""}
          onClick={() => setTab("training")}
        >
          Training Videos
        </button>
      </div>
      <section className="video-playlists">
        {uploadedRoutineVideos.map(({ routine, asset }) => (
          <article className="video-card" key={asset.id}>
            <header>
              <div>
                <span className="section-label">Uploaded routine video</span>
                <h2>{routine.name}</h2>
              </div>
              <TeamBadge teamId={routine.teamId} />
            </header>
            <video controls preload="metadata" src={`/api/media/${asset.id}`}>
              <track kind="captions" />
            </video>
          </article>
        ))}
        {playlists.map((playlist) => (
          <article className="video-card" key={playlist.id}>
            <header>
              <div>
                <span className="section-label">
                  {playlist.kind === "routine"
                    ? "Routine playlist"
                    : "Training playlist"}
                </span>
                <h2>{playlist.title}</h2>
              </div>
              {coach && (
                <span className="video-card-actions">
                  <Pill tone={playlist.active ? "sage" : "neutral"}>
                    {playlist.active ? "Published" : "Draft"}
                  </Pill>
                  <button
                    className="icon-button"
                    onClick={() => setEditing(playlist)}
                    aria-label={`Edit ${playlist.title}`}
                  >
                    <Pencil />
                  </button>
                </span>
              )}
            </header>
            <iframe
              title={playlist.title}
              src={`https://www.youtube-nocookie.com/embed/videoseries?list=${encodeURIComponent(playlist.youtubePlaylistId)}`}
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </article>
        ))}
        {!playlists.length && !uploadedRoutineVideos.length && (
          <Empty
            title={
              tab === "routine"
                ? "No routine playlists yet"
                : "No training videos yet"
            }
            copy={
              coach
                ? tab === "routine"
                  ? "Publish a routine playlist here, or upload a routine video from Music."
                  : "Publish general training content for every active member."
                : tab === "routine"
                  ? "Your Coach has not published video content for your routines yet."
                  : "No general training videos have been published yet."
            }
            action={
              coach ? (
                <button
                  className="primary-button"
                  onClick={() => setEditing("new")}
                >
                  <Plus /> Add {tab === "routine" ? "routine" : "training"}{" "}
                  playlist
                </button>
              ) : undefined
            }
          />
        )}
      </section>
      {editing && (
        <VideoPlaylistForm
          data={data}
          item={editing === "new" ? undefined : editing}
          reload={reload}
          onClose={() => setEditing(undefined)}
        />
      )}
    </>
  );
}

type PushSetupState =
  | "checking"
  | "enabled"
  | "prompt"
  | "blocked"
  | "unsubscribed"
  | "install-required"
  | "unsupported"
  | "unconfigured";

interface NotificationSettingsGuide {
  path: string;
  url: string;
  linkLabel: string;
}

const notificationSettingsGuide = (): NotificationSettingsGuide => {
  const userAgent = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(userAgent))
    return {
      path: "Settings → Notifications → Avante Hub → Allow Notifications",
      url: "https://support.apple.com/guide/iphone/change-notification-settings-iph7c3d96bab/ios",
      linkLabel: "Apple notification settings guide",
    };
  if (/Android/i.test(userAgent))
    return {
      path: "Settings → Notifications → App notifications → Avante Hub",
      url: "https://support.google.com/android/answer/9079661",
      linkLabel: "Android notification settings guide",
    };
  if (/Firefox/i.test(userAgent))
    return {
      path: "Firefox Settings → Privacy & Security → Permissions → Notifications",
      url: "https://support.mozilla.org/kb/push-notifications-firefox",
      linkLabel: "Firefox notification settings guide",
    };
  if (/Safari/i.test(userAgent) && !/Chrome|Chromium|Edg/i.test(userAgent))
    return {
      path: "System Settings → Notifications → Avante → Allow Notifications",
      url: "https://support.apple.com/guide/safari/customize-website-notifications-sfri40734/mac",
      linkLabel: "Safari notification settings guide",
    };
  return {
    path: "Open Avante in your browser, select the site controls beside the address, then set Notifications to Allow",
    url: "https://support.google.com/chrome/answer/3220216",
    linkLabel: "Browser notification settings guide",
  };
};

const hasPushSupport = () =>
  "Notification" in window &&
  "serviceWorker" in navigator &&
  "PushManager" in window;

const currentPushSetupState = async (): Promise<PushSetupState> => {
  if (installPlatform() === "ios" && !standaloneDisplay())
    return "install-required";
  if (!hasPushSupport()) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  if (Notification.permission !== "granted") return "prompt";
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return "unsubscribed";
  return (await registration.pushManager.getSubscription())
    ? "enabled"
    : "unsubscribed";
};

const applicationServerKey = (publicKey: string) => {
  const padding = "=".repeat((4 - (publicKey.length % 4)) % 4);
  return Uint8Array.from(
    atob((publicKey + padding).replaceAll("-", "+").replaceAll("_", "/")),
    (character) => character.charCodeAt(0),
  );
};

const enablePushNotifications = async (): Promise<{
  state: PushSetupState;
  message: string;
}> => {
  if (!hasPushSupport())
    return {
      state: "unsupported",
      message: "Push notifications are not supported in this browser.",
    };
  if (Notification.permission === "denied")
    return {
      state: "blocked",
      message: "Notifications are blocked in your device or browser settings.",
    };
  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();
  if (permission !== "granted")
    return {
      state: permission === "denied" ? "blocked" : "prompt",
      message:
        permission === "denied"
          ? "Notifications are blocked in your device or browser settings."
          : "Notifications were not enabled.",
    };
  const config = await api<{ publicKey: string | null }>(
    "/api/notifications/push-config",
  );
  if (!config.publicKey)
    return {
      state: "unconfigured",
      message: "Push delivery is not configured in this environment.",
    };
  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey(config.publicKey),
    }));
  await api("/api/notifications/subscriptions", {
    method: "POST",
    body: JSON.stringify(subscription.toJSON()),
  });
  window.dispatchEvent(new Event("avante-push-state-changed"));
  return { state: "enabled", message: "Push notifications enabled." };
};

function NotificationPermissionCallout() {
  const [state, setState] = useState<PushSetupState>("checking");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const guide = notificationSettingsGuide();
  const refresh = async () => {
    try {
      setState(await currentPushSetupState());
    } catch {
      setState("unsupported");
    }
  };
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    void refresh();
    window.addEventListener("focus", refresh);
    window.addEventListener("avante-push-state-changed", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("avante-push-state-changed", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
  if (state === "checking" || state === "enabled") return null;
  const title =
    state === "blocked"
      ? "Notifications are off for Avante Hub"
      : state === "install-required"
        ? "Install Avante to enable notifications"
        : state === "unsupported"
          ? "Notifications are unavailable here"
          : state === "unconfigured"
            ? "Push delivery is unavailable"
            : state === "unsubscribed"
              ? "Finish notification setup"
              : "Turn on Avante notifications";
  const copy =
    state === "blocked"
      ? `To receive schedule, payment, and birthday alerts, open ${guide.path}.`
      : state === "install-required"
        ? "On iPhone or iPad, add Avante to your Home Screen, open the installed app, then tap Enable notifications."
        : state === "unsupported"
          ? "Use the installed Avante app or a browser that supports Web Push. In-app reminders will still appear under the bell."
          : state === "unconfigured"
            ? "In-app reminders still work, but push delivery is not configured for this environment."
            : "Receive schedule, payment, and birthday alerts even when Avante is closed.";
  return (
    <aside className={`notification-callout notification-callout-${state}`}>
      <span className="notification-callout-icon">
        <BellRing />
      </span>
      <div>
        <b>{title}</b>
        <p>{copy}</p>
        {message && <small role="status">{message}</small>}
      </div>
      <div className="notification-callout-actions">
        {(state === "prompt" || state === "unsubscribed") && (
          <button
            className="primary-button"
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setMessage("");
              try {
                const result = await enablePushNotifications();
                setState(result.state);
                setMessage(result.message);
              } catch (cause) {
                setMessage(
                  cause instanceof Error
                    ? cause.message
                    : "Could not enable notifications.",
                );
              } finally {
                setBusy(false);
              }
            }}
          >
            <BellRing /> {busy ? "Enabling…" : "Enable notifications"}
          </button>
        )}
        {state === "blocked" && (
          <>
            <a
              className="secondary-button"
              href={guide.url}
              target="_blank"
              rel="noreferrer"
            >
              {guide.linkLabel} <ExternalLink />
            </a>
            <button
              className="text-button"
              type="button"
              onClick={() => void refresh()}
            >
              Check again
            </button>
          </>
        )}
        {state === "install-required" && (
          <a
            className="secondary-button"
            href="https://support.apple.com/en-euro/guide/iphone/iphea86e5236/ios"
            target="_blank"
            rel="noreferrer"
          >
            Apple installation guide <ExternalLink />
          </a>
        )}
      </div>
    </aside>
  );
}

function NotificationBell({
  data,
  reload,
}: {
  data: BootstrapData;
  reload: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [pushMessage, setPushMessage] = useState("");
  const unread = data.notifications.filter((item) => !item.readAt);
  const enablePush = async () => {
    try {
      setPushMessage((await enablePushNotifications()).message);
    } catch (cause) {
      setPushMessage(
        cause instanceof Error
          ? cause.message
          : "Could not enable notifications.",
      );
    }
  };
  return (
    <div className="notification-bell">
      <button
        className="icon-button"
        aria-label={`${unread.length} unread notifications`}
        onClick={() => setOpen(!open)}
      >
        <BellRing />
        {unread.length > 0 && <span>{unread.length}</span>}
      </button>
      {open && (
        <section
          className="notification-popover"
          role="dialog"
          aria-label="Notifications"
        >
          <header>
            <span>
              <b>Notifications</b>
              <small>
                {unread.length
                  ? `${unread.length} unread`
                  : "You’re all caught up"}
              </small>
            </span>
            <button
              className="icon-button"
              aria-label="Close notifications"
              onClick={() => setOpen(false)}
            >
              <X />
            </button>
          </header>
          {"Notification" in window && (
            <button
              className="notification-push-card"
              onClick={() => void enablePush()}
            >
              <span className="notification-icon">
                <BellRing />
              </span>
              <span>
                <b>Enable push notifications</b>
                <small>
                  {pushMessage ||
                    "Receive schedule, payment, and birthday alerts on this device."}
                </small>
              </span>
              <ChevronRight />
            </button>
          )}
          <div className="notification-list">
            {data.notifications.length ? (
              data.notifications.slice(0, 8).map((item) => (
                <button
                  className={item.readAt ? "" : "unread"}
                  key={item.id}
                  onClick={async () => {
                    if (!item.readAt)
                      await api(`/api/notifications/${item.id}/read`, {
                        method: "POST",
                        body: "{}",
                      });
                    await reload();
                    location.href = item.deepLink;
                  }}
                >
                  <span className="notification-read-dot" />
                  <span>
                    <b>{item.title}</b>
                    <small>{item.body}</small>
                  </span>
                  <ChevronRight />
                </button>
              ))
            ) : (
              <Empty
                title="No notifications"
                copy="Payment, schedule, and birthday alerts will appear here."
              />
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function App() {
  const [config, setConfig] = useState<AuthConfig>();
  const [data, setData] = useState<BootstrapData>();
  const [theme, setTheme] = useState<ThemeMode>(preferredTheme);
  const [themePalette, setThemePalette] = useState<ThemePalette>(
    preferredThemePalette,
  );
  const [page, setPage] = useState<Page>(() => {
    const shortcut = new URLSearchParams(window.location.search).get("open");
    if (shortcut === "schedule") return "schedule";
    if (shortcut === "payments") return "payments";
    if (shortcut === "home") return "home";
    if (shortcut === "practice") return "routines";
    return (localStorage.getItem("avante-page-v2") as Page | null) ?? "home";
  });
  const [mobile, setMobile] = useState(false);
  const [error, setError] = useState("");
  const reload = async () => {
    try {
      setData(await api<BootstrapData>("/api/bootstrap"));
      setError("");
    } catch (reason) {
      if (
        reason instanceof Error &&
        reason.message !== "Authentication required"
      )
        setError(reason.message);
    }
  };
  useEffect(() => {
    void Promise.all([
      api<AuthConfig>("/api/auth/config").then(setConfig),
      api<BootstrapData>("/api/bootstrap")
        .then(setData)
        .catch(() => undefined),
    ]);
  }, []);
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("open")) return;
    url.searchParams.delete("open");
    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, []);
  useEffect(() => localStorage.setItem("avante-page-v2", page), [page]);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.dataset.palette = themePalette;
    localStorage.setItem(themeStorageKey, theme);
    localStorage.setItem(themePaletteStorageKey, themePalette);
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", browserThemeColor(themePalette, theme));
  }, [theme, themePalette]);
  useEffect(() => {
    if (!data) return;
    void ensureClientMediaCacheOwner(data.user.id);
    const allowed = data.user.role === "teacher" ? teacherNav : studentNav;
    if (!allowed.some((item) => item.id === page)) setPage("home");
  }, [data, page]);
  useEffect(() => {
    if ("serviceWorker" in navigator)
      void navigator.serviceWorker.register("/sw.js");
  }, []);
  if (!config)
    return (
      <div className="splash">
        <img src="/brand/avante-wordmark.webp" alt="Avante" />
      </div>
    );
  if (!data) return <Login config={config} />;
  let content: React.ReactNode;
  if (data.user.role === "teacher") {
    content =
      page === "schedule" ? (
        <SchedulePage data={data} reload={reload} />
      ) : page === "routines" ? (
        <RoutinesPage data={data} reload={reload} />
      ) : page === "people" ? (
        <TeamPage data={data} reload={reload} />
      ) : page === "payments" ? (
        <PaymentsPage data={data} reload={reload} />
      ) : page === "announcements" ? (
        <AnnouncementsPage data={data} reload={reload} />
      ) : page === "merch" ? (
        <MerchPage data={data} reload={reload} />
      ) : page === "feedback" ? (
        <FeedbackPage data={data} reload={reload} />
      ) : page === "videos" ? (
        <VideosPage data={data} reload={reload} />
      ) : page === "profile" ? (
        <ProfilePage data={data} reload={reload} />
      ) : (
        <CoachHome data={data} setPage={setPage} />
      );
  } else {
    content =
      page === "schedule" ? (
        <SchedulePage data={data} reload={reload} />
      ) : page === "routines" ? (
        <RoutinesPage data={data} reload={reload} />
      ) : page === "music" ? (
        <PersonalMusic data={data} reload={reload} />
      ) : page === "payments" ? (
        <PaymentsPage data={data} reload={reload} />
      ) : page === "people" ? (
        <TeamPage data={data} reload={reload} />
      ) : page === "merch" ? (
        <MerchPage data={data} reload={reload} />
      ) : page === "feedback" ? (
        <FeedbackPage data={data} reload={reload} />
      ) : page === "videos" ? (
        <VideosPage data={data} reload={reload} />
      ) : page === "profile" ? (
        <ProfilePage data={data} reload={reload} />
      ) : (
        <MemberHome data={data} setPage={setPage} />
      );
  }
  return (
    <div className="app-shell">
      <Sidebar
        data={data}
        page={page}
        setPage={setPage}
        open={mobile}
        close={() => setMobile(false)}
      />
      <div className="app-main">
        <header className="topbar">
          <button
            className="mobile-menu icon-button"
            onClick={() => setMobile(true)}
            aria-label="Open navigation"
          >
            <Menu />
          </button>
          <span>
            <TeamBadges teamIds={data.user.teamIds} />
            {data.authMode === "development" && (
              <Pill tone="gold">Development</Pill>
            )}
          </span>
          <div className="topbar-actions">
            <InstallHint />
            <NotificationBell data={data} reload={reload} />
            <ThemePicker
              mode={theme}
              setMode={setTheme}
              palette={themePalette}
              setPalette={setThemePalette}
            />
            <b>{data.user.role === "teacher" ? "Coach" : "Member"}</b>
            <button
              className="icon-button topbar-signout"
              type="button"
              onClick={() => void signOut()}
              aria-label="Sign out"
            >
              <LogOut />
            </button>
          </div>
        </header>
        <main className="page-content">
          <NotificationPermissionCallout />
          {error && (
            <div className="error-banner">
              {error}
              <button onClick={() => setError("")}>
                <X />
              </button>
            </div>
          )}
          {content}
        </main>
      </div>
      {mobile && (
        <div className="sidebar-scrim" onClick={() => setMobile(false)} />
      )}
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
